/**
 * OzonAdsCampaigns — раздел РЕКЛАМА
 *
 * Вкладки: Кампании | По артикулам | По моделям | Свои группы | Анализатор РК
 * Каждая кампания разворачивается: Товары | Статистика | Поисковые фразы
 */
import { useEffect, useState, useCallback, useRef, useMemo, Fragment } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { useApi } from '../hooks/useApi';
import { fmtDateTime } from '../utils/ruDates';
import { useUrlParams } from '../hooks/useUrlParams';
import { usePlanFeatures } from '../contexts/PlanFeaturesContext';
import { copyText } from '../utils/clipboard';
import { IconCopy } from './icons';
import ToolHelpButton from './ToolHelpButton';
import UnifiedTable from './UnifiedTable/UnifiedTable';
import '../styles/OzonAdsCampaigns.css';

const ADS_TAB_KEYS = ['campaigns', 'articles', 'models', 'groups', 'analyzer', 'duplicates', 'generator'];
import { matchesSearch } from '../utils/search';
import { adsScopeParam, setAdsScope, useAdsScope } from '../utils/adsScope';

// ── уведомления ───────────────────────────────────────────────────────────────
// Сообщения раздела идут во всплывающие уведомления сервиса, а не в системный
// notify('error', ), который блокирует страницу. Корневой компонент подключает showStatus.
let notifySink = null;
function notify(type, message) {
  if (notifySink) notifySink(type, message);
  else window.alert(message);
}

// ── константы ─────────────────────────────────────────────────────────────────

const PERIOD_PRESETS = [
  { label: '7 дней',  days: 7 },
  { label: '14 дней', days: 14 },
  { label: '30 дней', days: 30 },
  { label: '90 дней', days: 90 },
];

const PLACEMENT_FILTER_OPTIONS = [
  { value: '',                            label: 'Все типы' },
  { value: 'PLACEMENT_TOP_PROMOTION',     label: 'Поиск' },
  { value: 'PLACEMENT_SEARCH_AND_CATEGORY', label: 'Поиск и рекомендации' },
  { value: 'CPO',                         label: 'Оплата за заказ' },
  { value: 'MEDIA',                       label: 'Медийная' },
];

const STATE_LABELS = {
  CAMPAIGN_STATE_RUNNING:  { label: 'Активна',     cls: 'state-running' },
  CAMPAIGN_STATE_PLANNED:  { label: 'Запланирована', cls: 'state-planned' },
  CAMPAIGN_STATE_STOPPED:  { label: 'Остановлена', cls: 'state-stopped' },
  CAMPAIGN_STATE_INACTIVE: { label: 'Неактивна',   cls: 'state-inactive' },
  CAMPAIGN_STATE_FINISHED: { label: 'Завершена',   cls: 'state-finished' },
  CAMPAIGN_STATE_ARCHIVED: { label: 'Архив',       cls: 'state-archived' },
};

const STOCK_FILTER_OPTIONS = [
  { value: '', label: 'Все товары' },
  { value: 'any_stock', label: 'Есть остаток (любой)' },
  { value: 'fbo', label: 'Есть FBO' },
  { value: 'fbs', label: 'Есть FBS' },
  { value: 'no_stock', label: 'Нет остатков' },
];

// ── форматирование ─────────────────────────────────────────────────────────────

const R = '₽';

function fMoney(v, digits = 0) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '—';
  return `${n.toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${R}`;
}

function fMoneyNum(v, digits = 0) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '—';
  return n.toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function fDiff(pct, inverseColor = false) {
  if (pct == null) return null;
  const up = pct > 0;
  const good = inverseColor ? !up : up;
  return { pct, up, good };
}

function fNum(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('ru-RU');
}

function fPct(v, digits = 1) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(digits)}%`;
}

function fDate(iso) {
  if (!iso) return '—';
  // Ozon отдаёт здесь календарную дату — читаем её как локальную полночь,
  // иначе в браузерах западнее UTC дата уезжает на день назад.
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fBudget(microrubles) {
  if (!microrubles) return '—';
  return fMoney(microrubles / 1_000_000);
}

function fCampaignType(placement, strategy, paymentModel) {
  if (paymentModel === 'CPO' || paymentModel === 'SEARCH_PROMO') return 'Оплата за заказ';
  if (paymentModel === 'CPO_ALL_SKU') return 'Оплата за заказ — все товары';
  if (paymentModel === 'MEDIA' || placement === 'PLACEMENT_MEDIA') return 'Медийная';

  const isSearch = placement === 'PLACEMENT_TOP_PROMOTION';
  const isSearchRec = placement === 'PLACEMENT_SEARCH_AND_CATEGORY';
  const prefix = isSearch ? 'Поиск' : isSearchRec ? 'ПиР' : (placement || '');

  const subtypeMap = {
    TARGET_BIDS:      'средняя ставка',
    NO_AUTO_STRATEGY: 'средняя ставка',
    TOP_MAX_CLICKS:   'автостратегия',
    MAX_CLICKS:       'автостратегия',
    TOP_PROMOTION:    'вывод в топ',
    TARGET_CIR:       'целевой расход',
    MAX_ORDERS:       'макс. заказы',
  };
  const sub = subtypeMap[strategy];
  return sub ? `${prefix} — ${sub}` : prefix || '—';
}

// ── копирование / ссылки ───────────────────────────────────────────────────────

// Личный кабинет продавца OZON — карточка рекламной кампании.
function sellerCampaignUrl(ozonCampaignId) {
  return `https://seller.ozon.ru/app/advertisement/product/cpc/${ozonCampaignId}`;
}

// Крошечная кнопка-копирка. Класс `icon-button` обязателен — без него глобальное
// правило `.app button:not(.icon-button)` навешивает padding: 10px 18px и
// схлопывает иконку; `ads-copy-btn` доводит размер до 16×16 для плотных таблиц.
function CopyBtn({ value, title = 'Скопировать' }) {
  if (value == null || value === '') return null;
  return (
    <button
      type="button"
      className="ads-copy-btn"
      title={title}
      onClick={e => { e.stopPropagation(); copyText(value); }}
    >
      <IconCopy />
    </button>
  );
}

// ID кампании мелким серым текстом: ссылка в ЛК OZON (новая вкладка) + копирка.
function CampaignIdLine({ id }) {
  if (!id) return null;
  return (
    <span className="camp-id-line">
      <a
        href={sellerCampaignUrl(id)}
        target="_blank"
        rel="noopener noreferrer"
        className="camp-id-link"
        title="Открыть кампанию в личном кабинете OZON"
        onClick={e => e.stopPropagation()}
      >
        ID {id}
      </a>
      <CopyBtn value={id} title="Скопировать ID кампании" />
    </span>
  );
}

// ── дата-хелперы ───────────────────────────────────────────────────────────────

// Дата по локальным компонентам: toISOString() переводит в UTC и между 00:00
// и 03:00 МСК сдвигал период на день назад.
function isoDate(d) {
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// «Сегодня» по МСК — дни рекламной статистики считаются по Москве
function mskToday() {
  const [y, m, d] = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' }).format(new Date()).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function fDateShort(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y.slice(2)}`;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function defaultPeriod(days = 30) {
  const to = addDays(mskToday(), -1);
  const from = addDays(to, -(days - 1));
  return { from: isoDate(from), to: isoDate(to) };
}

// ── DRR-ячейка с тремя уровнями ────────────────────────────────────────────────

function DrrCell({ drrAd, drrTotal, drrTotalAvailable }) {
  return (
    <span className="drr-cell">
      <span className="drr-ad" title="ДРР рекламный = расход / modelSales">
        {fPct(drrAd)}
      </span>
      {' / '}
      <span
        className={drrTotalAvailable ? 'drr-total' : 'drr-na'}
        title={drrTotalAvailable
          ? 'ДРР общий = расход / выручка (Статистика)'
          : 'Нет данных статистики продаж за период'}
      >
        {drrTotalAvailable ? fPct(drrTotal) : '—'}
      </span>
    </span>
  );
}

// ── Период сравнения: направление "лучше" по метрике (для DeltaCell) ───────────
// 'up'   — выше = лучше (зелёный при росте): метрики, которые прямо ведут к
//          деньгам — показы, клики, корзина, заказы (шт/₽), выручка.
// 'down' — ниже = лучше (зелёный при снижении): ДРР — доля расхода в выручке,
//          снижение ДРР = больше выручки на тот же рекламный рубль.
// null   — нейтрально, без окраски: сам по себе Расход не хорош и не плох без
//          контекста итоговой выручки, красим только выручко-образующие метрики
//          (по формулировке пользователя: "лучше — то, что ведёт к большим деньгам").
const METRIC_DIRECTION = {
  spent_rubles: null,
  impressions: 'up',
  clicks: 'up',
  ctr: 'up',
  to_cart: 'up',
  article_orders: 'up',
  article_sales_rubles: 'up',
  model_add_orders: 'up',
  model_add_sales_rubles: 'up',
  drr_ad: 'down',
  total_orders: 'up',
  total_revenue: 'up',
  total_orders_rubles: 'up',
  drr_total: 'down',
  // Алиасы: строки товаров внутри кампании (CampaignProductRows) агрегируют
  // sku-stats в собственную короткую схему полей — те же метрики, другие имена.
  spent: null,
  orders: 'up',
  article_sales: 'up',
  model_add_sales: 'up',
};

function fDeltaMoney(v) {
  return Math.abs(v).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
}

function fDeltaNum(v) {
  return Math.abs(v).toLocaleString('ru-RU');
}

// ── MetricCell — значение метрики + (опционально) дельта к прошлому периоду ────

// Только строка дельты — вынесена отдельно, чтобы вкладка Кампании могла
// подвесить её под собственное форматирование значения (fMoneyNum и т.п.),
// не переключаясь на формат MetricCell.
function MetricDelta({ value, prev, metricKey, kind = 'number' }) {
  if (prev == null) return null;
  const cur = Number(value) || 0;
  const prv = Number(prev) || 0;
  const delta = cur - prv;
  const direction = METRIC_DIRECTION[metricKey];
  let cls = 'ads-delta-neutral';
  if (direction != null && delta !== 0) {
    const better = direction === 'up' ? delta > 0 : delta < 0;
    cls = better ? 'ads-delta-good' : 'ads-delta-bad';
  }
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '±';
  let deltaText;
  if (kind === 'percent') {
    deltaText = `${sign}${Math.abs(delta).toFixed(1)} п.п.`;
  } else {
    const pct = prv !== 0 ? Math.round((Math.abs(delta) / Math.abs(prv)) * 100) : null;
    const amount = kind === 'money' ? fDeltaMoney(delta) : fDeltaNum(delta);
    deltaText = `${sign}${amount}${pct != null ? ` (${sign}${pct}%)` : ''}`;
  }
  return <div className={`ads-delta ${cls}`}>{deltaText}</div>;
}

function MetricCell({ value, prev, compare, metricKey, kind = 'number' }) {
  const fmt = kind === 'money' ? fMoney : kind === 'percent' ? fPct : fNum;
  const display = fmt(value);
  if (!compare || prev == null) {
    return <span className="num-cell">{display}</span>;
  }
  return (
    <span className="num-cell">
      <div>{display}</div>
      <MetricDelta value={value} prev={prev} metricKey={metricKey} kind={kind} />
    </span>
  );
}

// ── makeMetricRender / renderCtr — UnifiedTable column `render` factories ──────
// Shared between TabArticles and TabModels: gates on isPartialPeriod (or on
// value > 0 for the "всего" columns, matching prior behaviour), blanks out
// fields the campaign-breakdown rows don't carry (total_*/drr_total), and
// wires in the period-comparison delta via MetricCell when compare is on.

function makeMetricRender(key, kind, { isPartialPeriod, compareEnabled, zeroGate = false, blankForCampaign = false } = {}) {
  return (_value, row) => {
    if (row._type === 'campaign_status') return null;
    const isCampaign = row._type === 'campaign';
    if (blankForCampaign && isCampaign) {
      return <span className="num-cell text-muted">—</span>;
    }
    const raw = row[key];
    if (zeroGate) {
      if (!(Number(raw) > 0)) return <span className="num-cell">—</span>;
    } else if (!isCampaign && isPartialPeriod) {
      return <span className="num-cell">—</span>;
    }
    // Сравнение с прошлым периодом работает и на вложенных строках разбивки по
    // кампаниям — бэкенд отдаёт им `prev` (sku/{id}/campaigns?compare=true).
    const compare = compareEnabled;
    const prev = compare && row._prev ? row._prev[key] : null;
    return <MetricCell value={raw} prev={prev} compare={compare} metricKey={key} kind={kind} />;
  };
}

function renderCtr(isPartialPeriod, compareEnabled) {
  return (_value, row) => {
    if (row._type === 'campaign_status') return null;
    const isCampaign = row._type === 'campaign';
    if (!isCampaign && isPartialPeriod) return <span className="num-cell">—</span>;
    const ctr = isCampaign
      ? (row.ctr != null ? row.ctr : null)
      : (row.impressions > 0 ? (row.clicks / row.impressions) * 100 : null);
    const compare = compareEnabled;
    let prevCtr = null;
    if (compare && row._prev) {
      prevCtr = row._prev.impressions > 0 ? (row._prev.clicks / row._prev.impressions) * 100 : null;
    }
    return <MetricCell value={ctr} prev={prevCtr} compare={compare} metricKey="ctr" kind="percent" />;
  };
}

// Lazy per-SKU campaign breakdown, shared by both tables' 3rd expand level.
async function fetchSkuCampaigns(accountId, skuId, dateFrom, dateTo, request, compare = false) {
  const q = `date_from=${dateFrom}&date_to=${dateTo}${compare ? '&compare=true' : ''}${adsScopeParam()}`;
  const d = await request(`/api/v1/ozon-ads/${accountId}/sku/${skuId}/campaigns?${q}`);
  return d.campaigns || [];
}

// ── MetricCard ─────────────────────────────────────────────────────────────────

function DiffBadge({ diff }) {
  if (!diff) return null;
  const cls = diff.good ? 'diff-good' : 'diff-bad';
  return (
    <span className={`metric-diff ${cls}`}>
      {diff.up ? '▲' : '▼'} {Math.abs(diff.pct).toFixed(1)}%
    </span>
  );
}

function MetricCard({ title, value, sub, diff, hint, cls }) {
  return (
    <div className={`ads-metric-card ${cls || ''}`} title={hint}>
      <div className="ads-metric-title">{title}</div>
      <div className="ads-metric-value">{value}</div>
      <div className="ads-metric-sub">
        {diff ? <DiffBadge diff={diff} /> : null}
        {sub ? <span>{sub}</span> : null}
      </div>
    </div>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────────

function QuickSummary({ accountId, dateFrom, dateTo, request }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function openSummary() {
    setOpen(true);
    setLoading(true);
    setError('');
    request(`/api/v1/ozon-ads/${accountId}/quick-summary?date_from=${dateFrom}&date_to=${dateTo}`)
      .then(setData)
      .catch(e => { setData(null); setError(e.message || 'Не удалось собрать сводку'); })
      .finally(() => setLoading(false));
  }

  const list = (title, rows, extra) => rows?.length > 0 && (
    <div className="quick-summary__block">
      <b>{title}</b>
      <ul>
        {rows.map(r => (
          <li key={r.id}>{r.name} <span className="quick-summary__id">({r.id})</span> — {extra(r)}</li>
        ))}
      </ul>
    </div>
  );
  const drrText = r => `ДРР ${r.drr != null ? fPct(r.drr) : '—'}${r.prev_drr != null ? ` (было ${fPct(r.prev_drr)})` : ''}, расход ${fMoney(r.spent)}, продажи ${fMoney(r.sales)}`;

  return (
    <>
      <button className="btn-secondary btn-sm" onClick={openSummary}>Быстрая сводка</button>
      {open && (
        <div className="quick-summary__overlay" onClick={() => setOpen(false)}>
          <div className="quick-summary" onClick={e => e.stopPropagation()}>
            <div className="quick-summary__head">
              <h3>Быстрая сводка</h3>
              <button className="btn-secondary btn-sm" onClick={() => setOpen(false)}>Закрыть</button>
            </div>
            {loading && <div>Считаем…</div>}
            {error && <div>{error}</div>}
            {data && (
              <>
                <div className="quick-summary__period">
                  {dateFrom} — {dateTo} в сравнении с {data.prev_from} — {data.prev_to}
                </div>
                <ul className="quick-summary__notes">{data.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
                {list('Лучшие по ДРР', data.best, drrText)}
                {list('Худшие по ДРР', data.worst, drrText)}
                {list('Расход без продаж', data.no_sales, r => `расход ${fMoney(r.spent)}`)}
                {list('Сильнее всего выросли в расходе', data.growth, r => `расход ${fMoney(r.spent)}${r.spent_change_pct != null ? ` (${r.spent_change_pct > 0 ? '+' : ''}${r.spent_change_pct}%)` : ''}, ${drrText(r)}`)}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function AdsDashboard({ accountId, dateFrom, dateTo, request }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    request(`/api/v1/ozon-ads/${accountId}/dashboard?date_from=${dateFrom}&date_to=${dateTo}`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [accountId, dateFrom, dateTo, request]);

  if (loading) return <div className="ads-dashboard-loading">Загрузка сводки…</div>;
  if (!data) return null;

  const m = data.metrics || {};

  return (
    <div className="ads-dashboard">
      <div className="ads-metrics-row">
        <MetricCard
          title="Расход"
          value={fMoney(m.spent_rubles)}
          diff={fDiff(m.spent_diff_pct, true)}
          cls="metric-spent"
        />
        <MetricCard
          title="Показы"
          value={fNum(m.impressions)}
          diff={fDiff(m.impressions_diff_pct)}
        />
        <MetricCard title="Клики" value={fNum(m.clicks)} sub={`CTR ${fPct(m.ctr)}`} diff={fDiff(m.clicks_diff_pct)} />
        <MetricCard
          title="Корзина"
          value={fNum(m.to_cart)}
          diff={fDiff(m.to_cart_diff_pct)}
          sub={m.cart_conv != null ? `конв. ${fPct(m.cart_conv)}` : undefined}
        />
        <MetricCard
          title="Заказы"
          value={fMoney(m.model_sales_rubles)}
          diff={fDiff(m.model_sales_diff_pct)}
          sub={m.model_orders ? `${fNum(m.model_orders)} шт` : undefined}
        />
      </div>
      <div className="ads-metrics-row">
        <MetricCard
          title="ДРР рекламный"
          value={fPct(m.drr_ad)}
          diff={fDiff(m.drr_ad_diff_pct, true)}
          hint="расход / modelSales × 100%"
          cls={m.drr_ad > 50 ? 'metric-warn' : ''}
        />
        <MetricCard
          title="ДРР общий"
          value={m.drr_total_available ? fPct(m.drr_total) : '—'}
          diff={m.drr_total_available ? fDiff(m.drr_total_diff_pct, true) : undefined}
          sub={m.total_revenue_rubles ? fMoney(m.total_revenue_rubles) : 'Нет данных'}
          hint="расход / выручка (Статистика продаж) × 100%"
        />
        <MetricCard
          title="Активных РК"
          value={fNum(m.active_campaigns)}
          sub={m.paused_campaigns ? `приост. ${m.paused_campaigns}` : undefined}
        />
      </div>
      <div className="ads-top-campaigns">
        <QuickSummary accountId={accountId} dateFrom={dateFrom} dateTo={dateTo} request={request} />
      </div>
      {data.top_campaigns?.length > 0 && (
        <div className="ads-top-campaigns">
          <span className="ads-top-title">Топ по расходу: </span>
          {data.top_campaigns.map((c, i) => (
            <span key={c.campaign_id} className="ads-top-item">
              {i > 0 && ' · '}
              {c.name} <b>{fMoney(c.spent_rubles)}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ProductPickerModal ─────────────────────────────────────────────────────────

function ProductPickerModal({ accountId, campaignId, request, onClose, onAdded }) {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [bid, setBid] = useState('');
  const [adding, setAdding] = useState(false);

  const doSearch = useCallback((q) => {
    setLoading(true);
    const qs = q ? `?search=${encodeURIComponent(q)}` : '';
    request(`/api/v1/ozon-ads/${accountId}/campaigns/${campaignId}/available-products${qs}`)
      .then(d => setProducts(d.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [accountId, campaignId, request]);

  useEffect(() => {
    const tm = setTimeout(() => doSearch(search), search ? 300 : 0);
    return () => clearTimeout(tm);
  }, [doSearch, search]);

  async function handleAdd() {
    if (!selected) return;
    setAdding(true);
    try {
      await request(`/api/v1/ozon-ads/${accountId}/campaigns/${campaignId}/products`, {
        method: 'POST',
        body: JSON.stringify({ sku_ids: [selected.sku_id], bid_rubles: parseFloat(bid) || 0 }),
      });
      onAdded();
      onClose();
    } catch (e) {
      notify('error', isRateLimit(e) ? rateLimitMessage(e) : ('Ошибка: ' + (e.message || e)));
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <span>Добавить товар в кампанию</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <input
            className="modal-search"
            placeholder="Поиск по названию, артикулу, SKU…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="product-picker-list">
            {loading ? (
              <div className="tab-loading">Загрузка…</div>
            ) : products.length === 0 ? (
              <div className="tab-empty">Нет товаров</div>
            ) : products.map(p => (
              <div
                key={p.sku_id}
                className={`product-picker-item ${selected?.sku_id === p.sku_id ? 'selected' : ''}`}
                onClick={() => setSelected(p)}
              >
                {p.primary_image && (
                  <img src={p.primary_image} alt="" className="product-picker-img" />
                )}
                <div className="product-picker-info">
                  <div className="product-picker-name">{p.name || '—'}</div>
                  <div className="product-picker-meta">Арт. {p.offer_id} · SKU {p.sku_id}</div>
                </div>
              </div>
            ))}
          </div>
          {selected && (
            <div className="modal-bid-row">
              <span>Ставка CPC для <b>{selected.offer_id}</b>:</span>
              <input
                className="bid-input-inline"
                type="number"
                min="0"
                step="0.5"
                value={bid}
                onChange={e => setBid(e.target.value)}
                placeholder="₽"
              />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Отмена</button>
          <button className="btn-primary" onClick={handleAdd} disabled={!selected || adding || !(parseFloat(bid) > 0)}
            title={selected && !(parseFloat(bid) > 0) ? 'Укажите ставку CPC — без неё Ozon товар не примет' : undefined}>
            {adding ? 'Добавляю…' : 'Добавить'}
          </button>
        </div>
      </div>
    </div>
  );
}

// OZON режет частоту запросов на аккаунт. parseError в ApiContext вешает на ошибку
// status/retryAfter, но часть ручек заворачивает 429 в текст — поэтому проверяем и то,
// и другое. Единая точка для детектора дублей и для сохранения ставок.
function isRateLimit(e) {
  if (!e) return false;
  if (e.status === 429) return true;
  const m = String(e.message || e);
  return m.includes('429') || m.includes('RATE_LIMIT') || m.includes('ограничивает');
}

// Сколько секунд ждать, если лимит короткий (до 2 мин); null — лимит длинный, ждать смысла нет.
function shortRetryDelay(e) {
  const m = String(e?.message || e).match(/через\s+(\d+)\s*сек/i);
  const sec = Number(e?.retryAfter) > 0 ? Number(e.retryAfter) : (m ? Number(m[1]) : 15);
  return sec <= 120 ? sec + 1 : null;
}

function rateLimitMessage(e) {
  const sec = Number(e?.retryAfter) > 0 ? Number(e.retryAfter) : 60;
  // Лимиты Ozon бывают часовыми и суточными: «через 3000 сек.» читается плохо
  const wait = sec > 120 ? `${Math.ceil(sec / 60)} мин.` : `${sec} сек.`;
  return `OZON временно ограничивает запросы, повторите через ${wait}`;
}

// ── ProductRow ─────────────────────────────────────────────────────────────────

function ProductRow({ p, s, prev, drr, drrTotal, ctr, totalOrders, totalRevenue, isCir, currentBid, bidPending, queueBid, flushBids, deletingId, deleteProduct }) {
  const [lightbox, setLightbox] = useState(null);
  const ozonUrl = `https://www.ozon.ru/product/${p.sku_id}/`;

  // Значение остаётся в родном формате строки товара, дельта подвешивается снизу.
  const D = (key, kind = 'number') =>
    (prev ? <MetricDelta value={s[key]} prev={prev[key]} metricKey={key} kind={kind} /> : null);
  // Производные метрики (ctr/ДРР) считаются на месте — для прошлого периода их
  // отдаёт бэкенд в prev под каноническими именами.
  const DCalc = (curValue, prevKey, metricKey, kind) =>
    (prev ? <MetricDelta value={curValue} prev={prev[prevKey]} metricKey={metricKey} kind={kind} /> : null);

  return (
    <tr className="prod-inline-row">
      <td></td>
      <td></td>
      <td className="prod-name-cell">
        {p.primary_image && (
          <>
            <img
              src={p.primary_image}
              alt=""
              className="prod-thumb"
              onClick={() => setLightbox(p.primary_image)}
              style={{ cursor: 'zoom-in' }}
            />
            <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
          </>
        )}
        <div className="prod-name-info">
          <div className="prod-name">{p.product_name || p.offer_id || '—'}</div>
          <div className="prod-meta mono">
            {p.offer_id && (
              <>
                <span>Арт. {p.offer_id}</span>
                <CopyBtn value={p.offer_id} title="Скопировать артикул" />
                <span className="meta-sep"> · </span>
              </>
            )}
            <a
              href={ozonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="prod-sku-link"
              onClick={e => e.stopPropagation()}
            >
              SKU {p.sku_id}
            </a>
            <CopyBtn value={p.sku_id} title="Скопировать SKU" />
          </div>
        </div>
      </td>
      <td className="num">
        <input
          className={`bid-input-inline${bidPending ? ' bid-input-pending' : ''}`}
          type="number"
          min={isCir ? 10 : 0}
          max={isCir ? 100 : undefined}
          step={isCir ? 1 : 0.5}
          value={currentBid}
          // Ввод/спиннер только копят изменение — отправка идёт пачкой по дебаунсу,
          // иначе каждый клик по стрелке = отдельный запрос к OZON и мгновенный 429.
          onChange={e => queueBid(p.sku_id, e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') flushBids(); }}
          onBlur={() => queueBid(p.sku_id, currentBid)}
          placeholder={isCir ? '%' : '₽'}
          title={bidPending ? 'Изменение ещё не сохранено' : undefined}
        />
      </td>
      <td className="num">{isCir ? '—' : (p.min_bid_rubles ? fMoney(p.min_bid_rubles) : '—')}</td>
      <td className="num">
        <StockBadge v={p.stock_fbo} /><span className="stock-sep">/</span><StockBadge v={p.stock_fbs} />
      </td>
      <td className="num">{s.spent ? fMoneyNum(s.spent) : '—'}{D('spent', 'money')}</td>
      <td className="num">{s.impressions ? fNum(s.impressions) : '—'}{D('impressions')}</td>
      <td className="num">{s.clicks ? fNum(s.clicks) : '—'}{D('clicks')}</td>
      <td className="num">{fPct(ctr)}{DCalc(ctr, 'ctr', 'ctr', 'percent')}</td>
      <td className="num">{s.to_cart ? fNum(s.to_cart) : '—'}{D('to_cart')}</td>
      <td className="num">{s.orders ? fNum(s.orders) : '—'}{D('orders')}</td>
      <td className="num">{s.article_sales ? fMoneyNum(s.article_sales) : '—'}{D('article_sales', 'money')}</td>
      <td className="num">{s.model_add_orders ? fNum(s.model_add_orders) : '—'}{D('model_add_orders')}</td>
      <td className="num">{s.model_add_sales ? fMoneyNum(s.model_add_sales) : '—'}{D('model_add_sales', 'money')}</td>
      <td className="num">{fPct(drr)}{DCalc(drr, 'drr_ad', 'drr_ad', 'percent')}</td>
      <td className="num">{totalOrders ? fNum(totalOrders) : '—'}{D('total_orders')}</td>
      <td className="num">{totalRevenue ? fMoneyNum(totalRevenue) : '—'}{D('total_revenue', 'money')}</td>
      <td className="num">{fPct(drrTotal)}{DCalc(drrTotal, 'drr_total', 'drr_total', 'percent')}</td>
      <td>
        <button className="btn-del-product" title="Удалить из кампании" disabled={deletingId === p.sku_id} onClick={() => deleteProduct(p.sku_id)}>🗑</button>
      </td>
    </tr>
  );
}

// Пауза после последнего изменения ставки, после которой очередь уходит одним запросом.
const BID_FLUSH_DELAY_MS = 2000;

// ── CampaignProductRows — <tr> rows directly into the outer campaigns table ────

function CampaignProductRows({ products, accountId, campaignId, autoStrategy, isSearch, dateFrom, dateTo, request, onRefresh, onBidsSaved, onProductDeleted, compareOn }) {
  const [skuStats, setSkuStats] = useState({});
  const [prevSkuStats, setPrevSkuStats] = useState({});
  const [editBids, setEditBids] = useState({});
  const [pendingBids, setPendingBids] = useState({});
  const [flushing, setFlushing] = useState(false);
  const [bidError, setBidError] = useState(null);
  const [savedBids, setSavedBids] = useState(0);
  const [queuedBids, setQueuedBids] = useState(null);   // { count, retryAt } — ждут лимита Ozon
  const savedTimerRef = useRef(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [syncingStocks, setSyncingStocks] = useState(false);

  const isCir = autoStrategy === 'TARGET_CIR';

  const [skuDataRange, setSkuDataRange] = useState(null);
  const [campTotals, setCampTotals] = useState(null);

  useEffect(() => {
    if (!products?.length) return;
    request(`/api/v1/ozon-ads/${accountId}/campaigns/${campaignId}/sku-stats?date_from=${dateFrom}&date_to=${dateTo}${compareOn ? '&compare=true' : ''}`)
      .then(d => {
        const map = {};
        const blank = () => ({ spent: 0, impressions: 0, clicks: 0, to_cart: 0, orders: 0, article_sales: 0, model_add_orders: 0, model_add_sales: 0, total_revenue: 0, total_orders: 0 });
        for (const r of (d.rows || [])) {
          if (!map[r.sku_id]) map[r.sku_id] = blank();
          map[r.sku_id].spent            += r.spent_rubles || 0;
          map[r.sku_id].impressions      += r.impressions  || 0;
          map[r.sku_id].clicks           += r.clicks       || 0;
          map[r.sku_id].to_cart          += r.to_cart      || 0;
          map[r.sku_id].orders           += r.article_orders || 0;
          map[r.sku_id].article_sales    += r.article_sales_rubles || 0;
          map[r.sku_id].model_add_orders += r.model_add_orders || 0;
          map[r.sku_id].model_add_sales  += r.model_add_sales_rubles || 0;
        }
        for (const [skuId, tot] of Object.entries(d.totals_by_sku || {})) {
          if (!map[skuId]) map[skuId] = blank();
          map[skuId].total_revenue = tot.total_revenue || 0;
          map[skuId].total_orders  = tot.total_orders  || 0;
        }
        setSkuStats(map);
        // Прошлый период приходит в канонических именах полей — приводим к той же
        // короткой схеме, что и текущий период, чтобы дельты считались по ключу.
        const prevMap = {};
        for (const [skuId, pv] of Object.entries(d.prev_by_sku || {})) {
          prevMap[skuId] = {
            spent: pv.spent_rubles || 0,
            impressions: pv.impressions || 0,
            clicks: pv.clicks || 0,
            to_cart: pv.to_cart || 0,
            orders: pv.article_orders || 0,
            article_sales: pv.article_sales_rubles || 0,
            model_add_orders: pv.model_add_orders || 0,
            model_add_sales: pv.model_add_sales_rubles || 0,
            total_revenue: pv.total_revenue || 0,
            total_orders: pv.total_orders || 0,
            ctr: pv.ctr,
            drr_ad: pv.drr_ad,
            drr_total: pv.drr_total,
          };
        }
        setPrevSkuStats(prevMap);
        setCampTotals(d.campaign_totals || null);
        if (d.actual_data_from && d.actual_data_to && d.actual_data_from !== dateFrom) {
          setSkuDataRange({ from: d.actual_data_from, to: d.actual_data_to });
        } else {
          setSkuDataRange(null);
        }
      })
      .catch(() => {});
  }, [accountId, campaignId, dateFrom, dateTo, products, request, compareOn]);

  // ── Ставки: копим изменения и отправляем одной пачкой ───────────────────────
  //
  // Раньше каждый blur/Enter слал свой POST /bids и следом дёргал onRefresh(),
  // а тот запускал sync-quick — 6 последовательных list_campaigns к OZON.
  // Правка одной ставки = ~8 запросов к OZON → мгновенный 429 на аккаунт.
  // Теперь: изменения копятся в pendingBids, через BID_FLUSH_DELAY_MS после
  // последнего уходит ОДИН запрос со всем массивом bids, а локальные строки
  // правим на месте — ручка /bids сама пишет значение в нашу БД.
  const pendingRef = useRef({});
  const flushTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  // Ссылка на актуальный flush — чтобы cleanup при размонтировании (сворачивание
  // строки кампании = unmount этого компонента) не схватил протухшее замыкание.
  const flushRef = useRef(() => {});

  // Сохранённые значения из строк товаров — чтобы не слать ставку, равную текущей.
  const persistedBids = useMemo(() => {
    const m = {};
    for (const p of (products || [])) {
      const v = isCir ? p.target_cir : p.bid_cpc_rubles;
      m[String(p.sku_id)] = v != null ? Number(v) : null;
    }
    return m;
  }, [products, isCir]);
  const persistedRef = useRef(persistedBids);
  persistedRef.current = persistedBids;

  const setPending = useCallback(next => {
    pendingRef.current = next;
    if (mountedRef.current) setPendingBids(next);
  }, []);

  const doFlush = useCallback(async () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const batch = pendingRef.current;
    const skus = Object.keys(batch);
    if (!skus.length || inFlightRef.current) return;

    inFlightRef.current = true;
    if (mountedRef.current) { setFlushing(true); setBidError(null); }
    const bids = skus.map(skuId => (isCir
      ? { sku_id: skuId, target_cir: batch[skuId] }
      : { sku_id: skuId, bid_rubles: batch[skuId] }));
    try {
      const res = await request(`/api/v1/ozon-ads/${accountId}/campaigns/${campaignId}/bids`, {
        method: 'POST',
        body: JSON.stringify({ bids }),
      });
      // Лимит Ozon исчерпан: часть ставок осталась в очереди сервера и уйдёт сама.
      // Строки не правим — в Ozon этих значений ещё нет.
      const queuedSet = new Set((res?.queued_skus || []).map(String));
      const failedMap = res?.failed || {};
      const sentSkus = skus.filter(skuId => !queuedSet.has(String(skuId)) && !(String(skuId) in failedMap));
      if (mountedRef.current) {
        setQueuedBids(queuedSet.size ? { count: queuedSet.size, retryAt: res.retry_at } : null);
        const failedCount = Object.keys(failedMap).length;
        if (failedCount) setBidError(`Ozon отклонил ставок: ${failedCount}. ${Object.values(failedMap)[0]}`);
      }
      // Снимаем из очереди только то, что реально ушло с тем же значением:
      // пока запрос летел, пользователь мог поправить ставку ещё раз.
      const rest = { ...pendingRef.current };
      for (const skuId of skus) {
        if (rest[skuId] === batch[skuId]) delete rest[skuId];
      }
      setPending(rest);
      // Явное подтверждение: без него «Сохраняем…» просто пропадает, и непонятно, ушло ли
      if (mountedRef.current) {
        setSavedBids(sentSkus.length);
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => { if (mountedRef.current) setSavedBids(0); }, 5000);
      }
      // Бэкенд уже записал новые значения в нашу БД, поэтому вместо
      // перезагрузки правим строки товаров локально. sync-quick не трогаем.
      if (onBidsSaved) {
        const patch = {};
        for (const skuId of sentSkus) {
          patch[skuId] = isCir ? { target_cir: batch[skuId] } : { bid_cpc_rubles: batch[skuId] };
        }
        if (sentSkus.length) onBidsSaved(patch);
      }
    } catch (e) {
      // Правки НЕ теряем — остаются в очереди, можно повторить кнопкой «Сохранить».
      if (mountedRef.current) {
        setBidError(isRateLimit(e) ? rateLimitMessage(e) : ('Ошибка: ' + (e.message || e)));
      }
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setFlushing(false);
    }
  }, [accountId, campaignId, isCir, request, onBidsSaved, setPending]);

  flushRef.current = doFlush;

  const flushBids = useCallback(() => { flushRef.current(); }, []);

  const queueBid = useCallback((skuId, raw) => {
    const key = String(skuId);
    setEditBids(b => ({ ...b, [key]: raw }));
    const val = parseFloat(raw);
    const next = { ...pendingRef.current };
    if (!val || val <= 0 || Number.isNaN(val)) {
      // Пустое/мусорное значение не отправляем, но и в очереди не держим.
      delete next[key];
    } else if (persistedRef.current[key] === val) {
      delete next[key];
    } else {
      next[key] = val;
    }
    setPending(next);
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    if (Object.keys(next).length) {
      flushTimerRef.current = setTimeout(() => flushRef.current(), BID_FLUSH_DELAY_MS);
    }
  }, [setPending]);

  // Сворачивание строки кампании размонтирует этот компонент — дожимаем очередь,
  // чтобы правка ставки (это деньги) не потерялась молча.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      if (Object.keys(pendingRef.current).length) flushRef.current();
    };
  }, []);

  const pendingCount = Object.keys(pendingBids).length;

  async function deleteProduct(skuId) {
    if (!window.confirm(`Удалить SKU ${skuId} из кампании?`)) return;
    setDeletingId(skuId);
    try {
      await request(`/api/v1/ozon-ads/${accountId}/campaigns/${campaignId}/products/${skuId}`, { method: 'DELETE' });
      // Ручка удаления сама чистит локальную строку и коммитит — пересинхронизация
      // (sync-quick) не нужна и вредна: она выжигает лимит запросов к OZON.
      if (onProductDeleted) onProductDeleted(skuId);
      else onRefresh();
    } catch (e) {
      notify('error', isRateLimit(e) ? rateLimitMessage(e) : ('Ошибка: ' + (e.message || e)));
    } finally {
      setDeletingId(null);
    }
  }

  const [showPhrases, setShowPhrases] = useState(false);
  const hasAdStats = (Object.keys(skuStats).length > 0 || (campTotals && campTotals.spent > 0)) && !skuDataRange;

  const tot = Object.values(skuStats).reduce(
    (a, s) => ({
      spent: a.spent + s.spent,
      impressions: a.impressions + s.impressions,
      clicks: a.clicks + s.clicks,
      to_cart: a.to_cart + s.to_cart,
      orders: a.orders + s.orders,
      article_sales: a.article_sales + s.article_sales,
      model_add_orders: a.model_add_orders + s.model_add_orders,
      model_add_sales: a.model_add_sales + s.model_add_sales,
      total_revenue: a.total_revenue + (s.total_revenue || 0),
      total_orders: a.total_orders + (s.total_orders || 0),
    }),
    { spent: 0, impressions: 0, clicks: 0, to_cart: 0, orders: 0, article_sales: 0, model_add_orders: 0, model_add_sales: 0, total_revenue: 0, total_orders: 0 }
  );
  const totAdSales = tot.article_sales + tot.model_add_sales;
  const totCtr = tot.impressions > 0 ? tot.clicks / tot.impressions * 100 : null;
  const totDrrAd = totAdSales > 0 ? tot.spent / totAdSales * 100 : null;
  const totDrrTotal = tot.total_revenue > 0 ? tot.spent / tot.total_revenue * 100 : null;

  return (
    <>
      {/* sub-header row */}
      <tr className="prod-subheader-row">
        <td></td>
        <td></td>
        <td>
          Товар
          {skuDataRange && (
            <span className="sku-data-range-note" title="Рекламная детализация по артикулам — за фактически накопленный период">
              {' '}· рекл. данные: {fDateShort(skuDataRange.from)}–{fDateShort(skuDataRange.to)}
            </span>
          )}
        </td>
        <td className="num">{isCir ? 'Цел. расход %' : 'Ставка CPC'}</td>
        <td className="num">{isCir ? '—' : 'Мин. ставка'}</td>
        <td className="num">Ост. FBO/FBS</td>
        <td className="num">Расход</td>
        <td className="num">Показы</td>
        <td className="num">Клики</td>
        <td className="num">CTR</td>
        <td className="num">Корзина</td>
        <td className="num">Заказы рекл. шт</td>
        <td className="num">Заказы рекл. ₽</td>
        <td className="num">Заказы рекл модели, шт</td>
        <td className="num">Заказы рекл модели, ₽</td>
        <td className="num">ДРР рекл.</td>
        <td className="num">Заказов всего шт</td>
        <td className="num">Заказов всего ₽</td>
        <td className="num">ДРР общий</td>
        <td></td>
      </tr>

      {/* empty state */}
      {!products?.length && (
        <tr className="prod-empty-row">
          <td colSpan={20} style={{ padding: '12px 20px', color: 'var(--text-muted,#888)', fontSize: 13 }}>
            Нет товаров в кампании
          </td>
        </tr>
      )}

      {/* product rows */}
      {(products || []).map(p => {
        const s = skuStats[p.sku_id] || {};
        const skuAdSales = (s.article_sales || 0) + (s.model_add_sales || 0);
        const drr = skuAdSales > 0 ? s.spent / skuAdSales * 100 : null;
        const drrTotal = s.total_revenue > 0 ? s.spent / s.total_revenue * 100 : null;
        const ctr = s.impressions > 0 ? s.clicks / s.impressions * 100 : null;
        const bidDefault = isCir
          ? (p.target_cir != null ? p.target_cir : '')
          : (p.bid_cpc_rubles != null ? p.bid_cpc_rubles : '');
        const skuKey = String(p.sku_id);
        const currentBid = editBids[skuKey] !== undefined ? editBids[skuKey] : bidDefault;
        return (
          <ProductRow
            key={p.sku_id}
            p={p}
            s={hasAdStats ? s : {}}
            prev={compareOn && hasAdStats ? (prevSkuStats[p.sku_id] || null) : null}
            drr={hasAdStats ? drr : null}
            drrTotal={hasAdStats ? drrTotal : null}
            ctr={hasAdStats ? ctr : null}
            totalOrders={s.total_orders || 0}
            totalRevenue={s.total_revenue || 0}
            isCir={isCir} currentBid={currentBid}
            bidPending={pendingBids[skuKey] !== undefined}
            queueBid={queueBid} flushBids={flushBids}
            deletingId={deletingId} deleteProduct={deleteProduct}
          />
        );
      })}

      {/* totals row */}
      <tr className="prod-totals-row">
        <td></td>
        <td></td>
        <td>
          <b>Итого {products?.length || 0} тов.</b>
          <button className="btn-add-product" style={{ marginLeft: 10 }} onClick={() => setShowPicker(true)}>+ Добавить</button>
          {isSearch && (
            <button className="btn-phrases-toggle" onClick={() => setShowPhrases(v => !v)}>
              {showPhrases ? '▾ Фразы' : '▸ Фразы'}
            </button>
          )}
          {/* Состояние очереди ставок: пользователь всегда видит, сохранены ли правки */}
          {flushing && (
            <span className="bids-pending-note" style={{ marginLeft: 10, fontSize: 12, color: 'var(--text-muted,#888)' }}>
              Сохраняем…
            </span>
          )}
          {!flushing && pendingCount > 0 && (
            <span className="bids-pending-note" style={{ marginLeft: 10, fontSize: 12 }}>
              <span style={{ color: '#c47f00' }}>Несохранённых ставок: {pendingCount}</span>
              <button className="btn-add-product" style={{ marginLeft: 6 }} onClick={flushBids}>Сохранить</button>
            </span>
          )}
          {!flushing && queuedBids && (
            <span className="bids-queued-note" style={{ marginLeft: 10, fontSize: 12, color: '#c47f00' }}>
              Лимит Ozon исчерпан — в очереди ставок: {queuedBids.count}
              {queuedBids.retryAt ? `, отправим после ${fmtDateTime(queuedBids.retryAt)}` : ''}. Повторять не нужно.
            </span>
          )}
          {!flushing && !bidError && pendingCount === 0 && savedBids > 0 && (
            <span className="bids-saved-note" style={{ marginLeft: 10, fontSize: 12, color: '#27ae60' }}>
              ✓ Сохранено в Ozon: {savedBids}
            </span>
          )}
          {bidError && (
            <span className="bids-pending-error" style={{ marginLeft: 10, fontSize: 12, color: '#c0392b' }}>
              {bidError}
            </span>
          )}
        </td>
        <td></td>
        <td></td>
        <td></td>
        <td className="num"><b>{hasAdStats ? (campTotals ? fMoneyNum(campTotals.spent) : (tot.spent ? fMoneyNum(tot.spent) : '—')) : '—'}</b></td>
        <td className="num"><b>{hasAdStats ? (campTotals ? fNum(campTotals.impressions) : (tot.impressions ? fNum(tot.impressions) : '—')) : '—'}</b></td>
        <td className="num"><b>{hasAdStats ? (campTotals ? fNum(campTotals.clicks) : (tot.clicks ? fNum(tot.clicks) : '—')) : '—'}</b></td>
        <td className="num">{hasAdStats ? (campTotals ? fPct(campTotals.clicks > 0 ? campTotals.clicks / campTotals.impressions * 100 : null) : fPct(totCtr)) : '—'}</td>
        <td className="num"><b>{hasAdStats ? (campTotals ? fNum(campTotals.to_cart) : (tot.to_cart ? fNum(tot.to_cart) : '—')) : '—'}</b></td>
        <td className="num"><b>{hasAdStats ? (tot.orders ? fNum(tot.orders) : '—') : '—'}</b></td>
        <td className="num"><b>{hasAdStats ? (tot.article_sales ? fMoneyNum(tot.article_sales) : '—') : '—'}</b></td>
        <td className="num"><b>{hasAdStats ? (tot.model_add_orders ? fNum(tot.model_add_orders) : '—') : '—'}</b></td>
        <td className="num"><b>{hasAdStats ? (tot.model_add_sales ? fMoneyNum(tot.model_add_sales) : '—') : '—'}</b></td>
        <td className="num">{hasAdStats ? fPct(totDrrAd) : '—'}</td>
        <td className="num"><b>{tot.total_orders ? fNum(tot.total_orders) : '—'}</b></td>
        <td className="num"><b>{tot.total_revenue ? fMoneyNum(tot.total_revenue) : '—'}</b></td>
        <td className="num">{hasAdStats ? fPct(totDrrTotal) : '—'}</td>
        <td></td>
      </tr>

      {/* search phrases */}
      {isSearch && showPhrases && (
        <tr className="prod-phrases-row">
          <td colSpan={20}>
            <SearchPhrasesTab accountId={accountId} campaignId={campaignId} dateFrom={dateFrom} dateTo={dateTo} request={request} />
          </td>
        </tr>
      )}

      {/* modal via portal */}
      {showPicker && createPortal(
        <ProductPickerModal
          accountId={accountId}
          campaignId={campaignId}
          request={request}
          onClose={() => setShowPicker(false)}
          onAdded={onRefresh}
        />,
        document.body
      )}
    </>
  );
}

// ── CampaignStatsTab ───────────────────────────────────────────────────────────

function CampaignStatsTab({ accountId, campaignId, dateFrom, dateTo, request }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    setLoading(true);
    request(`/api/v1/ozon-ads/${accountId}/campaigns/${campaignId}/sku-stats?date_from=${dateFrom}&date_to=${dateTo}`)
      .then(d => setRows(d.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [accountId, campaignId, dateFrom, dateTo, request]);

  if (loading) return <div className="tab-loading">Загрузка…</div>;
  if (!rows?.length) return <div className="tab-empty">Нет данных за период</div>;

  // Aggregate per date for chart-like summary
  const byDate = {};
  for (const r of rows) {
    if (!byDate[r.date]) byDate[r.date] = { spent: 0, model_sales: 0, impressions: 0, clicks: 0, model_orders: 0 };
    byDate[r.date].spent += r.spent_rubles;
    byDate[r.date].model_sales += r.model_sales_rubles;
    byDate[r.date].impressions += r.impressions;
    byDate[r.date].clicks += r.clicks;
    byDate[r.date].model_orders += r.model_orders;
  }
  const dates = Object.keys(byDate).sort().reverse();

  return (
    <div className="camp-stats-table-wrap">
      <table className="camp-inner-table">
        <thead>
          <tr>
            <th>Дата</th>
            <th>Расход</th>
            <th>Заказы</th>
            <th>Выручка рекл.</th>
            <th>ДРР рекл.</th>
            <th>Показы</th>
            <th>Клики</th>
          </tr>
        </thead>
        <tbody>
          {dates.map(d => {
            const r = byDate[d];
            const drr = r.model_sales > 0 ? r.spent / r.model_sales * 100 : null;
            return (
              <tr key={d}>
                <td>{fDate(d)}</td>
                <td>{fMoney(r.spent)}</td>
                <td>{fNum(r.model_orders)}</td>
                <td>{fMoney(r.model_sales)}</td>
                <td>{fPct(drr)}</td>
                <td>{fNum(r.impressions)}</td>
                <td>{fNum(r.clicks)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── SearchPhrasesTab ───────────────────────────────────────────────────────────

function SearchPhrasesTab({ accountId, campaignId, dateFrom, dateTo, request }) {
  const [phrases, setPhrases] = useState(null);
  const [loading, setLoading] = useState(false);
  const [triggered, setTriggered] = useState(false);
  const loaded = useRef(false);

  // Try to load cached phrases first
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    request(`/api/v1/ozon-ads/${accountId}/campaigns/${campaignId}/search-phrases?date_from=${dateFrom}&date_to=${dateTo}`)
      .then(d => setPhrases(d.phrases || []))
      .catch(() => setPhrases([]));
  }, [accountId, campaignId, dateFrom, dateTo, request]);

  async function handleLoad() {
    setLoading(true);
    setTriggered(true);
    try {
      const result = await request(
        `/api/v1/ozon-ads/${accountId}/campaigns/${campaignId}/search-phrases?date_from=${dateFrom}&date_to=${dateTo}`,
        { method: 'POST' }
      );
      if (result.phrases === 0) {
        setPhrases([]);
        return;
      }
      // Reload from cache
      const cached = await request(
        `/api/v1/ozon-ads/${accountId}/campaigns/${campaignId}/search-phrases?date_from=${dateFrom}&date_to=${dateTo}`
      );
      setPhrases(cached.phrases || []);
    } catch (e) {
      setPhrases([]);
      notify('error', 'Ошибка загрузки фраз: ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  }

  if (phrases === null) return <div className="tab-loading">Загрузка…</div>;

  return (
    <div className="search-phrases-wrap">
      {phrases.length === 0 ? (
        <div className="tab-empty">
          <p>{triggered ? 'Ozon не вернул фразы за этот период.' : 'Нет кэшированных фраз.'}</p>
          <button className="btn-secondary" onClick={handleLoad} disabled={loading}>
            {loading ? 'Загружаю из Ozon… (до 2 мин)' : triggered ? 'Повторить' : 'Загрузить поисковые фразы'}
          </button>
        </div>
      ) : (
        <>
          <div className="phrases-header">
            <span>{phrases.length} фраз</span>
            <button className="btn-icon" onClick={handleLoad} disabled={loading} title="Обновить">↺</button>
          </div>
          <table className="camp-inner-table">
            <thead>
              <tr>
                <th>Фраза</th>
                <th>Показы</th>
                <th>Клики</th>
                <th>CTR</th>
                <th>Заказы</th>
              </tr>
            </thead>
            <tbody>
              {phrases.sort((a, b) => b.views - a.views).map((p, i) => (
                <tr key={i}>
                  <td>{p.phrase}</td>
                  <td>{fNum(p.views)}</td>
                  <td>{fNum(p.clicks)}</td>
                  <td>{fPct(p.ctr)}</td>
                  <td>{fNum(p.orders)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ── BudgetCell ─────────────────────────────────────────────────────────────────

function BudgetCell({ accountId, campaignId, budgetMicro, request, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const budgetRubles = budgetMicro ? Math.round(budgetMicro / 1_000_000) : null;

  async function save() {
    const v = parseFloat(value);
    if (!v || v <= 0 || v === budgetRubles) { setEditing(false); return; }
    setSaving(true);
    try {
      await request(`/api/v1/ozon-ads/${accountId}/campaigns/${campaignId}/budget`, {
        method: 'PUT',
        body: JSON.stringify({ weekly_budget_rubles: v }),
      });
      onRefresh();
    } catch (e) {
      notify('error', 'Ошибка: ' + (e.message || e));
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }

  if (editing) {
    return (
      <input
        type="number"
        className="budget-edit-input"
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        onBlur={() => { if (!saving) save(); }}
        title="Enter или клик мимо — сохранить, Esc — отменить"
        autoFocus
        disabled={saving}
      />
    );
  }

  return (
    <span
      className="budget-editable"
      onClick={() => { setValue(budgetRubles?.toString() || ''); setEditing(true); }}
      title="Нажмите для редактирования"
    >
      {budgetRubles ? fMoneyNum(budgetRubles) : '—'}
    </span>
  );
}

// ── CampaignRow ────────────────────────────────────────────────────────────────

function CampaignRow({ c, rowNum, accountId, dateFrom, dateTo, request, onAction, isPartialPeriod, selected, onToggleSelect, compareOn, autoFocus }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const rowRef = useRef(null);
  const focusDone = useRef(false);

  const stateInfo = STATE_LABELS[c.state] || { label: c.state, cls: 'state-other' };
  const isSearch = c.placement === 'PLACEMENT_TOP_PROMOTION' || c.placement === 'PLACEMENT_SEARCH_AND_CATEGORY';

  async function loadDetail() {
    setDetailLoading(true);
    try {
      const d = await request(`/api/v1/ozon-ads/${accountId}/campaigns/${c.ozon_campaign_id}`);
      setDetail(d);
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  }

  async function toggleExpand() {
    if (!expanded && !detail) await loadDetail();
    setExpanded(v => !v);
  }

  // Локальная правка строк товаров без похода в OZON: ручки ставок и удаления
  // товара сами пишут результат в нашу БД, поэтому перезагружать нечего.
  const patchProducts = useCallback(patch => {
    setDetail(d => (d ? {
      ...d,
      products: (d.products || []).map(p => (
        patch[String(p.sku_id)] ? { ...p, ...patch[String(p.sku_id)] } : p
      )),
    } : d));
  }, []);

  const removeProduct = useCallback(skuId => {
    setDetail(d => (d ? {
      ...d,
      products: (d.products || []).filter(p => String(p.sku_id) !== String(skuId)),
    } : d));
  }, []);

  // Переход "открыть эту кампанию" из вкладок По артикулам / По моделям:
  // строка сама разворачивается и подскроллена в видимую область.
  useEffect(() => {
    if (!autoFocus || focusDone.current) return;
    focusDone.current = true;
    let alive = true;
    (async () => {
      await loadDetail();
      if (!alive) return;
      setExpanded(true);
      rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    })();
    return () => { alive = false; };
  }, [autoFocus]);  // намеренно только autoFocus — разворот делается один раз, focusDone это страхует

  async function handlePause() {
    setActionLoading(true);
    try {
      await request(`/api/v1/ozon-ads/${accountId}/campaigns/${c.ozon_campaign_id}/pause`, { method: 'POST' });
      onAction();
    } catch (e) {
      notify('error', 'Ошибка: ' + (e.message || e));
    } finally {
      setActionLoading(false);
    }
  }

  async function handleResume() {
    setActionLoading(true);
    try {
      await request(`/api/v1/ozon-ads/${accountId}/campaigns/${c.ozon_campaign_id}/resume`, { method: 'POST' });
      onAction();
    } catch (e) {
      notify('error', 'Ошибка: ' + (e.message || e));
    } finally {
      setActionLoading(false);
    }
  }

  const s = c.stats || {};
  const isRunning = c.state === 'CAMPAIGN_STATE_RUNNING';
  const prev = compareOn ? (c.prev || null) : null;
  // Значение остаётся в родном формате вкладки, дельта подвешивается снизу.
  const D = (key, kind = 'number') =>
    (prev ? <MetricDelta value={s[key]} prev={prev[key]} metricKey={key} kind={kind} /> : null);

  return (
    <>
      <tr ref={rowRef} className={`camp-row ${expanded ? 'camp-row-expanded' : ''} ${autoFocus ? 'camp-row-focused' : ''}`}>
        <td>
          <input type="checkbox" checked={!!selected} onChange={onToggleSelect} />
        </td>
        <td className="num row-num">{rowNum}</td>
        <td>
          <button className="expand-btn" onClick={toggleExpand}>
            {expanded ? '▾' : '▸'}
          </button>
        </td>
        <td className="col-campaign">
          <div className="camp-name">{c.name}</div>
          <CampaignIdLine id={c.ozon_campaign_id} />
          <div className="camp-meta">
            {fCampaignType(c.placement, c.auto_strategy, c.payment_model)}
          </div>
        </td>
        <td><span className={`state-badge ${stateInfo.cls}`}>{stateInfo.label}</span></td>
        <td className="num">
          <BudgetCell
            accountId={accountId}
            campaignId={c.ozon_campaign_id}
            budgetMicro={c.weekly_budget_microrubles || c.daily_budget_microrubles}
            request={request}
            onRefresh={onAction}
          />
        </td>
        <td className="num">{fMoneyNum(s.spent_rubles)}{D('spent_rubles', 'money')}</td>
        <td className="num">{fNum(s.impressions)}{D('impressions')}</td>
        <td className="num">{fNum(s.clicks)}{D('clicks')}</td>
        <td className="num">{fPct(s.ctr)}{D('ctr', 'percent')}</td>
        <td className="num">{fNum(s.to_cart)}{D('to_cart')}</td>
        <td className="num">{fNum(s.article_orders)}{D('article_orders')}</td>
        <td className="num">{s.article_sales_rubles > 0 ? fMoneyNum(s.article_sales_rubles) : '—'}{D('article_sales_rubles', 'money')}</td>
        <td className="num">{fNum(s.model_add_orders)}{D('model_add_orders')}</td>
        <td className="num">{s.model_add_sales_rubles > 0 ? fMoneyNum(s.model_add_sales_rubles) : '—'}{D('model_add_sales_rubles', 'money')}</td>
        <td className="num">{fPct(s.drr_ad)}{D('drr_ad', 'percent')}</td>
        <td className="num">{s.total_orders != null ? fNum(s.total_orders) : '—'}{D('total_orders')}</td>
        <td className="num">{s.total_orders_rubles > 0 ? fMoneyNum(s.total_orders_rubles) : '—'}{D('total_orders_rubles', 'money')}</td>
        <td className="num">{s.drr_total != null ? fPct(s.drr_total) : '—'}{D('drr_total', 'percent')}</td>
        <td className="camp-id">
          <a
            href={sellerCampaignUrl(c.ozon_campaign_id)}
            target="_blank"
            rel="noopener noreferrer"
            className="camp-id-link"
            title="Открыть кампанию в личном кабинете OZON"
          >
            {c.ozon_campaign_id}
          </a>
          <CopyBtn value={c.ozon_campaign_id} title="Скопировать ID кампании" />
        </td>
        <td>
          {isRunning ? (
            <button className="btn-pause" onClick={handlePause} disabled={actionLoading}>⏸</button>
          ) : (
            <button className="btn-resume" onClick={handleResume} disabled={actionLoading}>▶</button>
          )}
        </td>
      </tr>
      {expanded && detailLoading && (
        <tr>
          <td colSpan={21} style={{ padding: '10px 20px', color: 'var(--text-muted,#888)', fontSize: 13 }}>Загрузка…</td>
        </tr>
      )}
      {expanded && !detailLoading && (
        <CampaignProductRows
          products={detail?.products}
          accountId={accountId}
          campaignId={c.ozon_campaign_id}
          autoStrategy={c.auto_strategy}
          isSearch={isSearch}
          dateFrom={dateFrom}
          dateTo={dateTo}
          request={request}
          // onRefresh остался только для «+ Добавить товар» — там действительно нужны
          // новые данные карточки. onAction() (→ sync-quick, 6 запросов к OZON) отсюда
          // убран: правка ставки и удаление товара уже записаны бэкендом в нашу БД.
          onRefresh={loadDetail}
          onBidsSaved={patchProducts}
          onProductDeleted={removeProduct}
          compareOn={compareOn}
        />
      )}
    </>
  );
}

// ── TabCampaigns ───────────────────────────────────────────────────────────────

const SORT_FIELDS = {
  name:        c => c.name?.toLowerCase() || '',
  spent:       c => c.stats?.spent_rubles || 0,
  impressions: c => c.stats?.impressions || 0,
  clicks:      c => c.stats?.clicks || 0,
  ctr:         c => c.stats?.ctr || 0,
  to_cart:     c => c.stats?.to_cart || 0,
  orders:      c => c.stats?.article_orders || 0,
  sales:       c => c.stats?.article_sales_rubles || 0,
  drr:         c => c.stats?.drr_ad ?? null,
  model_orders: c => c.stats?.model_add_orders || 0,
  model_sales:  c => c.stats?.model_add_sales_rubles || 0,
  total_orders: c => c.stats?.total_orders ?? null,
  total_sales:  c => c.stats?.total_orders_rubles ?? null,
  drr_total:    c => c.stats?.drr_total ?? null,
  budget:      c => (c.weekly_budget_microrubles || c.daily_budget_microrubles || 0),
};

function matchesPlacementFilter(c, f) {
  if (!f) return true;
  if (f === 'CPO') return c.payment_model === 'CPO' || c.payment_model === 'SEARCH_PROMO';
  if (f === 'MEDIA') return c.payment_model === 'MEDIA' || c.placement === 'PLACEMENT_MEDIA';
  return c.placement === f;
}

function TabCampaigns({ accountId, dateFrom, dateTo, request, initialSearch, focusCampaignId, refreshNonce, campaignIds = [], onClearCampaignIds }) {
  const adsScope = useAdsScope();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  // При переходе "открыть эту кампанию" статус-фильтр снимается — иначе
  // остановленная/архивная кампания просто не попала бы в список.
  const [stateFilter, setStateFilter] = useState('');
  const [placementFilter, setPlacementFilter] = useState('');
  const [search, setSearch] = useState(initialSearch || '');
  const [sortBy, setSortBy] = useState('spent');
  const [sortDir, setSortDir] = useState('desc');
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState({}); // { [ozon_campaign_id]: true }
  const [bulkBusy, setBulkBusy] = useState(''); // '' | 'minbids' | 'pause' — занята только запущенная команда
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkReport, setBulkReport] = useState(null); // { kind, done: [{id,name}], failed: [{id,name,error}] }
  const [showBulkReport, setShowBulkReport] = useState(false);
  const [drrThreshold, setDrrThreshold] = useState('');
  const [compareOn, setCompareOn] = useState(false);
  const [salesCoverage, setSalesCoverage] = useState(null);
  // Синхронизация списка с Ozon — при открытии, смене периода и «Обновить», но не
  // на каждое переключение сравнения: каждый sync-quick — несколько запросов к Ozon.
  const syncedKeyRef = useRef('');
  const load = useCallback(() => {
    if (!accountId) return;
    setLoading(true);
    const syncKey = `${accountId}|${dateFrom}|${dateTo}|${refreshKey}|${refreshNonce}`;
    const needSync = syncedKeyRef.current !== syncKey;
    syncedKeyRef.current = syncKey;
    const fetchList = () => request(`/api/v1/ozon-ads/${accountId}/campaigns?date_from=${dateFrom}&date_to=${dateTo}${compareOn ? '&compare=true' : ''}${adsScopeParam()}`)
      .then(d => {
        setCampaigns(d.campaigns || []);
        setSalesCoverage(d.sales_incomplete ? { covered: d.sales_covered_days, expected: d.sales_expected_days } : null);
      });
    // Список показываем сразу из базы; синхронизация с Ozon идёт следом и тихо обновляет таблицу.
    fetchList()
      .catch(() => { setCampaigns([]); setSalesCoverage(null); })
      .finally(() => setLoading(false))
      .then(() => (needSync
        ? request(`/api/v1/ozon-ads/${accountId}/campaigns/sync-quick`, { method: 'POST' })
          .then(() => fetchList()).catch(() => null)
        : null));
  }, [accountId, dateFrom, dateTo, request, compareOn, refreshKey, refreshNonce]);

  // refreshNonce — кнопка "Обновить" в шапке раздела. Приходит пропом, а не через
  // key: перемонтирование сбросило бы фильтры, раскрытые строки и галочку сравнения.
  useEffect(() => { load(); }, [load]);

  const onAction = useCallback(() => setRefreshKey(k => k + 1), []);

  function toggleSort(col) {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  }

  const idFilter = campaignIds.length ? new Set(campaignIds.map(String)) : null;
  const filtered = campaigns
    .filter(c => !idFilter || idFilter.has(String(c.ozon_campaign_id)))
    .filter(c => !stateFilter || c.state === stateFilter)
    .filter(c => matchesPlacementFilter(c, placementFilter))
    .filter(c => matchesSearch(c, search, ['ozon_campaign_id', 'name', 'search_text']));

  const sorted = [...filtered].sort((a, b) => {
    const fn = SORT_FIELDS[sortBy] || (c => 0);
    const av = fn(a), bv = fn(b);
    // пустое значение (нет продаж → нет ДРР) — всегда в конце
    if (av == null || bv == null) return av == null && bv == null ? 0 : (av == null ? 1 : -1);
    if (av === bv) return a.ozon_campaign_id - b.ozon_campaign_id;
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const selectedCount = Object.values(selectedIds).filter(Boolean).length;
  const allVisibleSelected = sorted.length > 0 && sorted.every(c => selectedIds[c.ozon_campaign_id]);

  function toggleSelectAll() {
    if (allVisibleSelected) {
      setSelectedIds({});
    } else {
      const next = {};
      sorted.forEach(c => { next[c.ozon_campaign_id] = true; });
      setSelectedIds(next);
    }
  }

  function toggleSelectOne(id) {
    setSelectedIds(prev => ({ ...prev, [id]: !prev[id] }));
  }

  // Область действия для массовых команд: выбранные кампании, если есть выбор,
  // иначе — все кампании, видимые сейчас по фильтру/поиску.
  function getScopeCampaigns() {
    if (selectedCount > 0) return sorted.filter(c => selectedIds[c.ozon_campaign_id]);
    return sorted;
  }

  async function runSetMinBids() {
    // Ставка вручную задаётся только в CPC-кампаниях без автостратегии: в «целевом
    // расходе» вместо ставки процент, в выводе в топ и автостратегиях её ведёт Ozon.
    const all = getScopeCampaigns();
    const scope = all.filter(c => c.payment_model === 'CPC' && MANUAL_BID_STRATEGIES.includes(c.auto_strategy || ''));
    const skippedAuto = all.length - scope.length;
    if (!scope.length) {
      notify('info', 'Среди выбранных нет кампаний с ручной ставкой — менять нечего.');
      return;
    }
    if (!window.confirm(
      `Поставить минимальные ставки для ${scope.length} кампани${scope.length === 1 ? 'и' : 'й'}?`
      + (skippedAuto ? `\nКампании с автостратегией и целевым расходом (${skippedAuto}) не трогаем.` : '')
      + (selectedCount ? '' : '\nНичего не выбрано — действие применится ко всем кампаниям по фильтру.')
    )) return;
    await executeMinBids(scope);
  }

  async function executeMinBids(scope) {
    await runBulk('minbids', scope, async c => {
      const detail = await request(`/api/v1/ozon-ads/${accountId}/campaigns/${c.ozon_campaign_id}`);
      const bids = (detail.products || [])
        .filter(p => p.min_bid_rubles != null && p.min_bid_rubles > 0)
        .map(p => ({ sku_id: p.sku_id, bid_rubles: p.min_bid_rubles }));
      if (!bids.length) throw new Error('нет данных о минимальной ставке');
      await request(`/api/v1/ozon-ads/${accountId}/campaigns/${c.ozon_campaign_id}/bids`, {
        method: 'POST',
        body: JSON.stringify({ bids }),
      });
    }, 'Ставки обновлены');
  }

  // Ozon ограничивает частоту запросов («повторите через N сек.») и иногда отвечает 504:
  // такие ошибки не считаем отказом, а ждём и повторяем.
  async function withRetry(fn) {
    let lastErr;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      try {
        return await fn();
      } catch (e) {
        lastErr = e;
        const msg = String(e?.message || e);
        const transient = isRateLimit(e) || /502|503|504|Gateway/i.test(msg);
        const waitSec = isRateLimit(e) ? shortRetryDelay(e) : 6;
        if (!transient || waitSec == null || attempt === 4) break;
        await new Promise(r => setTimeout(r, waitSec * 1000));
      }
    }
    throw lastErr;
  }

  async function runBulk(kind, scope, action, doneLabel) {
    setBulkBusy(kind);
    setBulkResult(null);
    setBulkReport(null);
    setShowBulkReport(false);
    const done = [];
    const failed = [];
    for (const c of scope) {
      try {
        await withRetry(() => action(c));
        done.push({ id: c.ozon_campaign_id, name: c.name });
      } catch (e) {
        failed.push({ id: c.ozon_campaign_id, name: c.name, error: String(e?.message || e).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160) });
      }
    }
    setBulkBusy('');
    setBulkReport({ kind, done, failed });
    setBulkResult(`${doneLabel}: ${done.length} из ${scope.length} кампаний` + (failed.length ? `, не удалось: ${failed.length}` : ''));
    setRefreshKey(k => k + 1);
  }

  function retryFailedBulk() {
    if (!bulkReport?.failed.length) return;
    const ids = new Set(bulkReport.failed.map(f => f.id));
    const scope = campaigns.filter(c => ids.has(c.ozon_campaign_id));
    if (bulkReport.kind === 'pause') executePause(scope);
    else executeMinBids(scope);
  }

  async function runPauseByDrr() {
    const x = parseFloat(drrThreshold);
    if (!x || x <= 0) {
      notify('info', 'Укажите порог ДРР, %');
      return;
    }
    if (salesCoverage) {
      notify('warning', `Продажи за период загружены не полностью (${salesCoverage.covered} из ${salesCoverage.expected} дн.) — ДРР общий недостоверен, отключать по нему РК нельзя. Выберите период с полными данными.`);
      return;
    }
    const scope = getScopeCampaigns().filter(
      c => c.state === 'CAMPAIGN_STATE_RUNNING' && c.stats?.drr_total != null && c.stats.drr_total > x
    );
    if (!scope.length) {
      notify('info', 'Нет активных кампаний с ДРР общим выше указанного порога');
      return;
    }
    const names = scope.map(c => `${c.name} — ДРР ${c.stats.drr_total}%`).join('\n');
    if (!window.confirm(`Отключить ${scope.length} кампани${scope.length === 1 ? 'ю' : 'й'} с ДРР выше ${x}%?\n\n${names}`)) return;
    await executePause(scope);
  }

  async function executePause(scope) {
    await runBulk('pause', scope, c =>
      request(`/api/v1/ozon-ads/${accountId}/campaigns/${c.ozon_campaign_id}/pause`, { method: 'POST' }),
    'Отключено');
  }

  function SortTh({ col, children, className }) {
    const active = sortBy === col;
    return (
      <th className={`sortable ${active ? 'sort-active' : ''} ${className || ''}`} onClick={() => toggleSort(col)}>
        {children}{active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
      </th>
    );
  }

  return (
    <div className="tab-campaigns">
      {idFilter && (
        <div
          role="status"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '0 0 10px', padding: '10px 14px', border: '1px solid var(--surface-border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--ink)' }}
        >
          <span>
            Показаны кампании из уведомления: <strong>{filtered.length}</strong> из {campaignIds.length}
            {' '}за период {dateFrom} — {dateTo}.
          </span>
          {onClearCampaignIds && (
            <button type="button" className="ghost" onClick={onClearCampaignIds}>Показать все кампании</button>
          )}
        </div>
      )}
      <div className="tab-toolbar">
        <input
          className="search-input"
          placeholder="Поиск: РК (id, название), товар, SKU, артикул"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {adsScope === 'all' && (
          <select className="filter-select" value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
            <option value="">Все статусы</option>
            {Object.entries(STATE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        )}
        <select className="filter-select" value={placementFilter} onChange={e => setPlacementFilter(e.target.value)}>
          {PLACEMENT_FILTER_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <label className="compare-toggle" title="Показать дельту к предыдущему периоду такой же длины под каждым значением">
          <input type="checkbox" checked={compareOn} onChange={e => setCompareOn(e.target.checked)} />
          В сравнении с аналогичным периодом
        </label>
        <span className="count-badge">{sorted.length} / {campaigns.length} кампаний</span>
      </div>

      <div className="tab-toolbar camp-bulk-toolbar">
        <span className="camp-bulk-scope-hint">
          {selectedCount > 0 ? `Выбрано: ${selectedCount}` : `Без выбора — действие применится ко всем ${sorted.length} по фильтру`}
        </span>
        <button
          className="btn-secondary btn-sm"
          onClick={runSetMinBids}
          disabled={!!bulkBusy || sorted.length === 0}
          title="Поставить минимальную ставку для каждого товара в кампании(ях)"
        >
          {bulkBusy === 'minbids' ? 'Выполняется…' : 'Поставить минимальные ставки'}
        </button>
        <span className="camp-bulk-drr">
          <button
            className="btn-secondary btn-sm"
            onClick={runPauseByDrr}
            disabled={!!bulkBusy || sorted.length === 0 || !!salesCoverage}
            title={salesCoverage
              ? 'Недоступно: продажи за период загружены не полностью, ДРР общий недостоверен'
              : 'Отключить кампании, у которых ДРР общий (расход / вся выручка) выше указанного порога'}
          >
            {bulkBusy === 'pause' ? 'Выполняется…' : 'Отключить РК с ДРР выше'}
          </button>
          <input
            type="number"
            className="camp-bulk-drr-input"
            placeholder="ДРР общий, %"
            title="Порог ДРР общего, %"
            value={drrThreshold}
            onChange={e => setDrrThreshold(e.target.value)}
            disabled={!!bulkBusy}
            min="0"
            step="1"
          />
        </span>
        {bulkResult && <span className="camp-bulk-result">{bulkResult}</span>}
        {bulkReport && (
          <>
            <button className="btn-secondary btn-sm" onClick={() => setShowBulkReport(v => !v)}>
              {showBulkReport ? 'Скрыть список' : 'Показать список'}
            </button>
            {bulkReport.failed.length > 0 && (
              <button className="btn-secondary btn-sm" onClick={retryFailedBulk} disabled={!!bulkBusy}>
                Повторить для неудавшихся ({bulkReport.failed.length})
              </button>
            )}
          </>
        )}
      </div>
      {bulkReport && showBulkReport && (
        <div className="camp-bulk-report">
          {bulkReport.failed.length > 0 && (
            <>
              <b>Не удалось ({bulkReport.failed.length})</b>
              <ul>{bulkReport.failed.map(f => <li key={f.id}>{f.id} — {f.name}: {f.error}</li>)}</ul>
            </>
          )}
          <b>{bulkReport.kind === 'pause' ? 'Отключены' : 'Обновлены'} ({bulkReport.done.length})</b>
          <ul>{bulkReport.done.map(d => <li key={d.id}>{d.id} — {d.name}</li>)}</ul>
        </div>
      )}

      {salesCoverage && (
        <div className="data-incomplete-banner">
          Продажи за период загружены за {salesCoverage.covered} из {salesCoverage.expected} дн. — «Заказов всего» и «ДРР общий» не показываются, пока данные неполные.
        </div>
      )}

      {loading ? (
        <div className="tab-loading">Загрузка кампаний…</div>
      ) : sorted.length === 0 ? (
        <div className="tab-empty">{campaigns.length === 0 ? 'Нет кампаний' : 'Нет кампаний по фильтру'}</div>
      ) : (
        <div className="campaigns-table-wrap">
          <table className="campaigns-table">
            <thead>
              <tr>
                <th style={{ width: 24 }}>
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} title="Выбрать все видимые" />
                </th>
                <th className="num" style={{ width: 32 }}>#</th>
                <th></th>
                <SortTh col="name" className="col-campaign">Кампания</SortTh>
                <th>Статус</th>
                <SortTh col="budget">Бюджет, ₽</SortTh>
                <SortTh col="spent" className="num">Расход, ₽</SortTh>
                <SortTh col="impressions" className="num">Показы</SortTh>
                <SortTh col="clicks" className="num">Клики</SortTh>
                <SortTh col="ctr" className="num">CTR</SortTh>
                <SortTh col="to_cart" className="num">Корзина</SortTh>
                <SortTh col="orders" className="num">Заказы рекл. шт</SortTh>
                <SortTh col="sales" className="num">Заказы рекл. ₽</SortTh>
                <SortTh col="model_orders" className="num">Заказы рекл модели, шт</SortTh>
                <SortTh col="model_sales" className="num">Заказы рекл модели, ₽</SortTh>
                <SortTh col="drr" className="num">ДРР рекл.</SortTh>
                <SortTh col="total_orders" className="num">Заказов всего шт</SortTh>
                <SortTh col="total_sales" className="num">Заказов всего ₽</SortTh>
                <SortTh col="drr_total" className="num">ДРР общий</SortTh>
                <th>ID</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c, idx) => (
                <CampaignRow
                  key={c.ozon_campaign_id}
                  c={c}
                  rowNum={idx + 1}
                  accountId={accountId}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  request={request}
                  onAction={onAction}
                  compareOn={compareOn}
                  autoFocus={!!focusCampaignId && String(c.ozon_campaign_id) === String(focusCampaignId)}
                  selected={!!selectedIds[c.ozon_campaign_id]}
                  onToggleSelect={() => toggleSelectOne(c.ozon_campaign_id)}
                />
              ))}
            </tbody>
            {sorted.length > 0 && (() => {
              const t = sorted.reduce((a, c) => {
                const s = c.stats || {};
                return {
                  spent: a.spent + (s.spent_rubles || 0),
                  impressions: a.impressions + (s.impressions || 0),
                  clicks: a.clicks + (s.clicks || 0),
                  to_cart: a.to_cart + (s.to_cart || 0),
                  orders: a.orders + (s.article_orders || 0),
                  sales: a.sales + (s.article_sales_rubles || 0),
                  model_add_orders: a.model_add_orders + (s.model_add_orders || 0),
                  model_add_sales: a.model_add_sales + (s.model_add_sales_rubles || 0),
                  model_sales: a.model_sales + (s.model_sales_rubles || 0),
                };
              }, { spent: 0, impressions: 0, clicks: 0, to_cart: 0, orders: 0, sales: 0, model_add_orders: 0, model_add_sales: 0, model_sales: 0 });
              const tCtr = t.impressions > 0 ? t.clicks / t.impressions * 100 : null;
              // та же формула, что в строке: расход / продажи модели (кампанийная статистика)
              const tDrr = t.model_sales > 0 ? t.spent / t.model_sales * 100 : null;
              return (
                <tfoot>
                  <tr className="camp-totals-row">
                    <td></td>
                    <td></td>
                    <td></td>
                    <td><b>Итого {sorted.length} кампаний</b></td>
                    <td></td>
                    <td></td>
                    <td className="num"><b>{fMoneyNum(t.spent)}</b></td>
                    <td className="num"><b>{fNum(t.impressions)}</b></td>
                    <td className="num"><b>{fNum(t.clicks)}</b></td>
                    <td className="num">{fPct(tCtr)}</td>
                    <td className="num"><b>{fNum(t.to_cart)}</b></td>
                    <td className="num"><b>{fNum(t.orders)}</b></td>
                    <td className="num"><b>{t.sales ? fMoneyNum(t.sales) : '—'}</b></td>
                    <td className="num"><b>{fNum(t.model_add_orders)}</b></td>
                    <td className="num"><b>{t.model_add_sales ? fMoneyNum(t.model_add_sales) : '—'}</b></td>
                    <td className="num">{fPct(tDrr)}</td>
                    <td className="num">—</td>
                    <td className="num">—</td>
                    <td className="num">—</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>
      )}
    </div>
  );
}

// ── StockBadge ─────────────────────────────────────────────────────────────────

function StockBadge({ v }) {
  if (v == null) return <span className="stock-na">—</span>;
  if (v === 0) return <span className="stock-zero">0</span>;
  return <span className="stock-ok">{v}</span>;
}

// ── ImageLightbox ──────────────────────────────────────────────────────────────

function ImageLightbox({ src, onClose }) {
  if (!src) return null;
  return createPortal(
    <div className="lightbox-overlay" onClick={onClose}>
      <div className="lightbox-inner" onClick={e => e.stopPropagation()}>
        <img src={src} alt="" className="lightbox-img" />
        <button className="lightbox-close" onClick={onClose}>✕</button>
      </div>
    </div>,
    document.body
  );
}

// ── CampaignBreakdownCell ──────────────────────────────────────────────────────
// Строка разбивки по кампаниям в таблицах "По артикулам" / "По моделям".
// Название кампании — кнопка перехода во вкладку Кампании (там строка сама
// раскроется и подскроллится), ниже — ID со ссылкой в ЛК OZON и копиркой.

// Тип размещения РК: Поиск / Поиск и рекомендации / Вывод в топ и т.п.
function campaignKindBadge(placement, strategy) {
  if (placement === 'PLACEMENT_TOP_PROMOTION') {
    if (strategy === 'TOP_PROMOTION') return { label: 'Вывод в топ', cls: 'badge-search' };
    if (strategy === 'TOP_MAX_CLICKS') return { label: 'Поиск · автостратегия', cls: 'badge-search' };
    return { label: 'Поиск', cls: 'badge-search' };
  }
  if (placement === 'PLACEMENT_SEARCH_AND_CATEGORY') {
    if (strategy === 'TARGET_CIR') return { label: 'Поиск и рекомендации · целевой расход', cls: 'badge-rec' };
    if (strategy === 'MAX_CLICKS') return { label: 'Поиск и рекомендации · автостратегия', cls: 'badge-rec' };
    return { label: 'Поиск и рекомендации', cls: 'badge-rec' };
  }
  if (placement === 'PLACEMENT_PDP') return { label: 'Карточка товара', cls: 'badge-rec' };
  return null;
}

function CampaignBreakdownCell({ row, onOpenCampaign }) {
  const stateInfo = STATE_LABELS[row.state] || { label: row.state, cls: 'state-other' };
  const kind = campaignKindBadge(row.placement, row.auto_strategy);
  return (
    <div className="indent-2">
      <div>
        {onOpenCampaign ? (
          <button
            type="button"
            className="camp-name-inline camp-name-link"
            title="Открыть эту кампанию во вкладке Кампании"
            onClick={e => { e.stopPropagation(); onOpenCampaign(row); }}
          >
            {row.name}
          </button>
        ) : (
          <span className="camp-name-inline">{row.name}</span>
        )}
        <span className={`state-badge ${stateInfo.cls}`} style={{ marginLeft: 6, fontSize: 12 }}>{stateInfo.label}</span>
        <span className="camp-type-badge">{row.payment_model}</span>
        {kind && <span className={`state-badge ${kind.cls}`} style={{ marginLeft: 6, fontSize: 12 }}>{kind.label}</span>}
      </div>
      <CampaignIdLine id={row.ozon_campaign_id} />
    </div>
  );
}

// ── Правка ставки / бюджета прямо в таблицах (Свои группы / По артикулам / По моделям) ──
// Клик по числу в строке: группа / модель — для всех её артикулов, артикул — для него во
// всех его РК, строка РК — только для неё. Автостратегии (вывод в топ, целевой расход и
// др.) — только бюджет, ставка не меняется.

function useAdsInlineEdit({ accountId, request, dateFrom, dateTo, setCampaignsCache, expandedSkusRef, reloadRef }) {
  // Массовое изменение из строки группы / модели / артикула: только SKU этой строки
  const applyMass = useCallback(async (kind, row, value) => {
    const isAgg = row._type === 'group' || row._type === 'model';
    const skuIds = isAgg ? (row.skus || []).map(x => x.sku_id) : [row.sku_id];
    let what = `артикула ${row.offer_id || row.sku_id}`;
    if (row._type === 'group') what = `группы «${row.name}» (${skuIds.length} арт.)`;
    if (row._type === 'model') what = `модели «${row.model_name}» (${skuIds.length} арт.)`;
    const question = kind === 'bid'
      ? `Поставить ставку ${value} ₽ для ${what} во всех их кампаниях с ручной ставкой?\nАвтостратегии (вывод в топ, целевой расход и т.п.) не меняются.`
      : `Задать общий недельный бюджет ${value} ₽ для кампаний ${what}?\nСумма поделится между кампаниями пропорционально текущим бюджетам. В РК с несколькими артикулами меньше 2000 ₽ на артикул не ставится.`;
    if (!window.confirm(question)) return;
    const d = await request(`/api/v1/ozon-ads/${accountId}/custom-groups/${kind === 'bid' ? 'bid' : 'budget'}`, {
      method: 'POST',
      body: JSON.stringify(kind === 'bid' ? { sku_ids: skuIds, bid_rubles: value } : { sku_ids: skuIds, weekly_budget_rubles: value }),
    });
    const notes = [];
    if (kind === 'bid') {
      notes.push(`Ставок обновлено: ${d.updated_bids} (РК: ${d.campaigns})`);
      if (d.raised_to_min) notes.push(`Поднято до минимальной ставки: ${d.raised_to_min}`);
      if (d.skipped_no_min_bid) notes.push(`Пропущено (снижение при неизвестном минимуме): ${d.skipped_no_min_bid}`);
      if (d.skipped_auto) notes.push(`Не тронуто автостратегий и не-CPC: ${d.skipped_auto}`);
    } else {
      notes.push(`Бюджет обновлён у РК: ${d.updated_campaigns}`);
      if (d.skipped_min_budget) notes.push(`Пропущено РК (меньше 2000 ₽ на артикул): ${d.skipped_min_budget}`);
      if (d.skipped_daily_budget) notes.push(`Пропущено РК с дневным бюджетом: ${d.skipped_daily_budget}`);
    }
    if (d.errors?.length) notes.push(`Ошибки:\n${d.errors.join('\n')}`);
    notify('info', notes.join('\n'));
    // раскрытые артикулы — перечитать кампании (ставки/бюджеты поменялись)
    Object.keys(expandedSkusRef.current).filter(k => expandedSkusRef.current[k]).forEach(k => {
      fetchSkuCampaigns(accountId, k, dateFrom, dateTo, request, false)
        .then(camps => setCampaignsCache(c => ({ ...c, [k]: camps })))
        .catch(() => {});
    });
    reloadRef.current?.();
  }, [accountId, dateFrom, dateTo, request, setCampaignsCache, expandedSkusRef, reloadRef]);

  const saveCampaignBid = useCallback(async (skuId, camp, bid) => {
    await request(`/api/v1/ozon-ads/${accountId}/campaigns/${camp.ozon_campaign_id}/bids`, {
      method: 'POST',
      body: JSON.stringify({ bids: [{ sku_id: skuId, bid_rubles: bid }] }),
    });
    setCampaignsCache(c => ({
      ...c,
      [skuId]: (c[skuId] || []).map(x => (x.ozon_campaign_id === camp.ozon_campaign_id ? { ...x, bid_rubles: bid } : x)),
    }));
    reloadRef.current?.();
  }, [accountId, request, setCampaignsCache, reloadRef]);

  const saveCampaignBudget = useCallback(async (skuId, camp, rubles) => {
    await request(`/api/v1/ozon-ads/${accountId}/campaigns/${camp.ozon_campaign_id}/budget`, {
      method: 'PUT',
      body: JSON.stringify({ weekly_budget_rubles: rubles }),
    });
    setCampaignsCache(c => ({
      ...c,
      [skuId]: (c[skuId] || []).map(x => (x.ozon_campaign_id === camp.ozon_campaign_id ? { ...x, budget_microrubles: Math.round(rubles * 1_000_000) } : x)),
    }));
    reloadRef.current?.();
  }, [accountId, request, setCampaignsCache, reloadRef]);

  return useMemo(() => ({ applyMass, saveCampaignBid, saveCampaignBudget }), [applyMass, saveCampaignBid, saveCampaignBudget]);
}

function makeBidColumn(edit) {
  return {
    key: 'avg_bid_rubles', header: 'Ставка', width: 130, align: 'right', sortable: false,
    title: 'Группа / модель / артикул — средняя ставка по кампаниям с ручной ставкой; нажмите на число, чтобы выставить одну ставку. В строке кампании — ставка этого товара в ней.',
    render: (v, row) => {
      if (row._type === 'campaign') {
        if (row.payment_model !== 'CPC' || !MANUAL_BID_STRATEGIES.includes(row.auto_strategy || '')) {
          return <span className="num-cell text-muted" title="У этой кампании ставка не задаётся вручную">—</span>;
        }
        return (
          <EditableMoneyCell
            value={row.bid_rubles}
            digits={2}
            disabled={CLOSED_CAMPAIGN_STATES.includes(row.state)}
            title="Ставка этого товара в кампании — нажмите, чтобы изменить"
            onSave={val => edit.saveCampaignBid(row._skuId, row, val)}
          />
        );
      }
      if (row._type === 'campaign_status' || v == null) return <span className="num-cell text-muted">—</span>;
      const isAgg = row._type === 'group' || row._type === 'model';
      return (
        <EditableMoneyCell
          value={v}
          digits={2}
          disabled={!!row._ungrouped}
          title={isAgg
            ? 'Средняя ставка. Нажмите, чтобы выставить одну ставку всем артикулам строки во всех их кампаниях с ручной ставкой'
            : 'Средняя ставка артикула. Нажмите, чтобы выставить одну ставку во всех его кампаниях с ручной ставкой'}
          onSave={val => edit.applyMass('bid', row, val)}
        />
      );
    },
  };
}

function makeBudgetColumn(edit) {
  return {
    key: 'weekly_budget_rubles', header: 'Недельный бюджет', width: 160, align: 'right', sortable: false,
    title: 'Сумма недельных бюджетов кампаний; нажмите на число, чтобы задать общий бюджет (делится между кампаниями пропорционально текущим)',
    render: (v, row) => {
      if (row._type === 'campaign') {
        return (
          <EditableMoneyCell
            value={(row.budget_microrubles || 0) / 1_000_000}
            digits={0}
            disabled={CLOSED_CAMPAIGN_STATES.includes(row.state)}
            title="Недельный бюджет кампании — нажмите, чтобы изменить (он общий для всех товаров кампании)"
            onSave={val => edit.saveCampaignBudget(row._skuId, row, val)}
          />
        );
      }
      if (row._type === 'campaign_status' || v == null) return <span className="num-cell text-muted">—</span>;
      return (
        <EditableMoneyCell
          value={v}
          digits={0}
          disabled={!!row._ungrouped}
          suffix={row.campaign_count ? ` · ${row.campaign_count} РК` : null}
          title="Нажмите, чтобы задать общий недельный бюджет кампаний (делится пропорционально текущим; в РК с несколькими артикулами меньше 2000 ₽ на артикул не ставится)"
          onSave={val => edit.applyMass('budget', row, val)}
        />
      );
    },
  };
}

// Предупреждение о неполных данных периода: реклама по SKU (/statistics/products/sku)
// и продажи (Статистика) догружаются отдельно — суммы за период без них занижены.
function DataCoverageBanner({ coverage }) {
  if (!coverage) return null;
  const parts = [];
  if (coverage.data_incomplete) parts.push(`статистика рекламы по артикулам — за ${coverage.covered_days} из ${coverage.expected_days} дн.`);
  if (coverage.sales_incomplete) parts.push(`продажи — за ${coverage.sales_covered_days} из ${coverage.sales_expected_days} дн. («ДРР общий» не показывается)`);
  if (!parts.length) return null;
  return <div className="data-incomplete-banner">Данные за период неполные: {parts.join('; ')}.</div>;
}

function pickCoverage(d) {
  if (!d) return null;
  const { data_incomplete, covered_days, expected_days, sales_incomplete, sales_covered_days, sales_expected_days } = d;
  return { data_incomplete, covered_days, expected_days, sales_incomplete, sales_covered_days, sales_expected_days };
}

// ── TabArticles ────────────────────────────────────────────────────────────────

const ARTICLES_PAGE_SIZE = 200;

function TabArticles({ accountId, dateFrom, dateTo, request, isPartialPeriod, onOpenCampaign, refreshNonce }) {
  const [articles, setArticles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [stockFilter, setStockFilter] = useState('');
  const [stockThreshold, setStockThreshold] = useState('');
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [sortCol, setSortCol] = useState('spent_rubles');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState({});
  const [campaignsCache, setCampaignsCache] = useState({});
  const [compareOn, setCompareOn] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const queryKeyRef = useRef('');
  const expandedRef = useRef({});
  expandedRef.current = expanded;
  const loadedCountRef = useRef(0);
  loadedCountRef.current = articles.length;
  const reloadRef = useRef(null);

  const isStockThresholdApplicable = stockFilter === 'fbo' || stockFilter === 'fbs';

  // поиск уходит на сервер не на каждый символ
  useEffect(() => {
    const tm = setTimeout(() => setSearchApplied(search.trim()), 400);
    return () => clearTimeout(tm);
  }, [search]);

  // Сортировка — на сервере, по всем артикулам кабинета, а не по загруженной странице
  const buildQuery = useCallback((offset) => {
    let q = `date_from=${dateFrom}&date_to=${dateTo}&limit=${ARTICLES_PAGE_SIZE}&offset=${offset}`;
    q += `&sort_by=${sortCol}&sort_dir=${sortDir}`;
    if (stockFilter) q += `&stock_filter=${stockFilter}`;
    if (isStockThresholdApplicable && stockThreshold !== '') q += `&stock_min=${stockThreshold}`;
    if (searchApplied) q += `&search=${encodeURIComponent(searchApplied)}`;
    if (compareOn) q += `&compare=true`;
    q += adsScopeParam();
    return q;
  }, [dateFrom, dateTo, sortCol, sortDir, stockFilter, isStockThresholdApplicable, stockThreshold, searchApplied, compareOn]);

  // silent — перезапрос после правки ставки/бюджета: раскрытые строки и подгруженные страницы остаются
  const load = useCallback((silentArg) => {
    const silent = silentArg === true;
    if (!accountId) return;
    if (!silent) setLoading(true);
    const q = silent
      ? buildQuery(0).replace(`limit=${ARTICLES_PAGE_SIZE}`, `limit=${Math.min(1000, Math.max(ARTICLES_PAGE_SIZE, loadedCountRef.current))}`)
      : buildQuery(0);
    const key = buildQuery(0);
    queryKeyRef.current = key;
    setLoadError(null);
    request(`/api/v1/ozon-ads/${accountId}/article-view?${q}`)
      .then(d => {
        if (queryKeyRef.current !== key) return; // ответ на устаревший запрос
        setArticles(d.articles || []);
        setTotalCount(d.total_count ?? (d.articles || []).length);
        setTotals(d.totals || null);
        setCoverage(pickCoverage(d));
        if (!silent) { setExpanded({}); setCampaignsCache({}); }
      })
      .catch(e => {
        if (queryKeyRef.current !== key) return;
        setArticles([]); setTotalCount(0); setTotals(null); setLoadError(e.message || String(e));
      })
      .finally(() => setLoading(false));
  }, [accountId, buildQuery, request]);

  const loadMore = useCallback(() => {
    if (!accountId || loadingMore) return;
    const key = queryKeyRef.current;
    setLoadingMore(true);
    request(`/api/v1/ozon-ads/${accountId}/article-view?${buildQuery(articles.length)}`)
      .then(d => {
        // фильтр/сортировку сменили, пока шла подгрузка — не смешиваем выборки
        if (queryKeyRef.current !== key) return;
        setArticles(prev => [...prev, ...(d.articles || [])]);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [accountId, articles.length, buildQuery, loadingMore, request]);

  useEffect(() => { load(); }, [load, refreshNonce]);
  reloadRef.current = () => load(true);
  const edit = useAdsInlineEdit({ accountId, request, dateFrom, dateTo, setCampaignsCache, expandedSkusRef: expandedRef, reloadRef });

  function handleSortChange(key, dir) {
    setSortCol(key);
    setSortDir(dir);
  }

  function toggleExpand(skuId) {
    setExpanded(e => {
      const willOpen = !e[skuId];
      if (willOpen && campaignsCache[skuId] === undefined) {
        setCampaignsCache(c => ({ ...c, [skuId]: null }));
        fetchSkuCampaigns(accountId, skuId, dateFrom, dateTo, request, compareOn)
          .then(camps => setCampaignsCache(c => ({ ...c, [skuId]: camps })))
          .catch(() => setCampaignsCache(c => ({ ...c, [skuId]: [] })));
      }
      return { ...e, [skuId]: willOpen };
    });
  }

  const sorted = articles; // порядок задаёт сервер

  // Flatten article rows + (when expanded) their per-campaign breakdown rows,
  // so the hierarchy renders through a single flat UnifiedTable data array —
  // sort order is decided above and children stay attached to their parent.
  const tableData = useMemo(() => {
    const rows = [];
    for (const a of sorted) {
      rows.push({ ...a, _type: 'article', _key: `a-${a.sku_id}`, _prev: a.prev || null });
      if (expanded[a.sku_id]) {
        const camps = campaignsCache[a.sku_id];
        if (camps === undefined || camps === null) {
          rows.push({ _type: 'campaign_status', _key: `st-${a.sku_id}`, _label: 'Загрузка…' });
        } else if (!camps.length) {
          rows.push({ _type: 'campaign_status', _key: `st-${a.sku_id}`, _label: 'Нет данных за период' });
        } else {
          for (const c of camps) {
            rows.push({ ...c, _type: 'campaign', _skuId: a.sku_id, _key: `c-${a.sku_id}-${c.ozon_campaign_id}`, _prev: c.prev || null });
          }
        }
      }
    }
    return rows;
  }, [sorted, expanded, campaignsCache]);

  const columns = useMemo(() => ([
    {
      key: 'ident', header: 'Артикул', width: 280, minWidth: 220, hideable: false, sticky: true, sortable: false,
      render: (_v, row) => {
        if (row._type === 'campaign_status') {
          return <span className="indent-2 text-muted">{row._label}</span>;
        }
        if (row._type === 'campaign') {
          return <CampaignBreakdownCell row={row} onOpenCampaign={onOpenCampaign} />;
        }
        return (
          <div className="ads-ident-cell">
            <button type="button" className="expand-btn" tabIndex={-1}>{expanded[row.sku_id] ? '▾' : '▸'}</button>
            <div>
              <div className="camp-name">{row.product_name || '—'}</div>
              <div className="camp-meta">
                {row.offer_id && (
                  <>
                    <span>Арт. {row.offer_id}</span>
                    <CopyBtn value={row.offer_id} title="Скопировать артикул" />
                    <span className="meta-sep"> · </span>
                  </>
                )}
                <span>SKU {row.sku_id}</span>
                <CopyBtn value={row.sku_id} title="Скопировать SKU" />
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'campaign_count', header: 'РК', width: 50, align: 'center', sortable: false,
      render: (v, row) => (row._type === 'article' ? v : null),
    },
    makeBidColumn(edit),
    makeBudgetColumn(edit),
    { key: 'spent_rubles', header: 'Расход', width: 120, align: 'right', render: makeMetricRender('spent_rubles', 'money', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'impressions', header: 'Показы', width: 100, align: 'right', render: makeMetricRender('impressions', 'number', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'clicks', header: 'Клики', width: 90, align: 'right', render: makeMetricRender('clicks', 'number', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'ctr', header: 'CTR', width: 80, align: 'right', render: renderCtr(isPartialPeriod, compareOn) },
    { key: 'to_cart', header: 'Корзина', width: 90, align: 'right', render: makeMetricRender('to_cart', 'number', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'article_orders', header: 'Заказы рекл. шт', width: 130, align: 'right', render: makeMetricRender('article_orders', 'number', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'article_sales_rubles', header: 'Заказы рекл. ₽', width: 130, align: 'right', render: makeMetricRender('article_sales_rubles', 'money', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'model_add_orders', header: 'Заказы рекл модели, шт', width: 150, align: 'right', render: makeMetricRender('model_add_orders', 'number', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'model_add_sales_rubles', header: 'Заказы рекл модели, ₽', width: 150, align: 'right', render: makeMetricRender('model_add_sales_rubles', 'money', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'drr_ad', header: 'ДРР рекл.', width: 100, align: 'right', title: 'ДРР рекламный = расход / modelSales', render: makeMetricRender('drr_ad', 'percent', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'total_orders', header: 'Заказов всего шт', width: 130, align: 'right', render: makeMetricRender('total_orders', 'number', { isPartialPeriod, compareEnabled: compareOn, zeroGate: true, blankForCampaign: true }) },
    { key: 'total_revenue', header: 'Заказов всего ₽', width: 130, align: 'right', render: makeMetricRender('total_revenue', 'money', { isPartialPeriod, compareEnabled: compareOn, zeroGate: true, blankForCampaign: true }) },
    { key: 'drr_total', header: 'ДРР общий', width: 100, align: 'right', title: 'ДРР общий = расход / выручка (Статистика)', render: makeMetricRender('drr_total', 'percent', { isPartialPeriod, compareEnabled: compareOn, blankForCampaign: true }) },
    { key: 'stock_fbo', header: 'FBO', width: 70, align: 'center', sortable: false, render: (v, row) => (row._type === 'article' ? <StockBadge v={v} /> : null) },
    { key: 'stock_fbs', header: 'FBS', width: 70, align: 'center', sortable: false, render: (v, row) => (row._type === 'article' ? <StockBadge v={v} /> : null) },
  ]), [expanded, isPartialPeriod, compareOn, onOpenCampaign, edit]);

  return (
    <div className="tab-articles">
      <div className="tab-toolbar">
        <input
          className="search-input"
          placeholder="Поиск: товар, SKU, артикул, РК"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
        <select
          className="filter-select"
          value={stockFilter}
          onChange={e => {
            const val = e.target.value;
            setStockFilter(val);
            if (val !== 'fbo' && val !== 'fbs') setStockThreshold('');
          }}
        >
          {STOCK_FILTER_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {isStockThresholdApplicable && (
          <input
            type="number"
            min="0"
            className="filter-select stock-threshold-input"
            placeholder="Остаток больше чем"
            title="Выводить остаток больше чем"
            value={stockThreshold}
            onChange={e => setStockThreshold(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()}
          />
        )}
        <label className="compare-toggle" title="Показать дельту к предыдущему периоду такой же длины под каждым значением">
          <input type="checkbox" checked={compareOn} onChange={e => setCompareOn(e.target.checked)} />
          В сравнении с аналогичным периодом
        </label>
        <span className="count-badge">
          {totalCount > articles.length ? `${articles.length} из ${totalCount} артикулов` : `${articles.length} артикулов`}
        </span>
      </div>

      {totals && (
        <div className="ads-totals-bar">
          <b>Итого {totalCount} арт.</b>
          <span>Расход: {isPartialPeriod ? '—' : fMoney(totals.spent_rubles)}</span>
          <span>Показы: {isPartialPeriod ? '—' : fNum(totals.impressions)}</span>
          <span>Клики: {isPartialPeriod ? '—' : fNum(totals.clicks)}</span>
          <span>ДРР рекл.: {isPartialPeriod ? '—' : fPct(totals.drr_ad)}</span>
          <span>Заказов всего: {totals.total_orders > 0 ? fNum(totals.total_orders) : '—'} / {totals.total_revenue > 0 ? fMoney(totals.total_revenue) : '—'}</span>
          <span>ДРР общий: {isPartialPeriod ? '—' : fPct(totals.drr_total)}</span>
        </div>
      )}
      <DataCoverageBanner coverage={coverage} />

      {!loading && sorted.length === 0 ? (
        <div className="tab-empty">
          {loadError
            ? `Не удалось загрузить данные: ${loadError}`
            : (searchApplied || stockFilter)
              ? 'Нет артикулов по фильтру.'
              : 'Нет данных за период. Данные появятся после ежедневной синхронизации.'}
        </div>
      ) : (
        <>
          <UnifiedTable
            tableId="ads-campaigns-articles"
            columns={columns}
            data={tableData}
            getRowKey={(row) => row._key}
            onRowClick={(row) => { if (row._type === 'article') toggleExpand(row.sku_id); }}
            sortKey={sortCol}
            sortDir={sortDir}
            onSortChange={handleSortChange}
            serverSort
            showPagination={false}
            loading={loading}
            exportFilename="ads-articles"
          />
          {articles.length < totalCount && (
            <div className="load-more-row">
              <button className="btn-secondary btn-sm" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Загрузка…' : `Показать ещё (${totalCount - articles.length})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── TabModels ──────────────────────────────────────────────────────────────────

const MODELS_PAGE_SIZE = 200;

function TabModels({ accountId, dateFrom, dateTo, request, isPartialPeriod, onOpenCampaign, refreshNonce }) {
  const [models, setModels] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedModels, setExpandedModels] = useState({});
  const [expandedSkus, setExpandedSkus] = useState({});
  const [campaignsCache, setCampaignsCache] = useState({});
  const [sortCol, setSortCol] = useState('spent_rubles');
  const [sortDir, setSortDir] = useState('desc');
  const [stockFilter, setStockFilter] = useState('');
  const [stockThreshold, setStockThreshold] = useState('');
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [compareOn, setCompareOn] = useState(false);
  const [totals, setTotals] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    const tm = setTimeout(() => setSearchApplied(search.trim()), 400);
    return () => clearTimeout(tm);
  }, [search]);
  const queryKeyRef = useRef('');
  const expandedSkusRef = useRef({});
  expandedSkusRef.current = expandedSkus;
  const loadedCountRef = useRef(0);
  loadedCountRef.current = models.length;
  const reloadRef = useRef(null);

  const isStockThresholdApplicable = stockFilter === 'fbo' || stockFilter === 'fbs';

  const buildQuery = useCallback((offset) => {
    let q = `date_from=${dateFrom}&date_to=${dateTo}&limit=${MODELS_PAGE_SIZE}&offset=${offset}`;
    q += `&sort_by=${sortCol}&sort_dir=${sortDir}`;
    if (stockFilter) q += `&stock_filter=${stockFilter}`;
    if (isStockThresholdApplicable && stockThreshold !== '') q += `&stock_min=${stockThreshold}`;
    if (searchApplied) q += `&search=${encodeURIComponent(searchApplied)}`;
    if (compareOn) q += `&compare=true`;
    q += adsScopeParam();
    return q;
  }, [dateFrom, dateTo, sortCol, sortDir, stockFilter, isStockThresholdApplicable, stockThreshold, searchApplied, compareOn]);

  // silent — перезапрос после правки ставки/бюджета: раскрытые строки и подгруженные страницы остаются
  const load = useCallback((silentArg) => {
    const silent = silentArg === true;
    if (!accountId) return;
    if (!silent) setLoading(true);
    const q = silent
      ? buildQuery(0).replace(`limit=${MODELS_PAGE_SIZE}`, `limit=${Math.min(1000, Math.max(MODELS_PAGE_SIZE, loadedCountRef.current))}`)
      : buildQuery(0);
    const key = buildQuery(0);
    queryKeyRef.current = key;
    setLoadError(null);
    request(`/api/v1/ozon-ads/${accountId}/model-view?${q}`)
      .then(d => {
        if (queryKeyRef.current !== key) return;
        setModels(d.models || []);
        setTotalCount(d.total_count ?? (d.models || []).length);
        setTotals(d.totals || null);
        setCoverage(pickCoverage(d));
        if (!silent) { setExpandedModels({}); setExpandedSkus({}); setCampaignsCache({}); }
      })
      .catch(e => {
        if (queryKeyRef.current !== key) return;
        setModels([]); setTotalCount(0); setTotals(null); setLoadError(e.message || String(e));
      })
      .finally(() => setLoading(false));
  }, [accountId, buildQuery, request]);

  useEffect(() => { load(); }, [load, refreshNonce]);
  reloadRef.current = () => load(true);
  const edit = useAdsInlineEdit({ accountId, request, dateFrom, dateTo, setCampaignsCache, expandedSkusRef, reloadRef });

  const loadMore = useCallback(() => {
    if (!accountId || loadingMore) return;
    const key = queryKeyRef.current;
    setLoadingMore(true);
    request(`/api/v1/ozon-ads/${accountId}/model-view?${buildQuery(models.length)}`)
      .then(d => { if (queryKeyRef.current === key) setModels(prev => [...prev, ...(d.models || [])]); })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [accountId, buildQuery, models.length, loadingMore, request]);

  function handleSortChange(key, dir) {
    setSortCol(key);
    setSortDir(dir);
  }

  function toggleModelExpand(modelName) {
    setExpandedModels(e => ({ ...e, [modelName]: !e[modelName] }));
  }

  function toggleSkuExpand(skuId) {
    setExpandedSkus(e => {
      const willOpen = !e[skuId];
      if (willOpen && campaignsCache[skuId] === undefined) {
        setCampaignsCache(c => ({ ...c, [skuId]: null }));
        fetchSkuCampaigns(accountId, skuId, dateFrom, dateTo, request, compareOn)
          .then(camps => setCampaignsCache(c => ({ ...c, [skuId]: camps })))
          .catch(() => setCampaignsCache(c => ({ ...c, [skuId]: [] })));
      }
      return { ...e, [skuId]: willOpen };
    });
  }

  const sorted = models; // порядок задаёт сервер

  // Flatten model → sku → campaign hierarchy into one array (children only
  // included while their parent is expanded); sort order above is preserved
  // since children are always spliced in right after their parent.
  const tableData = useMemo(() => {
    const rows = [];
    for (const m of sorted) {
      rows.push({ ...m, _type: 'model', _key: `m-${m.model_name}`, _prev: m.prev || null });
      if (expandedModels[m.model_name]) {
        for (const sku of (m.skus || [])) {
          rows.push({ ...sku, _type: 'sku', _key: `s-${sku.sku_id}`, _prev: sku.prev || null });
          if (expandedSkus[sku.sku_id]) {
            const camps = campaignsCache[sku.sku_id];
            if (camps === undefined || camps === null) {
              rows.push({ _type: 'campaign_status', _key: `st-${sku.sku_id}`, _label: 'Загрузка…' });
            } else if (!camps.length) {
              rows.push({ _type: 'campaign_status', _key: `st-${sku.sku_id}`, _label: 'Нет данных за период' });
            } else {
              for (const c of camps) {
                rows.push({ ...c, _type: 'campaign', _skuId: sku.sku_id, _key: `c-${sku.sku_id}-${c.ozon_campaign_id}`, _prev: c.prev || null });
              }
            }
          }
        }
      }
    }
    return rows;
  }, [sorted, expandedModels, expandedSkus, campaignsCache]);

  const columns = useMemo(() => ([
    {
      key: 'ident', header: 'Модель / Артикул', width: 280, minWidth: 220, hideable: false, sticky: true, sortable: false,
      render: (_v, row) => {
        if (row._type === 'campaign_status') {
          return <span className="indent-2 text-muted">{row._label}</span>;
        }
        if (row._type === 'campaign') {
          return <CampaignBreakdownCell row={row} onOpenCampaign={onOpenCampaign} />;
        }
        if (row._type === 'sku') {
          return (
            <div className="ads-ident-cell" style={{ paddingLeft: 20 }}>
              <button type="button" className="expand-btn" tabIndex={-1}>{expandedSkus[row.sku_id] ? '▾' : '▸'}</button>
              <div>
                <div className="camp-name">{row.product_name || '—'}</div>
                <div className="camp-meta">
                  {row.offer_id && (
                    <>
                      <span>Арт. {row.offer_id}</span>
                      <CopyBtn value={row.offer_id} title="Скопировать артикул" />
                      <span className="meta-sep"> · </span>
                    </>
                  )}
                  <span>SKU {row.sku_id}</span>
                  <CopyBtn value={row.sku_id} title="Скопировать SKU" />
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="ads-ident-cell">
            <button type="button" className="expand-btn" tabIndex={-1}>{expandedModels[row.model_name] ? '▾' : '▸'}</button>
            <span className="model-name">{row.model_name || '— без модели —'}</span>
          </div>
        );
      },
    },
    {
      key: 'sku_count', header: 'SKU', width: 60, align: 'center', sortable: false,
      render: (v, row) => (row._type === 'model' ? v : null),
    },
    makeBidColumn(edit),
    makeBudgetColumn(edit),
    { key: 'spent_rubles', header: 'Расход', width: 120, align: 'right', render: makeMetricRender('spent_rubles', 'money', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'impressions', header: 'Показы', width: 100, align: 'right', render: makeMetricRender('impressions', 'number', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'clicks', header: 'Клики', width: 90, align: 'right', render: makeMetricRender('clicks', 'number', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'ctr', header: 'CTR', width: 80, align: 'right', render: renderCtr(isPartialPeriod, compareOn) },
    { key: 'to_cart', header: 'Корзина', width: 90, align: 'right', render: makeMetricRender('to_cart', 'number', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'article_orders', header: 'Заказы рекл. шт', width: 130, align: 'right', render: makeMetricRender('article_orders', 'number', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'article_sales_rubles', header: 'Заказы рекл. ₽', width: 130, align: 'right', render: makeMetricRender('article_sales_rubles', 'money', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'model_add_orders', header: 'Заказы рекл модели, шт', width: 150, align: 'right', render: makeMetricRender('model_add_orders', 'number', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'model_add_sales_rubles', header: 'Заказы рекл модели, ₽', width: 150, align: 'right', render: makeMetricRender('model_add_sales_rubles', 'money', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'drr_ad', header: 'ДРР рекл.', width: 100, align: 'right', title: 'ДРР рекламный = расход / modelSales', render: makeMetricRender('drr_ad', 'percent', { isPartialPeriod, compareEnabled: compareOn }) },
    { key: 'total_orders', header: 'Заказов всего шт', width: 130, align: 'right', render: makeMetricRender('total_orders', 'number', { isPartialPeriod, compareEnabled: compareOn, zeroGate: true, blankForCampaign: true }) },
    { key: 'total_revenue', header: 'Заказов всего ₽', width: 130, align: 'right', render: makeMetricRender('total_revenue', 'money', { isPartialPeriod, compareEnabled: compareOn, zeroGate: true, blankForCampaign: true }) },
    { key: 'drr_total', header: 'ДРР общий', width: 100, align: 'right', title: 'ДРР общий = расход / выручка (Статистика)', render: makeMetricRender('drr_total', 'percent', { isPartialPeriod, compareEnabled: compareOn, blankForCampaign: true }) },
  ]), [expandedModels, expandedSkus, isPartialPeriod, compareOn, onOpenCampaign, edit]);

  const toolbar = (
    <div className="tab-toolbar">
      <input
        className="search-input"
        placeholder="Поиск: модель, товар, SKU, артикул, РК"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      <select
        className="filter-select"
        value={stockFilter}
        onChange={e => {
          const val = e.target.value;
          setStockFilter(val);
          if (val !== 'fbo' && val !== 'fbs') setStockThreshold('');
        }}
      >
        {STOCK_FILTER_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {isStockThresholdApplicable && (
        <input
          type="number"
          min="0"
          className="filter-select stock-threshold-input"
          placeholder="Остаток больше чем"
          title="Выводить остаток больше чем"
          value={stockThreshold}
          onChange={e => setStockThreshold(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
      )}
      <label className="compare-toggle" title="Показать дельту к предыдущему периоду такой же длины под каждым значением">
        <input type="checkbox" checked={compareOn} onChange={e => setCompareOn(e.target.checked)} />
        В сравнении с аналогичным периодом
      </label>
    </div>
  );

  if (loading) return (
    <div className="tab-models">
      {toolbar}
      <div className="tab-loading">Загрузка…</div>
    </div>
  );
  if (!models.length) return (
    <div className="tab-models">
      {toolbar}
      <DataCoverageBanner coverage={coverage} />
      <div className="tab-empty">
        {loadError
          ? `Не удалось загрузить данные: ${loadError}`
          : stockFilter
            ? 'Нет моделей по фильтру.'
            : 'Нет данных за период. Данные появятся после ежедневной синхронизации.'}
      </div>
    </div>
  );

  return (
    <div className="tab-models">
      {toolbar}
      {totals && (
        <div className="ads-totals-bar">
          <b>Итого {totalCount} мод.</b>
          <span>Расход: {isPartialPeriod ? '—' : fMoney(totals.spent_rubles)}</span>
          <span>Показы: {isPartialPeriod ? '—' : fNum(totals.impressions)}</span>
          <span>Клики: {isPartialPeriod ? '—' : fNum(totals.clicks)}</span>
          <span>ДРР рекл.: {isPartialPeriod ? '—' : fPct(totals.drr_ad)}</span>
          <span>Заказов всего: {totals.total_orders > 0 ? fNum(totals.total_orders) : '—'} / {totals.total_revenue > 0 ? fMoney(totals.total_revenue) : '—'}</span>
          <span>ДРР общий: {isPartialPeriod ? '—' : fPct(totals.drr_total)}</span>
        </div>
      )}
      <DataCoverageBanner coverage={coverage} />
      <UnifiedTable
        tableId="ads-campaigns-models"
        columns={columns}
        data={tableData}
        getRowKey={(row) => row._key}
        onRowClick={(row) => {
          if (row._type === 'model') toggleModelExpand(row.model_name);
          else if (row._type === 'sku') toggleSkuExpand(row.sku_id);
        }}
        sortKey={sortCol}
        sortDir={sortDir}
        onSortChange={handleSortChange}
        serverSort
        showPagination={false}
        loading={loading}
        exportFilename="ads-models"
      />
      {models.length < totalCount && (
        <div className="load-more-row">
          <button className="btn-secondary btn-sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Загрузка…' : `Показать ещё (${totalCount - models.length})`}
          </button>
        </div>
      )}
    </div>
  );
}

// ── TabCustomGroups ────────────────────────────────────────────────────────────
// «Свои группы»: пользователь сам собирает группы SKU (таблица «Название — SKU»);
// по группе — те же метрики, что «По артикулам», + средняя ставка и общий
// недельный бюджет, массовое изменение ставки/бюджета на всю группу.
// SKU вне групп сидят в строке «Без группы» (разворачивается как «По артикулам»).

const COVERAGE_FILTER_OPTIONS = [
  { value: '', label: 'Любое размещение' },
  { value: 'search_only', label: 'Только Поиск' },
  { value: 'rec_only', label: 'Только Поиск и рекомендации' },
  { value: 'both', label: 'Поиск + Поиск и рекомендации' },
  { value: 'no_ads', label: 'Без рекламы' },
];

const COVERAGE_BADGES = {
  no_ads: { label: 'Без рекламы', cls: 'cov-none' },
  search_only: { label: 'Поиск', cls: 'cov-search' },
  rec_only: { label: 'ПиР', cls: 'cov-rec' },
  both: { label: 'Поиск + ПиР', cls: 'cov-both' },
};

const UNGROUPED_KEY = '__ungrouped';

// «Название ⇥ SKU» (Excel) или «Название - SKU, SKU» → [{name, skus}]
function parseGroupsPaste(text) {
  const byName = new Map();
  for (const raw of String(text || '').split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    let name;
    let skus;
    if (line.includes('\t')) {
      const i = line.indexOf('\t');
      name = line.slice(0, i);
      skus = line.slice(i + 1);
    } else if (line.includes(' - ')) {
      const i = line.indexOf(' - ');
      name = line.slice(0, i);
      skus = line.slice(i + 3);
    } else {
      continue;
    }
    name = name.trim();
    skus = skus.replace(/\t/g, ' ').trim();
    if (!name || !skus) continue;
    const key = name.toLowerCase();
    const prev = byName.get(key);
    byName.set(key, { name: prev?.name || name, skus: [prev?.skus, skus].filter(Boolean).join(', ') });
  }
  return [...byName.values()];
}

let rowKeySeq = 0;
const newRowKey = () => `new-${++rowKeySeq}`;

function GroupsEditorModal({ accountId, request, onClose, onSaved }) {
  const [rows, setRows] = useState(null);
  const [removed, setRemoved] = useState([]);
  const [paste, setPaste] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    request(`/api/v1/ozon-ads/${accountId}/custom-groups`)
      .then(d => setRows((d.groups || []).map(g => ({
        key: g.id, id: g.id, name: g.name, skus: g.skus.map(s => s.offer_id || s.sku_id).join('\n'),
      }))))
      .catch(e => { setRows([]); setErrors([e.message || String(e)]); });
  }, [accountId, request]);

  function addRow() {
    setRows(r => [...r, { key: newRowKey(), id: null, name: '', skus: '' }]);
  }

  function importPaste() {
    const parsed = parseGroupsPaste(paste);
    if (!parsed.length) {
      setErrors(['Не нашёл строк вида «Название ⇥ артикул» или «Название - артикул, артикул»']);
      return;
    }
    setErrors([]);
    setRows(prev => {
      const next = prev.map(r => ({ ...r }));
      for (const p of parsed) {
        const hit = next.find(r => r.name.trim().toLowerCase() === p.name.toLowerCase());
        if (hit) hit.skus = [hit.skus, p.skus].filter(Boolean).join(', ');
        else next.push({ key: newRowKey(), id: null, name: p.name, skus: p.skus });
      }
      return next;
    });
    setPaste('');
  }

  function removeRow(row) {
    if (row.id) setRemoved(r => [...r, row.id]);
    setRows(r => r.filter(x => x.key !== row.key));
  }

  async function save() {
    setSaving(true);
    setErrors([]);
    const base = `/api/v1/ozon-ads/${accountId}/custom-groups`;
    // id созданных в этом сохранении групп: повтор после ошибки идёт PUT, а не POST.
    const createdIds = {};
    const ops = [
      ...removed.map(id => ({
        label: 'удаление',
        run: async () => {
          await request(`${base}/${id}`, { method: 'DELETE' });
          setRemoved(rm => rm.filter(x => x !== id));
        },
      })),
      ...rows.filter(r => r.name.trim() || r.skus.trim()).map(r => ({
        label: r.name || 'без названия',
        run: async () => {
          const id = r.id || createdIds[r.key];
          const saved = await request(id ? `${base}/${id}` : base, {
            method: id ? 'PUT' : 'POST',
            body: JSON.stringify({ name: r.name, skus: r.skus }),
          });
          // созданной группе сразу даём id — при повторном «Сохранить» это будет PUT, а не дубль
          if (!id && saved?.id) {
            createdIds[r.key] = saved.id;
            setRows(rs => rs.map(x => (x.key === r.key ? { ...x, id: saved.id } : x)));
          }
        },
      })),
    ];
    // SKU, переехавшие из одной группы в другую, упираются в «один SKU — одна группа»,
    // если новая группа обработана раньше старой — повторяем проваленные ещё раз.
    let pending = ops;
    let failed = [];
    for (let pass = 0; pass < 2 && pending.length; pass++) {
      failed = [];
      for (const op of pending) {
        try { await op.run(); } catch (e) { failed.push({ op, msg: e.message || String(e) }); }
      }
      pending = failed.map(f => f.op);
    }
    setSaving(false);
    onSaved();
    if (failed.length) {
      setErrors(failed.map(f => `«${f.op.label}»: ${f.msg}`));
      return;
    }
    onClose();
  }

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box cg-editor" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span>Свои группы</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body cg-editor-body">
          {rows === null ? <div className="tab-loading">Загрузка…</div> : (
            <>
              <div className="cg-hint">
                Группа — название и список артикулов (как в карточке товара, например 12605micro). Подойдёт и числовой SKU Ozon. Один артикул — с новой строки; запятая и «;» тоже разделяют, пробел — нет. Один товар состоит только в одной группе.
              </div>
              <table className="cg-edit-table">
                <thead><tr><th style={{ width: '30%' }}>Название группы</th><th>Артикулы (или SKU)</th><th /></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.key}>
                      <td>
                        <input
                          className="modal-search"
                          value={r.name}
                          placeholder="Например, Кроссовки лето"
                          onChange={e => setRows(rs => rs.map(x => (x.key === r.key ? { ...x, name: e.target.value } : x)))}
                        />
                      </td>
                      <td>
                        <textarea
                          className="modal-search cg-sku-input"
                          rows={4}
                          value={r.skus}
                          placeholder={"12605micro\n12607\nART 001"}
                          onChange={e => setRows(rs => rs.map(x => (x.key === r.key ? { ...x, skus: e.target.value } : x)))}
                        />
                      </td>
                      <td><button type="button" className="cg-del" title="Удалить группу" onClick={() => removeRow(r)}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div><button className="btn-secondary btn-sm" onClick={addRow}>+ Добавить группу</button></div>
              <details className="cg-paste">
                <summary>Вставить из таблицы (Excel / Google Sheets)</summary>
                <textarea
                  className="modal-search"
                  rows={4}
                  value={paste}
                  placeholder={'Скопируйте два столбца «Название — артикул» и вставьте сюда.\nСтроки с одним названием объединятся в одну группу.'}
                  onChange={e => setPaste(e.target.value)}
                />
                <button className="btn-secondary btn-sm" onClick={importPaste} disabled={!paste.trim()}>Добавить в таблицу</button>
              </details>
              {errors.length > 0 && (
                <div className="cg-errors">{errors.map((er, i) => <div key={i}>{er}</div>)}</div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Закрыть</button>
          <button className="btn-primary" onClick={save} disabled={saving || rows === null}>{saving ? 'Сохраняю…' : 'Сохранить'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const MANUAL_BID_STRATEGIES = ['', 'NO_AUTO_STRATEGY', 'TARGET_BIDS'];
const CLOSED_CAMPAIGN_STATES = ['CAMPAIGN_STATE_ARCHIVED', 'CAMPAIGN_STATE_FINISHED'];

// Число, которое правится по клику: Enter — сохранить, Escape/уход фокуса — отмена.
function EditableMoneyCell({ value, digits, onSave, disabled, title, suffix = null }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  async function commit() {
    const v = parseFloat(String(text).replace(',', '.'));
    if (!Number.isFinite(v) || v <= 0 || v === Number(value)) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(v);
    } catch (e) {
      notify('error', 'Ошибка: ' + (e.message || e));
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }

  if (editing) {
    return (
      <input
        type="number"
        min="0"
        step={digits ? '0.01' : '1'}
        className="budget-edit-input"
        value={text}
        autoFocus
        disabled={saving}
        onClick={e => e.stopPropagation()}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        onBlur={() => { if (!saving) setEditing(false); }}
      />
    );
  }
  return (
    <span
      className={`num-cell ${disabled ? '' : 'cg-editable'}`}
      title={disabled ? undefined : title}
      onClick={e => {
        if (disabled) return;
        e.stopPropagation();
        setText(String(value ?? ''));
        setEditing(true);
      }}
    >
      {value ? fMoney(value, digits) : '—'}
      {suffix && <span className="text-muted">{suffix}</span>}
    </span>
  );
}

function TabCustomGroups({ accountId, dateFrom, dateTo, request, isPartialPeriod, onOpenCampaign, refreshNonce }) {
  const [data, setData] = useState({ groups: [], ungrouped: null });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [coverage, setCoverage] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [stockThreshold, setStockThreshold] = useState('');
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [sortCol, setSortCol] = useState('spent_rubles');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedSkus, setExpandedSkus] = useState({});
  const expandedSkusRef = useRef({});
  expandedSkusRef.current = expandedSkus;
  const [campaignsCache, setCampaignsCache] = useState({});
  const [editorOpen, setEditorOpen] = useState(false);

  const isStockThresholdApplicable = stockFilter === 'fbo' || stockFilter === 'fbs';
  const filtersActive = !!(coverage || stockFilter || searchApplied);

  useEffect(() => {
    const tm = setTimeout(() => setSearchApplied(search.trim()), 400);
    return () => clearTimeout(tm);
  }, [search]);

  // silent — перезапрос после правки в строке: раскрытые строки и кэш кампаний остаются
  const load = useCallback((silentArg) => {
    const silent = silentArg === true;
    if (!accountId) return;
    if (!silent) setLoading(true);
    setLoadError('');
    let q = `date_from=${dateFrom}&date_to=${dateTo}`;
    if (coverage) q += `&coverage=${coverage}`;
    if (stockFilter) q += `&stock_filter=${stockFilter}`;
    if (isStockThresholdApplicable && stockThreshold !== '') q += `&stock_min=${stockThreshold}`;
    if (searchApplied) q += `&search=${encodeURIComponent(searchApplied)}`;
    q += adsScopeParam();
    request(`/api/v1/ozon-ads/${accountId}/custom-groups/view?${q}`)
      .then(d => {
        setData({ groups: d.groups || [], ungrouped: d.ungrouped || null });
        if (!silent) { setCampaignsCache({}); setExpandedSkus({}); }
      })
      .catch(e => { setData({ groups: [], ungrouped: null }); setLoadError(e.message || String(e)); })
      .finally(() => setLoading(false));
  }, [accountId, dateFrom, dateTo, coverage, stockFilter, isStockThresholdApplicable, stockThreshold, searchApplied, request]);

  useEffect(() => { load(); }, [load, refreshNonce]);

  const reloadRef = useRef(null);
  reloadRef.current = () => load(true);
  const edit = useAdsInlineEdit({ accountId, request, dateFrom, dateTo, setCampaignsCache, expandedSkusRef, reloadRef });

  function toggleSkuExpand(skuId) {
    setExpandedSkus(e => {
      const willOpen = !e[skuId];
      if (willOpen && campaignsCache[skuId] === undefined) {
        setCampaignsCache(c => ({ ...c, [skuId]: null }));
        fetchSkuCampaigns(accountId, skuId, dateFrom, dateTo, request, false)
          .then(camps => setCampaignsCache(c => ({ ...c, [skuId]: camps })))
          .catch(() => setCampaignsCache(c => ({ ...c, [skuId]: [] })));
      }
      return { ...e, [skuId]: willOpen };
    });
  }

  const sortRows = useCallback((arr) => [...arr].sort((a, b) => {
    const va = a[sortCol] ?? -Infinity;
    const vb = b[sortCol] ?? -Infinity;
    return sortDir === 'desc' ? vb - va : va - vb;
  }), [sortCol, sortDir]);

  const tableData = useMemo(() => {
    const rows = [];
    const push = (g, key, ungrouped) => {
      rows.push({ ...g, _type: 'group', _key: `g-${key}`, _gkey: key, _ungrouped: ungrouped });
      if (!expandedGroups[key]) return;
      for (const sku of sortRows(g.skus || [])) {
        rows.push({ ...sku, _type: 'sku', _key: `s-${sku.sku_id}` });
        if (!expandedSkus[sku.sku_id]) continue;
        const camps = campaignsCache[sku.sku_id];
        if (camps === undefined || camps === null) {
          rows.push({ _type: 'campaign_status', _key: `st-${sku.sku_id}`, _label: 'Загрузка…' });
        } else if (!camps.length) {
          rows.push({ _type: 'campaign_status', _key: `st-${sku.sku_id}`, _label: 'Нет данных за период' });
        } else {
          for (const c of camps) {
            rows.push({ ...c, _type: 'campaign', _skuId: sku.sku_id, _key: `c-${sku.sku_id}-${c.ozon_campaign_id}` });
          }
        }
      }
    };
    for (const g of sortRows(data.groups)) push(g, g.id, false);
    if (data.ungrouped) push(data.ungrouped, UNGROUPED_KEY, true);
    return rows;
  }, [data, sortRows, expandedGroups, expandedSkus, campaignsCache]);

  const columns = useMemo(() => ([
    {
      key: 'ident', header: 'Группа / Артикул', width: 300, minWidth: 220, hideable: false, sticky: true, sortable: false,
      render: (_v, row) => {
        if (row._type === 'campaign_status') return <span className="indent-2 text-muted">{row._label}</span>;
        if (row._type === 'campaign') return <CampaignBreakdownCell row={row} onOpenCampaign={onOpenCampaign} />;
        if (row._type === 'sku') {
          const cov = COVERAGE_BADGES[row.coverage];
          return (
            <div className="ads-ident-cell" style={{ paddingLeft: 20 }}>
              <button type="button" className="expand-btn" tabIndex={-1}>{expandedSkus[row.sku_id] ? '▾' : '▸'}</button>
              <div>
                <div className="camp-name">{row.product_name || '—'}</div>
                <div className="camp-meta">
                  {row.offer_id && (
                    <>
                      <span>Арт. {row.offer_id}</span>
                      <CopyBtn value={row.offer_id} title="Скопировать артикул" />
                      <span className="meta-sep"> · </span>
                    </>
                  )}
                  <span>SKU {row.sku_id}</span>
                  <CopyBtn value={row.sku_id} title="Скопировать SKU" />
                  {cov && <span className={`state-badge ${cov.cls}`} style={{ marginLeft: 6, fontSize: 12 }}>{cov.label}</span>}
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="ads-ident-cell">
            <button type="button" className="expand-btn" tabIndex={-1}>{expandedGroups[row._gkey] ? '▾' : '▸'}</button>
            <span className={`model-name ${row._ungrouped ? 'text-muted' : ''}`}>{row.name}</span>
          </div>
        );
      },
    },
    {
      key: 'sku_count', header: 'SKU', width: 80, align: 'center', sortable: false,
      render: (v, row) => {
        if (row._type !== 'group') return null;
        return row.total_sku_count !== v ? `${v} из ${row.total_sku_count}` : v;
      },
    },
    makeBidColumn(edit),
    makeBudgetColumn(edit),
    { key: 'spent_rubles', header: 'Расход', width: 120, align: 'right', render: makeMetricRender('spent_rubles', 'money', { isPartialPeriod }) },
    { key: 'impressions', header: 'Показы', width: 100, align: 'right', render: makeMetricRender('impressions', 'number', { isPartialPeriod }) },
    { key: 'clicks', header: 'Клики', width: 90, align: 'right', render: makeMetricRender('clicks', 'number', { isPartialPeriod }) },
    { key: 'ctr', header: 'CTR', width: 80, align: 'right', render: renderCtr(isPartialPeriod, false) },
    { key: 'to_cart', header: 'Корзина', width: 90, align: 'right', render: makeMetricRender('to_cart', 'number', { isPartialPeriod }) },
    { key: 'article_orders', header: 'Заказы рекл. шт', width: 130, align: 'right', render: makeMetricRender('article_orders', 'number', { isPartialPeriod }) },
    { key: 'article_sales_rubles', header: 'Заказы рекл. ₽', width: 130, align: 'right', render: makeMetricRender('article_sales_rubles', 'money', { isPartialPeriod }) },
    { key: 'model_add_orders', header: 'Заказы рекл модели, шт', width: 150, align: 'right', render: makeMetricRender('model_add_orders', 'number', { isPartialPeriod }) },
    { key: 'model_add_sales_rubles', header: 'Заказы рекл модели, ₽', width: 150, align: 'right', render: makeMetricRender('model_add_sales_rubles', 'money', { isPartialPeriod }) },
    { key: 'drr_ad', header: 'ДРР рекл.', width: 100, align: 'right', title: 'ДРР рекламный = расход / modelSales', render: makeMetricRender('drr_ad', 'percent', { isPartialPeriod }) },
    { key: 'total_orders', header: 'Заказов всего шт', width: 130, align: 'right', render: makeMetricRender('total_orders', 'number', { isPartialPeriod, zeroGate: true, blankForCampaign: true }) },
    { key: 'total_revenue', header: 'Заказов всего ₽', width: 130, align: 'right', render: makeMetricRender('total_revenue', 'money', { isPartialPeriod, zeroGate: true, blankForCampaign: true }) },
    { key: 'drr_total', header: 'ДРР общий', width: 100, align: 'right', title: 'ДРР общий = расход / выручка (Статистика)', render: makeMetricRender('drr_total', 'percent', { isPartialPeriod, blankForCampaign: true }) },
    { key: 'stock_fbo', header: 'FBO', width: 70, align: 'center', sortable: false, render: (v, row) => (row._type === 'sku' ? <StockBadge v={v} /> : null) },
    { key: 'stock_fbs', header: 'FBS', width: 70, align: 'center', sortable: false, render: (v, row) => (row._type === 'sku' ? <StockBadge v={v} /> : null) },
  ]), [expandedGroups, expandedSkus, isPartialPeriod, onOpenCampaign, edit]);

  const hasAny = data.groups.length > 0 || !!data.ungrouped;

  return (
    <div className="tab-models tab-custom-groups">
      <div className="tab-toolbar">
        <input
          className="search-input"
          placeholder="Поиск: товар, SKU, артикул, группа, РК"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="btn-primary btn-sm" onClick={() => setEditorOpen(true)}>Управление группами</button>
        <select className="filter-select" value={coverage} onChange={e => setCoverage(e.target.value)} title="Размещение товаров в активных кампаниях">
          {COVERAGE_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          className="filter-select"
          value={stockFilter}
          onChange={e => {
            const val = e.target.value;
            setStockFilter(val);
            if (val !== 'fbo' && val !== 'fbs') setStockThreshold('');
          }}
        >
          {STOCK_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {isStockThresholdApplicable && (
          <input
            type="number"
            min="0"
            className="filter-select stock-threshold-input"
            placeholder="Остаток больше чем"
            title="Выводить остаток больше чем"
            value={stockThreshold}
            onChange={e => setStockThreshold(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()}
          />
        )}
        <span className="count-badge">{data.groups.length} групп</span>
      </div>
      {loadError && <div className="cg-errors">{loadError}</div>}
      {!loading && !hasAny ? (
        <div className="tab-empty">
          {filtersActive ? 'По выбранным фильтрам ничего нет.' : 'Нет данных за период. Данные появятся после ежедневной синхронизации.'}
        </div>
      ) : (
        <UnifiedTable
          tableId="ads-campaigns-custom-groups"
          columns={columns}
          data={tableData}
          getRowKey={(row) => row._key}
          onRowClick={(row) => {
            if (row._type === 'group') setExpandedGroups(e => ({ ...e, [row._gkey]: !e[row._gkey] }));
            else if (row._type === 'sku') toggleSkuExpand(row.sku_id);
          }}
          sortKey={sortCol}
          sortDir={sortDir}
          onSortChange={(key, dir) => { setSortCol(key); setSortDir(dir); }}
          serverSort
          showPagination={false}
          loading={loading}
          exportFilename="ads-custom-groups"
        />
      )}
      {editorOpen && (
        <GroupsEditorModal
          accountId={accountId}
          request={request}
          onClose={() => setEditorOpen(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}

// ── Analyzer: coverage section ────────────────────────────────────────────────

const COVERAGE_SECTION_LABELS = {
  no_ads:      { label: 'Без рекламы',          cls: 'cov-none'   },
  search_only: { label: 'Только Поиск',          cls: 'cov-search' },
  rec_only:    { label: 'Только ПиР',            cls: 'cov-rec'    },
  both:        { label: 'Поиск + ПиР',           cls: 'cov-both'   },
};

function CoverageProductTable({ items, onSelectForCreate, onNavigateToCampaigns }) {
  const [selected, setSelected] = useState({});
  const allIds = items.map(p => p.sku_id);
  const allSelected = allIds.length > 0 && allIds.every(id => selected[id]);

  function toggleAll() {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(allIds.map(id => [id, true])));
  }

  const selectedSkus = Object.keys(selected).filter(k => selected[k]).map(Number);

  const columns = useMemo(() => ([
    {
      key: '_select', header: '', width: 40, align: 'center', sortable: false,
      render: (_v, p) => (
        <input
          type="checkbox"
          checked={!!selected[p.sku_id]}
          onChange={() => setSelected(s => ({ ...s, [p.sku_id]: !s[p.sku_id] }))}
          onClick={e => e.stopPropagation()}
        />
      ),
    },
    {
      key: 'sku_id', header: 'SKU', width: 120,
      render: (v) => (
        <a className="sku-link" href={`https://www.ozon.ru/product/${v}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>{v}</a>
      ),
    },
    { key: 'offer_id', header: 'Артикул', width: 160, render: (v) => v || '—' },
    {
      key: 'name', header: 'Название', width: 320,
      render: (v, p) => (
        <div className="prod-name-cell">
          {p.primary_image && <img src={p.primary_image} alt="" className="prod-thumb" />}
          <div className="prod-name-info"><div className="prod-name">{v || '—'}</div></div>
        </div>
      ),
    },
    { key: 'model_name', header: 'Модель', width: 160, render: (v) => v || '—' },
    { key: 'stock_fbo', header: 'FBO', width: 80, align: 'right', render: (v) => v ?? 0 },
    { key: 'stock_fbs', header: 'FBS', width: 80, align: 'right', render: (v) => v ?? 0 },
    {
      key: 'campaigns', header: 'РК', width: 160, sortable: false,
      render: (camps) => (camps?.length > 0 ? (
        <div className="camp-tags">
          {camps.map((c, i) => (
            <span
              key={i}
              className={`state-badge ${c.placement === 'PLACEMENT_TOP_PROMOTION' ? 'badge-search' : 'badge-rec'} ${onNavigateToCampaigns ? 'cov-camp-link' : ''}`}
              title={c.name || ''}
              onClick={onNavigateToCampaigns && c.name ? (e) => { e.stopPropagation(); onNavigateToCampaigns(c.name); } : undefined}
            >
              {c.placement === 'PLACEMENT_TOP_PROMOTION' ? 'Поиск' : 'ПиР'}
            </span>
          ))}
        </div>
      ) : <span className="text-muted">—</span>),
    },
  ]), [selected, onNavigateToCampaigns]);

  return (
    <div className="coverage-table-wrap">
      <div className="coverage-selection-bar">
        <label>
          <input type="checkbox" checked={allSelected} onChange={toggleAll} /> Выбрать все
        </label>
        {selectedSkus.length > 0 && (
          <>
            <span>{selectedSkus.length} арт. выбрано</span>
            <button className="btn-primary btn-sm" onClick={() => onSelectForCreate(selectedSkus)}>
              Создать РК для выбранных
            </button>
          </>
        )}
      </div>
      <UnifiedTable
        tableId="ads-coverage"
        columns={columns}
        data={items}
        getRowKey={(row) => row.sku_id}
        showPagination={false}
        exportFilename="ads-coverage"
      />
    </div>
  );
}

const COVERAGE_PAGE_SIZE = 500;

function AnalyzerCoverage({ accountId, request, onCreateForSkus, onNavigateToCampaigns }) {
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [sectionItems, setSectionItems] = useState([]);
  const [sectionTotal, setSectionTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeSection, setActiveSection] = useState('no_ads');
  const [stockFilter, setStockFilter] = useState('all');
  const [stockThreshold, setStockThreshold] = useState('');
  const [loaded, setLoaded] = useState(false);

  const isStockThresholdApplicable = stockFilter === 'fbo' || stockFilter === 'fbs';

  const load = useCallback((sf = stockFilter, sec = activeSection, st = stockThreshold) => {
    setLoading(true);
    let url = `/api/v1/ozon-ads/${accountId}/analyzer/coverage?stock_filter=${sf}&section=${sec}&limit=${COVERAGE_PAGE_SIZE}&offset=0`;
    if ((sf === 'fbo' || sf === 'fbs') && st !== '') url += `&stock_min=${st}`;
    request(url)
      .then(d => {
        setSummary(d.summary || null);
        setTotal(d.total || 0);
        setSectionItems(d[sec] || []);
        setSectionTotal(d.section_total ?? (d[sec] || []).length);
        setLoaded(true);
      })
      .catch(() => { setSummary(null); setSectionItems([]); setSectionTotal(0); })
      .finally(() => setLoading(false));
  }, [accountId, activeSection, request, stockFilter, stockThreshold]);

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    let url = `/api/v1/ozon-ads/${accountId}/analyzer/coverage?stock_filter=${stockFilter}&section=${activeSection}&limit=${COVERAGE_PAGE_SIZE}&offset=${sectionItems.length}`;
    if (isStockThresholdApplicable && stockThreshold !== '') url += `&stock_min=${stockThreshold}`;
    request(url)
      .then(d => setSectionItems(prev => [...prev, ...(d[activeSection] || [])]))
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [accountId, activeSection, isStockThresholdApplicable, loadingMore, request, sectionItems.length, stockFilter, stockThreshold]);

  function handleStockFilter(val) {
    setStockFilter(val);
    if (val !== 'fbo' && val !== 'fbs') setStockThreshold('');
    if (loaded) load(val, activeSection, (val === 'fbo' || val === 'fbs') ? stockThreshold : '');
  }

  function applyStockThreshold(val) {
    setStockThreshold(val);
    if (loaded) load(stockFilter, activeSection, val);
  }

  function handleSectionChange(key) {
    setActiveSection(key);
    if (loaded) load(stockFilter, key);
  }

  useEffect(() => {
    load();
  }, [accountId]); // eslint-disable-line

  if (!loaded && !loading) {
    return <div className="tab-loading">Анализ покрытия…</div>;
  }

  if (loading) return <div className="tab-loading">Анализ покрытия…</div>;

  return (
    <div className="analyzer-coverage">
      <div className="coverage-header">
        <div className="coverage-stock-filter">
          {STOCK_FILTER_OPTIONS.map(o => (
            <button
              key={o.value}
              className={`stock-filter-btn ${stockFilter === o.value ? 'active' : ''}`}
              onClick={() => handleStockFilter(o.value)}
            >{o.label}</button>
          ))}
          {isStockThresholdApplicable && (
            <input
              type="number"
              min="0"
              className="stock-threshold-input"
              placeholder="Остаток больше чем"
              title="Выводить остаток больше чем"
              value={stockThreshold}
              onChange={e => setStockThreshold(e.target.value)}
              onBlur={e => applyStockThreshold(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyStockThreshold(e.target.value)}
            />
          )}
        </div>
        <div className="coverage-stats-row">
          {Object.entries(COVERAGE_SECTION_LABELS).map(([key, info]) => (
            <button
              key={key}
              className={`coverage-stat-card ${info.cls} ${activeSection === key ? 'active' : ''}`}
              onClick={() => handleSectionChange(key)}
            >
              <span className="cov-count">{summary?.[key] ?? 0}</span>
              <span className="cov-label">{info.label}</span>
            </button>
          ))}
          <div className="coverage-stat-card cov-total">
            <span className="cov-count">{total}</span>
            <span className="cov-label">Всего товаров</span>
          </div>
        </div>
        <button className="btn-secondary btn-sm" onClick={() => load()} style={{ marginLeft: 'auto' }}>↺ Обновить</button>
      </div>

      <div className="coverage-section-title">
        {COVERAGE_SECTION_LABELS[activeSection]?.label} — {sectionItems.length} из {sectionTotal} товаров
      </div>

      {sectionItems.length === 0 ? (
        <div className="tab-empty">Нет товаров в этой категории</div>
      ) : (
        <>
          <CoverageProductTable
            key={`${activeSection}|${stockFilter}|${stockThreshold}`}
            items={sectionItems}
            onSelectForCreate={skus => onCreateForSkus(skus)}
            onNavigateToCampaigns={onNavigateToCampaigns}
          />
          {sectionItems.length < sectionTotal && (
            <div className="load-more-row">
              <button className="btn-secondary btn-sm" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Загрузка…' : `Показать ещё (${sectionTotal - sectionItems.length})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Analyzer: generator ───────────────────────────────────────────────────────

const GENERATOR_CAMPAIGN_TYPES = [
  { placement: 'PLACEMENT_TOP_PROMOTION',       strategy: 'NO_AUTO_STRATEGY',  label: 'Поиск — средняя ставка',         default: true },
  { placement: 'PLACEMENT_TOP_PROMOTION',       strategy: 'TOP_PROMOTION',     topPosition: 'TOP-4',  label: 'Поиск — вывод в топ-4' },
  { placement: 'PLACEMENT_TOP_PROMOTION',       strategy: 'TOP_PROMOTION',     topPosition: 'TOP-12', label: 'Поиск — вывод в топ-12' },
  { placement: 'PLACEMENT_TOP_PROMOTION',       strategy: 'TOP_PROMOTION',     topPosition: 'TOP-20', label: 'Поиск — вывод в топ-20' },
  { placement: 'PLACEMENT_TOP_PROMOTION',       strategy: 'TOP_PROMOTION',     topPosition: 'TOP-30', label: 'Поиск — вывод в топ-30' },
  { placement: 'PLACEMENT_TOP_PROMOTION',       strategy: 'TOP_MAX_CLICKS',    label: 'Поиск — автостратегия' },
  { placement: 'PLACEMENT_SEARCH_AND_CATEGORY', strategy: 'NO_AUTO_STRATEGY',  label: 'ПиР — средняя ставка',           default: true },
  { placement: 'PLACEMENT_SEARCH_AND_CATEGORY', strategy: 'TARGET_CIR',        label: 'ПиР — целевой расход' },
  { placement: 'PLACEMENT_SEARCH_AND_CATEGORY', strategy: 'MAX_CLICKS',        label: 'ПиР — автостратегия' },
];

// Описания совпадают с тем, что делает бэкенд (_decide_generator_actions).
// Общее для всех: РК, где есть товары вне плана, не останавливается — из неё
// снимаются только товары плана. Существующие РК переименовываются только по ручной правке имени.
const STRATEGY_DESCRIPTIONS = [
  { id: 5, title: 'Добавление в подходящую', safe: true,
    desc: 'Ничего не останавливаем и не убираем. Недостающие товары добавляем в подходящую РК того же типа; если подходящей нет — создаём новую только для товаров, которые ещё нигде не рекламируются этим типом.' },
  { id: 2, title: 'Только создание новых', safe: true,
    desc: 'Старые РК не трогаем. Новая РК создаётся только для товаров, которых нет ни в одной работающей РК этого типа — без дублей.' },
  { id: 3, title: 'Точное совпадение',
    desc: 'Если работающая РК совпадает с группой по товарам и типу — оставляем её. Иначе создаём новую, а из других РК этого же типа снимаем товары плана (РК без чужих товаров останавливается).' },
  { id: 4, title: 'Частичная коррекция',
    desc: 'Берём ближайшую РК этого типа и докручиваем: добавляем недостающие товары, убираем те, что план разложит по своим РК. Остальные РК этого типа отдают товары плана.' },
  { id: 1, title: 'Полная перестройка',
    desc: 'Создаём новые РК по шаблону; товары плана снимаются со всех текущих РК этого плейсмента, включая РК других стратегий. РК без чужих товаров останавливаются.' },
];

const PLACEMENT_SHORT_LABEL = { PLACEMENT_TOP_PROMOTION: 'Поиск', PLACEMENT_SEARCH_AND_CATEGORY: 'ПиР' };

function fMoneyGen(v) {
  if (v == null || v === '') return '—';
  return `${Number(v).toLocaleString('ru-RU')} ₽`;
}

// Ключ строки плана — row_id с бэка (в нём есть позиция в топе: «топ-4» и
// «топ-12» одной группы — разные строки). Запасной вариант — для старого ответа.
function planRowKey(group, tc) {
  return tc.row_id || `${group.group_id}__${tc.placement}__${tc.strategy}__${tc.top_position || '-'}`;
}

function MatchBadge({ match }) {
  if (!match || match.type === 'none') return <span className="match-badge match-none">Новая</span>;
  if (match.type === 'exact')   return <span className="match-badge match-exact">Точное совпадение</span>;
  const best = match.campaigns?.[0];
  const total = (best?.matching_skus?.length || 0) + (best?.missing_skus?.length || 0);
  return <span className="match-badge match-partial" title="Сколько товаров группы уже есть в лучшей подходящей РК">Частичное ({best?.matching_skus?.length} из {total})</span>;
}

function PlanCampNameCell({ value, edited, onChange, onReset, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  function commit() { setEditing(false); if (draft.trim()) onChange(draft.trim()); else setDraft(value); }
  if (editing) return (
    <input
      className="plan-name-edit-input"
      value={draft}
      autoFocus
      maxLength={200}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
      onClick={e => e.stopPropagation()}
    />
  );
  return (
    <span className="plan-camp-name-wrap">
      <span
        className="plan-camp-name-editable"
        title={disabled ? '' : 'Нажмите, чтобы переименовать'}
        onClick={e => { if (disabled) return; e.stopPropagation(); setDraft(value); setEditing(true); }}
      >
        {value} {!disabled && <span className="plan-name-edit-icon">✎</span>}
      </span>
      {edited && (
        <span className="plan-edited-badge" title="Название изменено вручную">
          изменено
          {!disabled && <button type="button" className="btn-link" onClick={e => { e.stopPropagation(); onReset(); }} title="Вернуть название по шаблону">↺</button>}
        </span>
      )}
    </span>
  );
}

function bidChipText(s) {
  const parts = Object.entries(s.calc_bid_by_placement || {})
    .filter(([, v]) => v != null)
    .map(([pl, v]) => `${PLACEMENT_SHORT_LABEL[pl] || pl} ${v}₽`);
  return parts.join(' · ');
}

function PlanGroupRow({ group, defaultOpen, nameOverrides, onNameChange, onNameReset, removedKeys, excludedKeys, onRemove, onToggleApprove, locked }) {
  const [open, setOpen] = useState(defaultOpen || false);
  const visibleCampaigns = group.target_campaigns.filter(tc => !removedKeys.has(planRowKey(group, tc)));
  if (!visibleCampaigns.length) return null;
  const approvedCount = visibleCampaigns.filter(tc => !excludedKeys.has(planRowKey(group, tc))).length;
  return (
    <div className="plan-group">
      <div className="plan-group-header" onClick={() => setOpen(v => !v)}>
        <button className="expand-btn">{open ? '▾' : '▸'}</button>
        <span className="plan-group-name">{group.group_name}</span>
        <span className="plan-group-meta">{group.skus.length} тов. · {approvedCount}/{visibleCampaigns.length} РК</span>
      </div>
      {open && (
        <div className="plan-group-body">
          <div className="plan-skus">
            {group.skus.map(s => (
              <span
                key={s.sku_id}
                className="plan-sku-chip"
                title={`${s.name || ''}${bidChipText(s) ? ` · ставка: ${bidChipText(s)}` : ' · минимальная ставка будет получена из Ozon при применении'}`}
              >
                {s.offer_id || s.sku_id}
                {bidChipText(s) && <span className="sku-chip-bid"> {bidChipText(s)}</span>}
              </span>
            ))}
          </div>
          <table className="plan-camps-table">
            <thead>
              <tr>
                <th title="Снимите галочку, чтобы не применять строку. Её РК при этом не будут затронуты">Вкл.</th>
                <th>Название РК</th>
                <th>Тип</th>
                <th title="Минимум Ozon: 2 000 ₽ в неделю на каждый товар РК">Мин. бюджет Ozon, ₽/нед</th>
                <th>Совпадение</th>
                <th>Подходящая существующая РК</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleCampaigns.map(tc => {
                const key = planRowKey(group, tc);
                const edited = Object.prototype.hasOwnProperty.call(nameOverrides, key);
                const displayName = edited ? nameOverrides[key] : tc.name;
                const isExcluded = excludedKeys.has(key);
                // best = first same-strategy match (strategy_mismatch=false); fall back to first cross
                const best = tc.match?.campaigns?.find(m => !m.strategy_mismatch) || tc.match?.campaigns?.[0];
                const hasCrossOnly = best?.strategy_mismatch;
                const isClaimed = best?.claimed_by_other;
                const stateLabel = best && best.state !== 'CAMPAIGN_STATE_RUNNING' ? STATE_LABELS[best.state]?.label : null;
                return (
                  <tr key={key} className={isExcluded ? 'plan-camp-row-excluded' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!isExcluded}
                        disabled={locked}
                        title={isExcluded ? 'Исключено: строка не применяется, её РК не трогаем — включить обратно' : 'Снимите галочку, чтобы не применять строку и не трогать её РК'}
                        onChange={() => onToggleApprove(key)}
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                    <td className="plan-camp-name">
                      <PlanCampNameCell
                        value={displayName}
                        edited={edited}
                        disabled={locked}
                        onChange={name => onNameChange(key, name, tc.name)}
                        onReset={() => onNameReset(key)}
                      />
                    </td>
                    <td><span className={`placement-badge ${tc.placement === 'PLACEMENT_TOP_PROMOTION' ? 'badge-search' : 'badge-rec'}`}>{tc.label}</span></td>
                    <td>{fMoneyGen(tc.budget_min)}</td>
                    <td><MatchBadge match={tc.match} /></td>
                    <td>
                      {best ? (
                        <span className={`best-camp-info ${hasCrossOnly ? 'best-camp-mismatch' : ''} ${isClaimed ? 'best-camp-claimed' : ''}`}>
                          <b>{best.name}</b>
                          {stateLabel && <span className="best-camp-warn-badge" title="РК сейчас не крутится (например, исчерпан недельный бюджет)">{stateLabel}</span>}
                          {hasCrossOnly && <span className="best-camp-warn-badge" title="У этой РК другая стратегия — как совпадение она не используется. Затронет её только «Полная перестройка» (снимет товары плана)">⚠ другая стратегия: {best.existing_strategy_label}</span>}
                          {isClaimed && <span className="best-camp-warn-badge best-camp-warn-shared" title="Эту РК забирает другая строка плана с лучшим совпадением — для этой строки она не используется">⊗ занята другой строкой</span>}
                          <span className="meta-sep"> · </span>
                          {best.orders_7d != null && <span>заказов 7д: {best.orders_7d}</span>}
                          {best.drr_7d != null && <span className="meta-sep"> · ДРР: {best.drr_7d}%</span>}
                          {best.extra_skus?.length > 0 && <span className="text-warn" title="Товары этой РК, которых нет в группе"> +{best.extra_skus.length} других тов.</span>}
                          {best.missing_skus?.length > 0 && <span className="text-ok" title="Товары группы, которых нет в этой РК"> {best.missing_skus.length} нет в РК</span>}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      {!locked && (
                        <button
                          className="btn-del-product"
                          title="Убрать строку из плана (её РК не трогаем)"
                          onClick={e => { e.stopPropagation(); onRemove(key); }}
                        >✕</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PreviewList({ title, items, total, render, tone }) {
  const count = total ?? items?.length ?? 0;
  if (!count) return null;
  const shown = (items || []).slice(0, 50);
  return (
    <details className={`apply-preview-list ${tone ? `apply-preview-list--${tone}` : ''}`} open={tone === 'warn'}>
      <summary>{title} ({count})</summary>
      {shown.map((a, i) => <div key={i} className="apply-preview-row">{render(a)}</div>)}
      {count > shown.length && <div className="gen-hint">…и ещё {count - shown.length}</div>}
    </details>
  );
}

function fmtInt(v) {
  return Number(v || 0).toLocaleString('ru-RU');
}

function fmtMsk(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

// Окно подтверждения: вариант применения + недельный бюджет, и ровно то, что
// сделает бэкенд (тот же расчёт, что при применении, — /generator/preview), плюс
// оценка запросов к Ozon и предупреждение, если очередь не уложится в сегодняшний лимит.
function ApplyConfirmModal({ accountId, request, buildApplyBody, strategy, setStrategy, weeklyBudget, setWeeklyBudget, onConfirm, onClose, loading }) {
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const budgetValid = Number(weeklyBudget) >= 2000;

  useEffect(() => {
    if (!budgetValid) { setPreview(null); return undefined; }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    const tm = setTimeout(() => {
      request(`/api/v1/ozon-ads/${accountId}/generator/preview`, { method: 'POST', body: JSON.stringify(buildApplyBody()) })
        .then(d => { if (!cancelled) setPreview(d); })
        .catch(e => { if (!cancelled) setPreviewError(e.message || String(e)); })
        .finally(() => { if (!cancelled) setPreviewLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(tm); };
  }, [accountId, request, buildApplyBody, strategy, weeklyBudget, budgetValid]);

  const s = preview?.summary;
  const a = preview?.actions;
  const est = preview?.estimate;
  const nothing = s && !s.create && !s.adjust && !s.stop && !s.detach && !s.rename;
  return (
    <div className="modal-overlay">
      <div className="modal-box modal-box--wide">
        <div className="modal-header">
          <span>Применить план РК</span>
          <button className="modal-close" onClick={onClose} disabled={loading}>✕</button>
        </div>
        <div className="modal-body apply-modal-cols">
         <div className="apply-modal-col apply-modal-col--settings">
          <div className="gen-section-title">Вариант применения</div>
          <div className="gen-strategy-cards">
            {STRATEGY_DESCRIPTIONS.map(sd => (
              <label key={sd.id} className={`gen-strategy-card ${strategy === sd.id ? 'selected' : ''}`}>
                <input type="radio" name="applyStrategy" value={sd.id} checked={strategy === sd.id} onChange={() => setStrategy(sd.id)} />
                <div className="gen-strategy-content">
                  <div className="gen-strategy-title">
                    {sd.title}
                    {sd.safe ? <span className="default-badge">ничего не останавливает</span> : <span className="exp-badge">снимает товары со старых РК</span>}
                  </div>
                  <div className="gen-strategy-desc">{sd.desc}</div>
                </div>
              </label>
            ))}
          </div>
          <div className="gen-budget-row">
            <span className="gen-section-title" style={{ marginBottom: 0 }}>Недельный бюджет на 1 артикул:</span>
            <input
              type="number"
              className="bid-input-inline"
              value={weeklyBudget}
              min={2000}
              step={100}
              onChange={e => setWeeklyBudget(e.target.value === '' ? '' : Number(e.target.value))}
            />
            <span className="gen-hint">₽ в неделю (у Ozon бюджет РК только недельный; минимум 2 000 ₽ на товар). Бюджет новой РК = это значение × число товаров.</span>
          </div>
          {!budgetValid && <div className="text-warn">Бюджет должен быть не меньше 2 000 ₽ в неделю на артикул.</div>}
         </div>

         <div className="apply-modal-col apply-modal-col--preview">
          <div className="apply-preview">
            <div className="gen-section-title">Что будет сделано</div>
            {previewLoading && <div className="tab-loading">Считаю…</div>}
            {previewError && <div className="result-error">Не удалось посчитать: {previewError}</div>}
            {s && !previewLoading && (
              <>
                <div className="apply-result-stats">
                  <span className="result-ok">Создать РК: {fmtInt(s.create)}{s.create ? ` · ${fMoneyGen(s.new_weekly_budget_rubles)} в неделю` : ''}</span>
                  <span className="result-ok">Дополнить/скорректировать: {fmtInt(s.adjust)}</span>
                  <span className="result-info">Оставить как есть: {fmtInt(s.keep)}</span>
                  {s.rename > 0 && <span className="result-info">Переименовать: {fmtInt(s.rename)}</span>}
                  {s.detach > 0 && <span className="result-warn">Снять товары плана из РК: {fmtInt(s.detach)}</span>}
                  {s.stop > 0 && <span className="result-warn">Остановить РК: {fmtInt(s.stop)}</span>}
                  {s.skipped > 0 && <span className="result-info">Пропустить строк: {fmtInt(s.skipped)}</span>}
                </div>
                {est && (
                  <div className={`apply-estimate ${est.continues_next_day ? 'apply-estimate--warn' : ''}`}>
                    <div>
                      Запросов к Ozon: ≈ <b>{fmtInt(est.requests)}</b> (около {est.hours} ч работы очереди).
                      Лимит Ozon — {fmtInt(est.daily_limit)} запросов в сутки на кабинет; сегодня уже израсходовано {fmtInt(est.used_today)},
                      до безопасной границы {fmtInt(est.queue_stop_at)} осталось {fmtInt(est.remaining_today)}.
                    </div>
                    {est.bottleneck && (
                      <div>
                        Узкое место — «{est.bottleneck.label}»: Ozon разрешает {fmtInt(est.bottleneck.per_hour)} запросов в час
                        {est.bottleneck.per_day ? ` и ${fmtInt(est.bottleneck.per_day)} в сутки` : ''}, нужно {fmtInt(est.bottleneck.requests)},
                        поэтому не меньше <b>{est.bottleneck.hours} ч</b>.
                      </div>
                    )}
                    {est.continues_next_day ? (
                      <div className="apply-estimate-days">
                        ⚠ Очередь не уложится в сегодняшний остаток лимита. Часть выполнится сегодня, остальное она продолжит
                        сама после {fmtMsk(est.resets_at)} (МСК) — всего около {est.days} сут. Страницу держать открытой не нужно.
                      </div>
                    ) : (
                      <div>Очередь выполнится сегодня, страницу держать открытой не нужно.</div>
                    )}
                  </div>
                )}
                {nothing && <div className="gen-hint">Изменений нет: все товары уже в подходящих РК.</div>}
                <PreviewList tone="warn" title="Будут остановлены (все их товары есть в плане)" items={a.stop} total={a.stop_total}
                  render={x => <>РК {x.ozon_campaign_id} «{x.name}» — {x.sku_count} тов.</>} />
                <PreviewList tone="warn" title="Из этих РК будут сняты товары плана (остальные товары продолжат рекламироваться)" items={a.detach} total={a.detach_total}
                  render={x => <>РК {x.ozon_campaign_id} «{x.name}» — снять {x.remove_skus.length}, останется {x.remaining}</>} />
                <PreviewList title="Будут созданы" items={a.create} total={a.create_total}
                  render={x => <>«{x.name}» — {x.skus.length} тов., {fMoneyGen(x.weekly_budget_rubles)}/нед</>} />
                <PreviewList title="Будут дополнены/скорректированы" items={a.adjust} total={a.adjust_total}
                  render={x => <>РК {x.ozon_campaign_id} «{x.name}»{x.rename_to ? ` → «${x.rename_to}»` : ''}: +{x.add_skus.length}{x.remove_skus.length ? ` / −${x.remove_skus.length}` : ''} тов.{x.weekly_budget_to ? `, бюджет до ${fMoneyGen(x.weekly_budget_to)}/нед (минимум Ozon)` : ''}</>} />
                <PreviewList title="Останутся как есть" items={a.keep} total={a.keep_total}
                  render={x => <>РК {x.ozon_campaign_id} «{x.name}»{x.rename_to ? ` → переименовать в «${x.rename_to}»` : ''}</>} />
                <PreviewList title="Пропущены" items={a.skipped} total={a.skipped_total}
                  render={x => <>«{x.name}» — {x.reason}</>} />
              </>
            )}
          </div>
         </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>Отмена</button>
          <button
            className="btn-primary"
            onClick={() => onConfirm(preview)}
            disabled={loading || previewLoading || !s || !budgetValid || nothing || !!previewError}
          >
            {loading ? 'Ставлю в очередь…' : 'Подтвердить и поставить в очередь'}
          </button>
        </div>
      </div>
    </div>
  );
}

const QUEUE_TITLES = {
  preparing: 'Готовим очередь…',
  queued: 'В очереди',
  running: 'Выполняется',
  paused: 'На паузе',
  done: 'Готово',
  failed: 'Не выполнено',
  cancelled: 'Отменено',
};

// Очередь применения: идёт на сервере, страницу держать открытой не нужно.
function QueuePanel({ job, onCancel, onRetry, busy }) {
  const pr = job.progress || { total: 0, finished: 0 };
  const pct = pr.total ? Math.min(100, Math.round((pr.finished / pr.total) * 100)) : 0;
  const active = ['preparing', 'queued', 'running', 'paused'].includes(job.status);
  const result = job.result;
  const summary = result?.summary;
  const errCount = (pr.errors || 0) + (pr.blocked || 0);
  const failedMessage = job.status === 'failed' ? result?.error : null;
  return (
    <div className="apply-result queue-panel">
      <div className="apply-result-title">
        {QUEUE_TITLES[job.status] || job.status}
        {active && job.status !== 'preparing' && pr.total > 0 && <span className="gen-hint"> — {fmtInt(pr.finished)} из {fmtInt(pr.total)} действий</span>}
      </div>
      {job.status === 'preparing' && <div className="gen-hint">Считаем план и раскладываем действия по очереди — это может занять до минуты на большом плане.</div>}
      {active && pr.total > 0 && (
        <div className="queue-progress" title={`${pct}%`}>
          <div className="queue-progress-bar" style={{ width: `${pct}%` }} />
        </div>
      )}
      {job.notice && <div className="apply-estimate apply-estimate--warn">{job.notice}</div>}
      {active && job.status !== 'preparing' && job.eta && job.eta.remaining_requests > 0 && !job.notice && (
        <div className="gen-hint">Осталось около {fmtInt(job.eta.remaining_requests)} запросов к Ozon (≈ {job.eta.hours} ч). Страницу держать открытой не нужно.</div>
      )}
      {job.quota && (
        <div className="gen-hint">
          Запросы к Ozon за сутки по кабинету: {fmtInt(job.quota.used_today)} из {fmtInt(job.quota.daily_limit)} (очередь останавливается на {fmtInt(job.quota.queue_stop_at)}, остальное — синхронизациям и биддеру).
        </div>
      )}
      {failedMessage && <div className="apply-error-row">{failedMessage}</div>}
      {summary && (
        <div className="apply-result-stats">
          {summary.created    > 0 && <span className="result-ok">Создано: {fmtInt(summary.created)}{summary.new_weekly_budget_rubles ? ` · ${fMoneyGen(summary.new_weekly_budget_rubles)}/нед` : ''}</span>}
          {summary.adjusted   > 0 && <span className="result-ok">Скорректировано: {fmtInt(summary.adjusted)}</span>}
          {summary.detached   > 0 && <span className="result-warn">Сняты товары из РК: {fmtInt(summary.detached)}</span>}
          {summary.archived   > 0 && <span className="result-warn">Остановлено: {fmtInt(summary.archived)}</span>}
          {summary.kept       > 0 && <span className="result-info">Оставлено: {fmtInt(summary.kept)}</span>}
          {summary.skipped    > 0 && <span className="result-info">Пропущено: {fmtInt(summary.skipped)}</span>}
          {summary.errors     > 0 && <span className="result-error">Ошибок: {fmtInt(summary.errors)}</span>}
          {summary.bid_issues > 0 && <span className="result-error">Замечаний по ставкам: {fmtInt(summary.bid_issues)}</span>}
        </div>
      )}
      {!summary && !active && pr.total > 0 && (
        <div className="apply-result-stats">
          <span className="result-ok">Выполнено: {fmtInt(pr.done)}</span>
          {errCount > 0 && <span className="result-error">Ошибок: {fmtInt(errCount)}</span>}
          {pr.skipped > 0 && <span className="result-info">Пропущено: {fmtInt(pr.skipped)}</span>}
        </div>
      )}
      {result?.results && (
        <>
          <PreviewList tone="warn" title="Остановлены" items={result.results.archived} render={x => <>РК {x.ozon_campaign_id} «{x.name}»</>} />
          <PreviewList tone="warn" title="Сняты товары плана" items={result.results.detached}
            render={x => <>РК {x.ozon_campaign_id} «{x.name}» — снято {x.removed_skus}, осталось {x.remaining}</>} />
          <PreviewList title="Созданы" items={result.results.created} total={summary?.created}
            render={x => <>РК {x.ozon_campaign_id} «{x.name}» — {x.sku_count} тов.{x.reused ? ' (докручена ранее созданная)' : ''}</>} />
          <PreviewList title="Скорректированы" items={result.results.adjusted} total={summary?.adjusted}
            render={x => <>РК {x.ozon_campaign_id} «{x.name}»: +{x.added_skus} / −{x.removed_skus} тов.</>} />
          {result.results.errors?.length > 0 && (
            <details className="apply-errors" open>
              <summary>Ошибки ({fmtInt(summary?.errors ?? result.results.errors.length)}{summary?.errors > result.results.errors.length ? `, показаны первые ${result.results.errors.length}` : ''})</summary>
              {result.results.errors.map((e, i) => (
                <div key={i} className="apply-error-row">
                  {{ create: 'Создание', adjust: 'Корректировка', stop: 'Остановка', detach: 'Снятие товаров', rename: 'Переименование', blocked: 'Отложено' }[e.action] || e.action}
                  {': '}{e.name || `РК ${e.ozon_campaign_id}`}
                  {e.campaign_created ? ` (РК ${e.ozon_campaign_id} в Ozon создана — повтор её докрутит)` : ''}
                  {' — '}{humanizeOzonError(e.error)}
                </div>
              ))}
            </details>
          )}
          {result.results.bid_issues?.length > 0 && (
            <details className="apply-errors">
              <summary>Замечания по ставкам ({result.results.bid_issues.length})</summary>
              {result.results.bid_issues.map((b, i) => (
                <div key={i} className="apply-error-row">
                  РК {b.ozon_campaign_id}{b.name ? ` «${b.name}»` : ''}
                  {b.skus_without_bid ? `: ${b.skus_without_bid.length} SKU без ставки (${b.skus_without_bid.slice(0, 5).join(', ')}${b.skus_without_bid.length > 5 ? '…' : ''})` : ''}
                  {b.reason ? ` — ${b.reason}` : ''}
                </div>
              ))}
            </details>
          )}
        </>
      )}
      <div className="queue-actions">
        {active && (
          <button className="btn-secondary btn-sm" onClick={onCancel} disabled={busy}
            title="Невыполненные действия отменятся; то, что уже сделано в Ozon, останется">Отменить очередь</button>
        )}
        {!active && errCount > 0 && (
          <button className="btn-secondary btn-sm" onClick={onRetry} disabled={busy}>
            Повторить ошибки ({fmtInt(errCount)}) →
          </button>
        )}
      </div>
    </div>
  );
}

const BID_STRATEGY_OPTIONS = [
  { value: 'min',           label: 'Минимальные',         hint: 'Минимальная ставка Ozon для плейсмента РК (Поиск и ПиР — разные)' },
  { value: 'min_plus',      label: 'Мин. + y ₽',          hint: 'Минимальная ставка плюс фиксированная надбавка' },
  { value: 'competitive',   label: 'Конкурентные',        hint: 'Конкурентная ставка Ozon — запрашивается после добавления товара в РК; если Ozon её не дал, остаётся минимальная' },
  { value: 'competitive_x', label: 'Конкурентные × x',    hint: 'Конкурентная ставка, умноженная на коэффициент (не ниже минимальной)' },
];

function AnalyzerGenerator({ accountId, request, initialSkus, onClearSkus }) {
  // Product source — which products to send into the generator, before grouping
  const [productSource, setProductSource] = useState('all'); // all | offer_ids | brand | category
  const [offerIdsText, setOfferIdsText] = useState('');
  const [productFilters, setProductFilters] = useState(null); // { brands: [...], categories: [...] }
  const [selectedBrands, setSelectedBrands] = useState([]);
  // category selection is per (category_id, type_id) — Ozon "категория" сама по
  // себе часто широкая, type_id — это фактический товарный тип (3-й уровень).
  const [selectedCategoryKeys, setSelectedCategoryKeys] = useState([]); // "categoryId:typeId|-"
  const categoryKey = (c) => `${c.category_id}:${c.type_id ?? '-'}`;
  const selectedCategoryIds = useMemo(
    () => Array.from(new Set(selectedCategoryKeys.map(k => Number(k.split(':')[0])))),
    [selectedCategoryKeys]
  );
  const selectedTypeIds = useMemo(
    () => selectedCategoryKeys.map(k => k.split(':')[1]).filter(v => v !== '-').map(Number),
    [selectedCategoryKeys]
  );
  const [articlePattern, setArticlePattern] = useState('');

  useEffect(() => {
    if (initialSkus?.length) return; // sku filter from Analyzer takes precedence
    let cancelled = false;
    request(`/api/v1/ozon-ads/${accountId}/generator/product-filters`)
      .then(result => { if (!cancelled) setProductFilters(result); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [accountId, request, initialSkus]);

  function parseOfferIds(text) {
    return Array.from(new Set(
      text.split(/[\r\n,;\t]+/).map(s => s.trim()).filter(Boolean)
    ));
  }

  function handleOfferIdsFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const isSpreadsheet = /\.(xlsx|xls)$/i.test(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      let text = '';
      if (isSpreadsheet) {
        try {
          const wb = XLSX.read(reader.result, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
          text = rows
            .map(row => (row && row.length ? row[0] : ''))
            .filter(v => v !== undefined && v !== null && String(v).trim())
            .join('\n');
        } catch {
          notify('error', 'Не удалось прочитать файл. Убедитесь, что это корректный XLSX/CSV.');
          e.target.value = '';
          return;
        }
      } else {
        text = String(reader.result || '');
      }
      setOfferIdsText(prev => (prev ? prev + '\n' : '') + text);
    };
    if (isSpreadsheet) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
    e.target.value = '';
  }

  // Подбор артикулов через поиск — в дополнение к вставке готового списка offer_id.
  // Источник правды один — offerIdsText, поиск просто дописывает/убирает артикулы.
  const [offerSearch, setOfferSearch] = useState('');
  const [offerSearchResults, setOfferSearchResults] = useState([]);
  const [offerSearchLoading, setOfferSearchLoading] = useState(false);

  const selectedOfferIds = useMemo(() => new Set(parseOfferIds(offerIdsText)), [offerIdsText]);

  const runOfferSearch = useCallback((q) => {
    setOfferSearchLoading(true);
    const qs = q && q.trim() ? `?search=${encodeURIComponent(q.trim())}` : '';
    request(`/api/v1/ozon-ads/${accountId}/generator/products${qs}`)
      .then(d => setOfferSearchResults(d.products || []))
      .catch(() => setOfferSearchResults([]))
      .finally(() => setOfferSearchLoading(false));
  }, [accountId, request]);

  useEffect(() => {
    if (productSource !== 'offer_ids') return;
    const t = setTimeout(() => runOfferSearch(offerSearch), 300);
    return () => clearTimeout(t);
  }, [productSource, offerSearch, runOfferSearch]);

  function toggleOfferId(offerId) {
    if (!offerId) return;
    const current = parseOfferIds(offerIdsText);
    const next = current.includes(offerId)
      ? current.filter(x => x !== offerId)
      : [...current, offerId];
    setOfferIdsText(next.join('\n'));
  }

  const [grouping, setGrouping] = useState('per_sku');
  const [splitSize, setSplitSize] = useState(50);
  const [selectedTypes, setSelectedTypes] = useState(
    Object.fromEntries(GENERATOR_CAMPAIGN_TYPES.map((t, i) => [i, !!t.default]))
  );
  const [bidStrategyType, setBidStrategyType] = useState('min');
  const [bidAddValue, setBidAddValue] = useState(5);
  const [bidMultiplier, setBidMultiplier] = useState(1.1);
  const [nameTemplate, setNameTemplate] = useState('{model} - {placement} - {strategy}');
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [planParamsKey, setPlanParamsKey] = useState(null); // настройки, с которыми план построен
  const [planMoreLoading, setPlanMoreLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);   // идёт отправка в очередь
  const [applyError, setApplyError] = useState(null);
  const [queueJob, setQueueJob] = useState(null);           // статус очереди применения (с сервера)
  const [queueBusy, setQueueBusy] = useState(false);
  const [planApplied, setPlanApplied] = useState(false);    // применённый план повторно не отправляется
  const [confirmOpen, setConfirmOpen] = useState(false);
  const queuePollRef = useRef(null);
  // По умолчанию — вариант, который ничего не останавливает
  const [applyStrategy, setApplyStrategy] = useState(5);
  const [weeklyBudget, setWeeklyBudget] = useState(2000);
  const [planNameOverrides, setPlanNameOverrides] = useState({}); // row_id → имя, поправленное вручную
  const [removedPlanKeys, setRemovedPlanKeys] = useState(new Set());   // row_id → убрана из плана
  const [excludedPlanKeys, setExcludedPlanKeys] = useState(new Set()); // row_id → снята галочка "Вкл." (видна, но не применяется)

  // Price range state
  const [priceRanges, setPriceRanges] = useState([
    { min_price: 0,    max_price: 500,  label: 'до 500 ₽' },
    { min_price: 500,  max_price: 2000, label: '500–2 000 ₽' },
    { min_price: 2000, max_price: null, label: 'от 2 000 ₽' },
  ]);

  const activeCampaignTypes = GENERATOR_CAMPAIGN_TYPES
    .filter((_, i) => selectedTypes[i])
    .map(t => ({ placement: t.placement, strategy: t.strategy, top_position: t.topPosition || null }));

  const bidStrategy = useMemo(() => ({
    type: bidStrategyType,
    add_value:  bidStrategyType === 'min_plus'      ? Number(bidAddValue)   : 0,
    multiplier: bidStrategyType === 'competitive_x' ? Number(bidMultiplier) : 1,
  }), [bidStrategyType, bidAddValue, bidMultiplier]);

  // Тело запроса плана; оно же — снимок настроек. Если настройки поменяли после
  // формирования плана, план устарел: применять его нельзя, пока не пересоберут.
  function buildPlanBody() {
    const offerIds = parseOfferIds(offerIdsText);
    return {
      grouping,
      split_size: splitSize,
      campaign_types: activeCampaignTypes,
      bid_strategy: bidStrategy,
      name_template: nameTemplate,
      ...(grouping === 'per_price_range' ? { price_ranges: priceRanges } : {}),
      ...(initialSkus?.length ? { filter_skus: initialSkus } : {
        product_source: productSource,
        ...(productSource === 'offer_ids' ? { filter_offer_ids: offerIds } : {}),
        ...(productSource === 'brand'     ? { filter_brands: selectedBrands } : {}),
        ...(productSource === 'category'  ? { filter_category_ids: selectedCategoryIds, filter_type_ids: selectedTypeIds } : {}),
        ...(productSource === 'article_pattern' ? { filter_article_pattern: articlePattern.trim() } : {}),
      }),
    };
  }
  const currentParamsKey = JSON.stringify(buildPlanBody());
  const PLAN_PAGE_SIZE = 200;
  const planStale = !!plan && planParamsKey !== null && planParamsKey !== currentParamsKey;
  const hasEdits = Object.keys(planNameOverrides).length > 0 || removedPlanKeys.size > 0 || excludedPlanKeys.size > 0;

  async function handleGeneratePlan() {
    if (!activeCampaignTypes.length) { setPlanError('Выберите хотя бы один тип РК'); return; }
    const offerIds = parseOfferIds(offerIdsText);
    if (!initialSkus?.length) {
      if (productSource === 'offer_ids' && !offerIds.length) { setPlanError('Вставьте или загрузите список артикулов (offer_id)'); return; }
      if (productSource === 'brand' && !selectedBrands.length) { setPlanError('Выберите хотя бы один бренд'); return; }
      if (productSource === 'category' && !selectedCategoryIds.length) { setPlanError('Выберите хотя бы одну категорию'); return; }
      if (productSource === 'article_pattern' && !articlePattern.trim()) { setPlanError('Введите часть артикула для поиска'); return; }
    }
    if (splitSize < 1 || splitSize > 500) { setPlanError('Лимит артикулов в 1 РК — от 1 до 500'); return; }
    if (plan && hasEdits && !planApplied && !window.confirm('Пересобрать план? Ручные правки (названия, снятые и удалённые строки) будут потеряны.')) return;
    const body = buildPlanBody();
    setPlanLoading(true);
    setPlanError(null);
    setPlan(null);
    setApplyError(null);
    setPlanApplied(false);
    setPlanNameOverrides({});
    setRemovedPlanKeys(new Set());
    setExcludedPlanKeys(new Set());
    try {
      const result = await request(`/api/v1/ozon-ads/${accountId}/generator/plan?group_offset=0&group_limit=${PLAN_PAGE_SIZE}`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setPlan(result);
      setPlanParamsKey(JSON.stringify(body));
    } catch (e) {
      setPlanError(e.message || String(e));
    } finally {
      setPlanLoading(false);
    }
  }

  // Подгрузить следующую страницу групп (план большой — показывается постранично)
  async function loadMoreGroups() {
    if (!plan || planMoreLoading || !planParamsKey) return;
    setPlanMoreLoading(true);
    try {
      const more = await request(
        `/api/v1/ozon-ads/${accountId}/generator/plan?group_offset=${plan.groups.length}&group_limit=${PLAN_PAGE_SIZE}`,
        { method: 'POST', body: planParamsKey });
      setPlan(prev => (prev ? { ...prev, groups: [...prev.groups, ...(more.groups || [])], page: more.page } : prev));
    } catch (e) {
      setPlanError(e.message || String(e));
    } finally {
      setPlanMoreLoading(false);
    }
  }

  // Строки, снятые галочкой или убранные «✕»: сервер не применяет их и не трогает их РК
  const excludedIds = useMemo(() => [...removedPlanKeys, ...excludedPlanKeys], [removedPlanKeys, excludedPlanKeys]);
  const rowsToApply = Math.max(0, (plan?.summary?.total_rows ?? 0) - excludedIds.length);

  // План не пересылается целиком: на сервере он пересобирается по тем же параметрам
  // (planParamsKey — настройки, с которыми план построен) плюс правки с экрана.
  const buildApplyBody = useCallback(() => ({
    strategy: applyStrategy,
    plan: planParamsKey ? JSON.parse(planParamsKey) : {},
    renamed: planNameOverrides,
    excluded_row_ids: excludedIds,
    weekly_budget_per_sku_rubles: Number(weeklyBudget),
  }), [applyStrategy, planParamsKey, planNameOverrides, excludedIds, weeklyBudget]);

  // Очередь идёт на сервере; экран только опрашивает её статус (и после перезагрузки страницы подхватывает)
  const pollQueue = useCallback(async (jobId, failures = 0) => {
    try {
      const st = await request(`/api/v1/ozon-ads/${accountId}/generator/apply/status/${jobId}`);
      setQueueJob(st);
      if (['done', 'failed', 'cancelled'].includes(st.status)) return;
      const delay = st.status === 'paused' ? 20000 : 4000;
      queuePollRef.current = setTimeout(() => pollQueue(jobId), delay);
    } catch (e) {
      // сетевой сбой опроса — очередь на сервере идёт дальше, пробуем ещё
      if (failures < 10) {
        queuePollRef.current = setTimeout(() => pollQueue(jobId, failures + 1), 8000);
        return;
      }
      setApplyError(`Не удалось получить статус очереди: ${e.message || e}. Она продолжает работать на сервере — обновите страницу.`);
    }
  }, [accountId, request]);

  useEffect(() => () => { if (queuePollRef.current) clearTimeout(queuePollRef.current); }, []);

  // Подхватить очередь, запущенную раньше (перезагрузка страницы, другая вкладка)
  useEffect(() => {
    let cancelled = false;
    request(`/api/v1/ozon-ads/${accountId}/generator/apply/active`)
      .then(d => {
        if (cancelled || !d?.job) return;
        setQueueJob(d.job);
        if (!['done', 'failed', 'cancelled'].includes(d.job.status)) pollQueue(d.job.job_id);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [accountId, request, pollQueue]);

  async function handleConfirmApply() {
    setApplyLoading(true);
    setApplyError(null);
    try {
      const res = await request(`/api/v1/ozon-ads/${accountId}/generator/apply`, {
        method: 'POST',
        body: JSON.stringify(buildApplyBody()),
      });
      setPlanApplied(true);
      setConfirmOpen(false);
      setQueueJob({ job_id: res.job_id, status: 'preparing', progress: { total: 0, finished: 0 } });
      if (queuePollRef.current) clearTimeout(queuePollRef.current);
      pollQueue(res.job_id);
    } catch (e) {
      setApplyError(e.message || String(e));
      setConfirmOpen(false);
    } finally {
      setApplyLoading(false);
    }
  }

  async function queueAction(action) {
    if (!queueJob?.job_id) return;
    setQueueBusy(true);
    try {
      await request(`/api/v1/ozon-ads/${accountId}/generator/apply/${queueJob.job_id}/${action}`, { method: 'POST' });
      if (queuePollRef.current) clearTimeout(queuePollRef.current);
      await pollQueue(queueJob.job_id);
    } catch (e) {
      setApplyError(e.message || String(e));
    } finally {
      setQueueBusy(false);
    }
  }

  const templatePreview = ['{model}', 'Смарт-часы', '{prefix}', 'AB', '{article}', 'AB-2024-01', '{sku}', '123456789',
    '{placement}', 'Поиск', '{strategy}', 'ср.ставка', '{brand}', 'Xiaomi', '{category}', 'Электроника', '{name}', 'Смарт-часы X1 Pro']
    .reduce((acc, v, i, arr) => (i % 2 ? acc : acc.split(v).join(arr[i + 1])), nameTemplate);

  return (
    <div className="analyzer-generator">
      <div className="generator-config-panel">
        {!initialSkus?.length && (
          <div className="gen-section">
            <div className="gen-section-title">Товары для генерации</div>
            <div className="gen-options">
              {[
                { value: 'all',        label: 'Весь ассортимент' },
                { value: 'offer_ids',  label: 'По списку артикулов' },
                { value: 'brand',      label: 'По бренду' },
                { value: 'category',   label: 'По категории' },
                { value: 'article_pattern', label: 'По части артикула' },
              ].map(o => (
                <label key={o.value} className={`gen-option ${productSource === o.value ? 'selected' : ''}`}>
                  <input type="radio" name="product_source" value={o.value} checked={productSource === o.value} onChange={() => setProductSource(o.value)} />
                  {o.label}
                </label>
              ))}
            </div>

            {productSource === 'offer_ids' && (
              <div className="gen-offer-ids-block">
                <textarea
                  className="gen-offer-ids-textarea"
                  placeholder="Вставьте offer_id — каждый с новой строки или через запятую"
                  value={offerIdsText}
                  onChange={e => setOfferIdsText(e.target.value)}
                  rows={4}
                />
                <div className="gen-offer-ids-row">
                  <label className="btn-secondary btn-sm gen-offer-ids-upload">
                    Загрузить файл (.txt/.csv/.xlsx)
                    <input type="file" accept=".txt,.csv,.xlsx,.xls" onChange={handleOfferIdsFile} style={{ display: 'none' }} />
                  </label>
                  <span className="gen-hint">
                    {parseOfferIds(offerIdsText).length > 0 ? `${parseOfferIds(offerIdsText).length} артикулов` : 'ничего не введено'}
                  </span>
                  {parseOfferIds(offerIdsText).length > 0 && (
                    <button type="button" className="btn-secondary btn-sm" onClick={() => setOfferIdsText('')}>
                      Очистить
                    </button>
                  )}
                </div>

                <div className="gen-offer-search">
                  <input
                    className="modal-search"
                    placeholder="Поиск товара по названию, артикулу, SKU, модели…"
                    value={offerSearch}
                    onChange={e => setOfferSearch(e.target.value)}
                  />
                  <div className="product-picker-list">
                    {offerSearchLoading ? (
                      <div className="tab-loading">Загрузка…</div>
                    ) : offerSearchResults.length === 0 ? (
                      <div className="tab-empty">Нет товаров</div>
                    ) : offerSearchResults.map(p => (
                      <div
                        key={p.sku_id || p.offer_id}
                        className={`product-picker-item ${selectedOfferIds.has(p.offer_id) ? 'selected' : ''}`}
                        onClick={() => toggleOfferId(p.offer_id)}
                      >
                        <input
                          type="checkbox"
                          readOnly
                          checked={selectedOfferIds.has(p.offer_id)}
                        />
                        {p.primary_image && <img src={p.primary_image} alt="" className="product-picker-img" />}
                        <div className="product-picker-info">
                          <div className="product-picker-name">{p.name || '—'}</div>
                          <div className="product-picker-meta">Арт. {p.offer_id} · SKU {p.sku_id}{p.model_name ? ` · ${p.model_name}` : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {productSource === 'brand' && (
              <div className="gen-checklist">
                {!productFilters && <span className="gen-hint">Загрузка списка брендов…</span>}
                {productFilters && !productFilters.brands.length && <span className="gen-hint">Бренды не найдены</span>}
                {productFilters?.brands.map(b => (
                  <label key={b.brand} className={`gen-option ${selectedBrands.includes(b.brand) ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedBrands.includes(b.brand)}
                      onChange={() => setSelectedBrands(s => s.includes(b.brand) ? s.filter(x => x !== b.brand) : [...s, b.brand])}
                    />
                    {b.brand} <span className="gen-hint">({b.count})</span>
                  </label>
                ))}
              </div>
            )}

            {productSource === 'category' && (
              <div className="gen-checklist">
                {!productFilters && <span className="gen-hint">Загрузка списка категорий…</span>}
                {productFilters && !productFilters.categories.length && <span className="gen-hint">Категории не найдены</span>}
                {productFilters?.categories.map(c => {
                  const key = categoryKey(c);
                  return (
                    <label key={key} className={`gen-option ${selectedCategoryKeys.includes(key) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedCategoryKeys.includes(key)}
                        onChange={() => setSelectedCategoryKeys(s => s.includes(key) ? s.filter(x => x !== key) : [...s, key])}
                      />
                      {c.name || `Категория ${c.category_id}`} <span className="gen-hint">({c.count})</span>
                    </label>
                  );
                })}
              </div>
            )}

            {productSource === 'article_pattern' && (
              <div className="gen-offer-ids-block">
                <input
                  type="text"
                  placeholder="Часть артикула, например AB-2024"
                  value={articlePattern}
                  onChange={e => setArticlePattern(e.target.value)}
                />
                <span className="gen-hint">Найдём все товары, у которых артикул содержит эту подстроку.</span>
              </div>
            )}
          </div>
        )}

        <div className="gen-section">
          <div className="gen-section-title">Группировка товаров</div>
          <div className="gen-options">
            {[
              { value: 'per_sku',         label: '1 РК = 1 артикул' },
              { value: 'per_model',       label: '1 РК = 1 модель' },
              { value: 'per_category',    label: '1 РК = 1 категория' },
              { value: 'per_price_range', label: '1 РК = ценовой диапазон' },
              { value: 'per_prefix',      label: '1 РК = префикс артикула', experimental: true },
            ].map(o => (
              <label key={o.value} className={`gen-option ${grouping === o.value ? 'selected' : ''}`}>
                <input type="radio" name="grouping" value={o.value} checked={grouping === o.value} onChange={() => setGrouping(o.value)} />
                {o.label}{o.experimental && <span className="exp-badge">экспериментальная</span>}
              </label>
            ))}
          </div>

          <div className="gen-split-row">
            <label>Лимит артикулов в 1 РК:</label>
            <input
              type="number"
              className="bid-input-inline"
              value={splitSize}
              min={1}
              max={500}
              onChange={e => setSplitSize(Number(e.target.value))}
            />
            <span className="gen-hint">если в группе больше товаров — она делится на несколько РК с суффиксом _1, _2, …</span>
          </div>

          {grouping === 'per_price_range' && (
            <div className="gen-price-ranges">
              {priceRanges.map((pr, i) => (
                <div key={i} className="price-range-row">
                  <input type="number" className="bid-input-inline" placeholder="от" value={pr.min_price ?? ''} onChange={e => setPriceRanges(r => r.map((x, j) => j === i ? { ...x, min_price: e.target.value === '' ? null : Number(e.target.value) } : x))} />
                  <span>—</span>
                  <input type="number" className="bid-input-inline" placeholder="до" value={pr.max_price ?? ''} onChange={e => setPriceRanges(r => r.map((x, j) => j === i ? { ...x, max_price: e.target.value === '' ? null : Number(e.target.value) } : x))} />
                  <input className="range-label-input" placeholder="Метка" value={pr.label ?? ''} onChange={e => setPriceRanges(r => r.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                  <button className="btn-del-product" onClick={() => setPriceRanges(r => r.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
              <button className="btn-secondary btn-sm" onClick={() => setPriceRanges(r => [...r, { min_price: null, max_price: null, label: '' }])}>+ Диапазон</button>
            </div>
          )}
        </div>

        <div className="gen-section">
          <div className="gen-section-title">Типы РК <span className="gen-hint">(по умолчанию: Поиск ср.ставка + ПиР ср.ставка)</span></div>
          <div className="gen-types-grid">
            {GENERATOR_CAMPAIGN_TYPES.map((t, i) => (
              <label key={i} className={`gen-type-option ${selectedTypes[i] ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={!!selectedTypes[i]}
                  onChange={() => setSelectedTypes(s => ({ ...s, [i]: !s[i] }))}
                />
                {t.label}
                {t.default && <span className="default-badge">по умолчанию</span>}
              </label>
            ))}
          </div>
        </div>

        <div className="gen-section">
          <div className="gen-section-title">Ставки для товаров</div>
          <div className="gen-options">
            {BID_STRATEGY_OPTIONS.map(o => (
              <label key={o.value} className={`gen-option ${bidStrategyType === o.value ? 'selected' : ''}`} title={o.hint}>
                <input type="radio" name="bid_strategy" value={o.value} checked={bidStrategyType === o.value} onChange={() => setBidStrategyType(o.value)} />
                {o.label}
                {o.value === 'min' && <span className="default-badge">по умолчанию</span>}
              </label>
            ))}
          </div>
          {bidStrategyType === 'min_plus' && (
            <div className="gen-split-row" style={{ marginTop: 10 }}>
              <label>Надбавка y:</label>
              <input type="number" className="bid-input-inline" value={bidAddValue} min={0} step={0.5} onChange={e => setBidAddValue(e.target.value)} />
              <span>₽</span>
            </div>
          )}
          {bidStrategyType === 'competitive_x' && (
            <div className="gen-split-row" style={{ marginTop: 10 }}>
              <label>Коэффициент x:</label>
              <input type="number" className="bid-input-inline" value={bidMultiplier} min={0.1} step={0.05} onChange={e => setBidMultiplier(e.target.value)} />
              <span className="gen-hint">например 1.1 = +10% к конкурентной ставке</span>
            </div>
          )}
        </div>

        <div className="gen-section-title">Название РК</div>
        <div className="gen-name-template-block">
          <input
            className="gen-name-template-input"
            value={nameTemplate}
            onChange={e => setNameTemplate(e.target.value)}
            placeholder="{model} - {placement} - {strategy}"
          />
          <div className="gen-name-template-hint">
            Переменные: <code>{'{model}'}</code> — модель/группа, <code>{'{prefix}'}</code> — префикс артикула, <code>{'{article}'}</code> — артикул (offer_id),{' '}
            <code>{'{sku}'}</code> — SKU Ozon, <code>{'{placement}'}</code> — Поиск/ПиР, <code>{'{strategy}'}</code> — ср.ставка/топ-4/…,{' '}
            <code>{'{brand}'}</code> — бренд, <code>{'{category}'}</code> — категория, <code>{'{name}'}</code> — название товара.{' '}
            Если группа превышает лимит артикулов в 1 РК, к названию автоматически добавляется <code>_1</code>, <code>_2</code>, …
          </div>
          <div className="gen-name-template-preview">
            Пример: <b>{templatePreview}</b>
          </div>
        </div>

        {initialSkus?.length > 0 && (
          <div className="gen-filter-note">
            Фильтр: {initialSkus.length} выбранных артикулов
            {onClearSkus && (
              <button className="btn-link gen-clear-skus" onClick={onClearSkus} title="Убрать фильтр, генерировать по всему ассортименту">× сбросить</button>
            )}
          </div>
        )}

        <button
          className="btn-primary gen-build-btn"
          onClick={handleGeneratePlan}
          disabled={planLoading}
        >
          {planLoading ? 'Формирую план…' : 'Сформировать план'}
        </button>
        {planError && <div className="result-error gen-plan-error">{planError}</div>}
      </div>

      {/* Plan preview */}
      {plan && (
        <div className="generator-plan-panel">
          <div className="plan-header">
            <div className="plan-summary">
              <span className="plan-stat"><b>{fmtInt(plan.summary.total_groups)}</b> групп</span>
              <span className="plan-stat"><b>{fmtInt(plan.summary.total_sku_count)}</b> товаров</span>
              <span className="plan-stat" title="Строки плана, которые уйдут в применение"><b>{fmtInt(rowsToApply)}</b> из {fmtInt(plan.summary.total_rows ?? rowsToApply)} строк к применению</span>
              <span className="plan-stat" title="Строк без подходящей работающей РК">{fmtInt(plan.summary.total_new_campaigns)} без подходящей РК</span>
            </div>
            <div className="plan-header-actions">
              {hasEdits && !planApplied && (
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => { setPlanNameOverrides({}); setRemovedPlanKeys(new Set()); setExcludedPlanKeys(new Set()); }}
                  title="Вернуть исходные названия и все снятые/удалённые строки"
                >
                  Сбросить правки
                </button>
              )}
              {removedPlanKeys.size > 0 && !planApplied && (
                <button className="btn-secondary btn-sm" onClick={() => setRemovedPlanKeys(new Set())}>
                  Вернуть удалённые строки ({removedPlanKeys.size})
                </button>
              )}
              <button
                className="btn-primary"
                onClick={() => setConfirmOpen(true)}
                disabled={applyLoading || planApplied || planStale || rowsToApply === 0}
                title={planApplied ? 'План уже поставлен в очередь — для новых изменений сформируйте план заново'
                  : planStale ? 'Настройки изменились после формирования плана — сформируйте план заново' : ''}
              >
                {planApplied ? 'План в очереди' : 'Применить план →'}
              </button>
            </div>
          </div>

          {plan.summary.price_info && (plan.summary.price_info.missing > 0 || plan.summary.price_info.error) && (
            <div className="data-incomplete-banner">
              {plan.summary.price_info.error
                ? `Ozon не отдал цены (${plan.summary.price_info.error}) — взяты последние известные из базы. `
                : ''}
              {plan.summary.price_info.missing > 0
                ? `Цену не удалось определить у ${plan.summary.price_info.missing} тов. — они в группе «Без цены».`
                : ''}
            </div>
          )}
          {planStale && !planApplied && (
            <div className="data-incomplete-banner">
              Настройки генератора изменились после формирования плана — план устарел. Нажмите «Сформировать план», чтобы применить новые настройки.
            </div>
          )}
          {planApplied && (
            <div className="gen-filter-note">План поставлен в очередь. Чтобы внести новые изменения, сформируйте план заново — он учтёт уже созданные РК.</div>
          )}

          <div className="plan-groups-list">
            {plan.groups.map((g, i) => (
              <PlanGroupRow
                key={g.group_id}
                group={g}
                defaultOpen={i < 3}
                locked={planApplied}
                nameOverrides={planNameOverrides}
                onNameChange={(key, name, original) => setPlanNameOverrides(prev => {
                  const next = { ...prev };
                  if (name === original) delete next[key]; else next[key] = name;
                  return next;
                })}
                onNameReset={key => setPlanNameOverrides(prev => { const next = { ...prev }; delete next[key]; return next; })}
                removedKeys={removedPlanKeys}
                excludedKeys={excludedPlanKeys}
                onRemove={key => setRemovedPlanKeys(prev => new Set(prev).add(key))}
                onToggleApprove={key => setExcludedPlanKeys(prev => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key); else next.add(key);
                  return next;
                })}
              />
            ))}
          </div>
          {plan.page && plan.page.total > plan.groups.length && (
            <div className="load-more-row">
              <button className="btn-secondary btn-sm" onClick={loadMoreGroups} disabled={planMoreLoading}>
                {planMoreLoading ? 'Загрузка…' : `Показать ещё (${fmtInt(plan.page.total - plan.groups.length)} групп из ${fmtInt(plan.page.total)})`}
              </button>
            </div>
          )}
        </div>
      )}

      {confirmOpen && plan && (
        <ApplyConfirmModal
          accountId={accountId}
          request={request}
          buildApplyBody={buildApplyBody}
          strategy={applyStrategy}
          setStrategy={setApplyStrategy}
          weeklyBudget={weeklyBudget}
          setWeeklyBudget={setWeeklyBudget}
          onConfirm={handleConfirmApply}
          onClose={() => setConfirmOpen(false)}
          loading={applyLoading}
        />
      )}

      {applyError && (
        <div className="apply-result">
          <div className="apply-result-title result-error">Не удалось поставить в очередь</div>
          <div className="apply-error-row">{humanizeOzonError(applyError)}</div>
        </div>
      )}

      {queueJob && (
        <QueuePanel
          job={queueJob}
          busy={queueBusy}
          onCancel={() => queueAction('cancel')}
          onRetry={() => queueAction('retry')}
        />
      )}

    </div>
  );
}

// Текст ошибки Ozon — для продавца, а не разработчика
function humanizeOzonError(err) {
  const s = String(err || '');
  if (/429|RATE_LIMIT/i.test(s)) return 'Ozon временно ограничил частоту запросов — нажмите «Повторить ошибки», очередь добьёт остальное сама';
  if (/401|403|UNAUTHORIZED|FORBIDDEN/i.test(s)) return 'Ozon отказал в доступе — проверьте ключ Performance API кабинета';
  if (/5\d\d|timeout|timed out/i.test(s)) return 'Ozon не ответил вовремя — повторите позже';
  return s;
}

// ── Analyzer: duplicates ──────────────────────────────────────────────────────

function AnalyzerDuplicates({ accountId, request }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [actionKey, setActionKey] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState({}); // key -> checked (default: our recommendation)
  // key -> ozon_campaign_id, куда применяем действие. По умолчанию = рекомендация
  // системы ("худшая" РК), но пользователь может выбрать любую из списка.
  const [targetKeys, setTargetKeys] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const dupKey = d => `${d.bucket}-${d.sku_id}`;

  // Кампания, к которой сейчас применится действие: выбор пользователя,
  // иначе рекомендация системы, иначе первая в списке.
  function targetCampaign(d) {
    const key = dupKey(d);
    const chosenId = targetKeys[key] != null
      ? targetKeys[key]
      : d.recommendation?.target_ozon_campaign_id;
    return (d.campaigns || []).find(c => c.ozon_campaign_id === chosenId)
      || (d.campaigns || [])[0]
      || null;
  }

  // Что именно произойдёт. Если в выбранной РК этот товар единственный —
  // удалить его нельзя, поэтому действие автоматически становится
  // «приостановить всю РК» (products_in_campaign приходит с бэкенда).
  function resolveAction(d) {
    const c = targetCampaign(d);
    if (!c) return null;
    const onlyProduct = Number(c.products_in_campaign) <= 1;
    const article = d.offer_id || d.sku_id;
    const text = onlyProduct
      ? `В РК «${c.name}» (ID ${c.ozon_campaign_id}) это единственный товар — будет приостановлена вся РК`
      : `Удалить артикул ${article} (SKU ${d.sku_id}) из РК «${c.name}» (ID ${c.ozon_campaign_id})`;
    return {
      campaign: c,
      action: onlyProduct ? 'pause_campaign' : 'remove_product',
      text,
    };
  }

  // sync=false — перезагрузка сразу после применения решения. Ручки паузы и
  // удаления товара сами правят локальные таблицы, поэтому пересинхронизация не
  // нужна: sync-quick (один запрос списка кампаний к OZON, без фильтра по
  // статусу) и так троттлится по аккаунту (_LIST_ONLY_COOLDOWN_SECONDS),
  // повторный вызов сразу после apply — просто лишняя нагрузка. Живое
  // состояние тянем только при первом открытии вкладки.
  const load = useCallback((options = {}) => {
    const { sync = true } = options;
    setLoading(true);
    // Детектор дублей читает ту же локальную таблицу, что и вкладка "Кампании",
    // поэтому при открытии состояние кампаний обновляем — иначе уже заархивированная
    // на стороне OZON кампания даст ложный дубль.
    (sync
      ? request(`/api/v1/ozon-ads/${accountId}/campaigns/sync-quick`, { method: 'POST' }).catch(() => null)
      : Promise.resolve())
      .then(() => request(`/api/v1/ozon-ads/${accountId}/analyzer/duplicates`))
      .then(d => {
        setData(d);
        setLoaded(true);
        // Уже сделанный юзером выбор (чекбокс/целевая РК) переживает
        // обновление — иначе после частичного применения (например,
        // остановки на 429) юзеру пришлось бы заново перепроверять то, что
        // он уже разметил. Дефолт (рекомендация системы) — только для новых
        // записей, которых не было в прошлой выдаче.
        setSelectedKeys(prevSelected => {
          const next = {};
          (d?.duplicates || []).forEach(item => {
            const key = dupKey(item);
            next[key] = key in prevSelected ? prevSelected[key] : true;
          });
          return next;
        });
        setTargetKeys(prevTargets => {
          const targets = {};
          (d?.duplicates || []).forEach(item => {
            const key = dupKey(item);
            targets[key] = key in prevTargets
              ? prevTargets[key]
              : (item.recommendation?.target_ozon_campaign_id ?? null);
          });
          return targets;
        });
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [accountId, request]); // eslint-disable-line

  useEffect(() => { load(); }, [accountId]); // eslint-disable-line

  function toggleSelected(key) {
    setSelectedKeys(s => ({ ...s, [key]: !s[key] }));
  }

  function chooseTarget(key, ozonCampaignId) {
    setTargetKeys(t => ({ ...t, [key]: ozonCampaignId }));
  }

  // Единая точка выполнения: и одиночная кнопка, и массовый прогон
  // применяют ВЫБРАННУЮ пользователем цель, а не жёсткую рекомендацию.
  async function applyDecision(d) {
    const plan = resolveAction(d);
    if (!plan) throw new Error('Не выбрана кампания');
    const campId = plan.campaign.ozon_campaign_id;
    if (plan.action === 'pause_campaign') {
      await request(`/api/v1/ozon-ads/${accountId}/campaigns/${campId}/pause`, { method: 'POST' });
    } else {
      await request(`/api/v1/ozon-ads/${accountId}/campaigns/${campId}/products/${d.sku_id}`, { method: 'DELETE' });
    }
  }

  async function handleApplyOne(d) {
    const key = dupKey(d);
    const plan = resolveAction(d);
    if (!plan) return;
    if (!window.confirm(`${plan.text}.\n\nПродолжить?`)) return;
    setActionKey(key);
    try {
      await applyDecision(d);
      load({ sync: false });
    } catch (e) {
      notify('error', isRateLimit(e)
        ? 'OZON временно ограничивает частоту запросов. Повторите через минуту.'
        : 'Ошибка: ' + (e.message || e));
    } finally {
      setActionKey(null);
    }
  }

  async function handleBulkApply() {
    const duplicates = data?.duplicates || [];
    const selected = duplicates.filter(d => selectedKeys[dupKey(d)]);
    if (!selected.length) { notify('info', 'Не выбрано ни одного дубля.'); return; }
    if (!window.confirm(`Применить решения для ${selected.length} из ${duplicates.length} дублей?`)) return;
    setBulkBusy(true);
    setBulkResult(null);
    let applied = 0;
    let rateLimited = false;
    const failed = [];
    for (let i = 0; i < selected.length; i++) {
      const d = selected[i];
      try {
        // OZON включает кулдаун на аккаунт («повторите через N сек.») — ждём его и
        // повторяем тот же дубль; останавливаемся, только если кулдаун не проходит.
        for (let attempt = 0; ; attempt += 1) {
          try {
            await applyDecision(d);
            break;
          } catch (err) {
            const waitSec = isRateLimit(err) ? shortRetryDelay(err) : null;
            if (waitSec == null || attempt >= 4) throw err;
            setBulkResult(`Применяем… ${applied} из ${selected.length}. OZON ограничивает запросы, ждём ${waitSec} с`);
            await new Promise(r => setTimeout(r, waitSec * 1000));
          }
        }
        applied += 1;
        setBulkResult(`Применяем… ${applied} из ${selected.length}`);
      } catch (e) {
        if (isRateLimit(e)) {
          rateLimited = true;
          break;
        }
        failed.push(`${d.product_name || d.offer_id || d.sku_id} (${e.message || e})`);
      }
      // Небольшая пауза, чтобы не упереться в лимит Performance API.
      if (i < selected.length - 1) {
        await new Promise(r => setTimeout(r, 700));
      }
    }
    setBulkBusy(false);
    setBulkResult(
      rateLimited
        ? `Применено ${applied} из ${selected.length}, остановлено из-за ограничения OZON, повторите позже`
          + (failed.length ? `. Не удалось: ${failed.join('; ')}` : '')
        : `Применено: ${applied} из ${selected.length}`
          + (failed.length ? `. Не удалось: ${failed.join('; ')}` : '')
    );
    load({ sync: false });
  }

  if (!loaded && !loading) return <div className="tab-loading">Поиск дублей…</div>;
  if (loading) return <div className="tab-loading">Поиск дублей…</div>;

  const duplicates = data?.duplicates || [];
  const selectedCount = duplicates.filter(d => selectedKeys[dupKey(d)]).length;

  return (
    <div className="analyzer-duplicates">
      <div className="dup-header">
        <div className="dup-summary">
          Дублей найдено: {duplicates.length}
          {data?.date_from && ` · период ${fDate(data.date_from)} — ${fDate(data.date_to)}`}
        </div>
        {duplicates.length > 0 && (
          <button
            className="btn-primary btn-sm"
            disabled={bulkBusy || selectedCount === 0}
            onClick={handleBulkApply}
            style={{ marginLeft: 'auto' }}
          >
            {bulkBusy ? '…' : `Применить выбранное (${selectedCount})`}
          </button>
        )}
        <button className="btn-secondary btn-sm" onClick={load} style={duplicates.length > 0 ? undefined : { marginLeft: 'auto' }}>↺ Обновить</button>
      </div>

      {bulkResult && <div className="dup-bulk-result">{bulkResult}</div>}

      {duplicates.length === 0 ? (
        <div className="tab-empty">
          Дублей не найдено — среди активных кампаний нет товаров, которые пересекались бы
          в двух и более кампаниях одного типа (Поиск / Вывод в топ / Поиск и рекомендации / ПиР с целевым расходом).
        </div>
      ) : (
        <div className="dup-list">
          {duplicates.map(d => {
            const key = `${d.bucket}-${d.sku_id}`;
            const expanded = expandedKey === key;
            const rec = d.recommendation;
            const plan = resolveAction(d);
            const chosenId = plan?.campaign?.ozon_campaign_id;
            return (
              <div key={key} className="dup-card">
                <div className="dup-card-head" onClick={() => setExpandedKey(expanded ? null : key)}>
                  <input
                    type="checkbox"
                    className="dup-select-checkbox"
                    checked={!!selectedKeys[key]}
                    onClick={e => e.stopPropagation()}
                    onChange={() => toggleSelected(key)}
                    title="Включить в массовое применение рекомендаций"
                  />
                  <span className="dup-bucket-badge">{d.bucket_label}</span>
                  <span className="dup-product-name">{d.product_name || d.offer_id || d.sku_id}</span>
                  {d.offer_id && <span className="dup-offer">арт. {d.offer_id}</span>}
                  <span className="dup-camp-count">{d.campaign_count} кампании</span>
                  {!expanded && plan && (
                    <span className="dup-head-action" title={plan.text} style={{ opacity: 0.8, fontSize: '0.85em', marginLeft: 8 }}>
                      {plan.action === 'pause_campaign'
                        ? `⏸ приостановить «${plan.campaign.name}»`
                        : `✕ убрать из «${plan.campaign.name}»`}
                    </span>
                  )}
                  <span className="dup-expand-icon">{expanded ? '▲' : '▼'}</span>
                </div>

                {expanded && (
                  <div className="dup-card-body">
                    <div className="unified-table-wrap">
                      <table className="dup-camp-table">
                        <thead>
                          <tr>
                            <th style={{ width: 34 }} title="Выберите, к какой кампании применить действие">✓</th>
                            <th>Кампания</th>
                            <th>Товаров в РК</th>
                            <th>Расход</th>
                            <th>Показы</th>
                            <th>Клики</th>
                            <th>CTR</th>
                            <th>Заказы</th>
                            <th>ДРР рекл.</th>
                            <th>ДРР общий</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.campaigns.map(c => {
                            const isRecommended = c.ozon_campaign_id === rec.target_ozon_campaign_id;
                            const isChosen = c.ozon_campaign_id === chosenId;
                            return (
                              <tr
                                key={c.ozon_campaign_id}
                                className={isChosen ? 'dup-row-worst' : ''}
                                onClick={() => chooseTarget(key, c.ozon_campaign_id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td>
                                  <input
                                    type="radio"
                                    name={`dup-target-${key}`}
                                    checked={isChosen}
                                    onChange={() => chooseTarget(key, c.ozon_campaign_id)}
                                    onClick={e => e.stopPropagation()}
                                    title="Применить действие к этой кампании"
                                  />
                                </td>
                                <td>
                                  {c.name}
                                  {isRecommended && (
                                    <span className="dup-rec-hint" style={{ marginLeft: 6, opacity: 0.75, fontSize: '0.85em' }}>
                                      рекомендуем
                                    </span>
                                  )}
                                </td>
                                <td>{c.products_in_campaign}</td>
                                <td>{fMoney(c.spent_rubles)}</td>
                                <td>{c.impressions}</td>
                                <td>{c.clicks}</td>
                                <td>{c.ctr}%</td>
                                <td>{c.article_orders}</td>
                                <td>{c.drr_ad != null ? c.drr_ad + '%' : '—'}</td>
                                <td>{c.drr_total != null ? c.drr_total + '%' : '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="dup-recommendation">
                      <span className={`dup-rec-badge ${plan?.action === 'pause_campaign' ? 'dup-rec-badge--pause' : 'dup-rec-badge--remove'}`}>
                        Что будет сделано: {plan ? plan.text : '—'}
                      </span>
                      {plan && chosenId !== rec.target_ozon_campaign_id && (
                        <span className="dup-rec-override" style={{ opacity: 0.75, fontSize: '0.85em' }}>
                          (изменено вручную; система рекомендовала «{rec.target_campaign_name}»)
                        </span>
                      )}
                      <button
                        className={plan?.action === 'pause_campaign' ? 'btn-pause' : 'btn-secondary btn-sm'}
                        disabled={actionKey === key || bulkBusy || !plan}
                        onClick={() => handleApplyOne(d)}
                        title={plan ? plan.text : ''}
                      >
                        {actionKey === key
                          ? '…'
                          : plan?.action === 'pause_campaign'
                            ? '⏸ Приостановить РК'
                            : 'Удалить товар из РК'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── PeriodSelector ─────────────────────────────────────────────────────────────

function PeriodSelector({ dateFrom, dateTo, onChange }) {
  return (
    <div className="period-selector">
      <div className="period-presets">
        {PERIOD_PRESETS.map(p => {
          const { from, to } = defaultPeriod(p.days);
          const active = from === dateFrom && to === dateTo;
          return (
            <button
              key={p.days}
              className={`period-btn ${active ? 'active' : ''}`}
              onClick={() => onChange(from, to)}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      <input
        type="date"
        className="date-input"
        value={dateFrom}
        max={dateTo}
        onChange={e => onChange(e.target.value, dateTo)}
        onClick={e => { try { e.currentTarget.showPicker?.(); } catch (_) { /* календарь не открылся — остаётся ввод с клавиатуры */ } }}
      />
      <span className="date-sep">—</span>
      <input
        type="date"
        className="date-input"
        value={dateTo}
        min={dateFrom}
        onChange={e => onChange(dateFrom, e.target.value)}
        onClick={e => { try { e.currentTarget.showPicker?.(); } catch (_) { /* календарь не открылся — остаётся ввод с клавиатуры */ } }}
      />
    </div>
  );
}

// ── SyncStatus ─────────────────────────────────────────────────────────────────

function SyncStatus({ accountId, request }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!accountId) return;
    request(`/api/v1/ozon-ads/${accountId}/sync-status`)
      .then(setStatus)
      .catch((err) => {
        // Не проглатываем ошибку молча — иначе 403/500 по правам доступа
        // (например, у employee-сотрудника кабинета) выглядит как "нет данных"
        // вместо явной ошибки авторизации, которую можно было бы диагностировать.
        console.error('SyncStatus: не удалось получить /sync-status', err);
      });
  }, [accountId, request]);

  if (!status) return null;

  return (
    <div className="sync-status">
      <span className="sync-meta">
        {status.first_sku_stat_date
          ? `стат. с ${fDate(status.first_sku_stat_date)}`
          : 'нет данных'}
        {status.last_stat_date && ` · по ${fDate(status.last_stat_date)}`}
        {status.campaign_count != null && ` · ${status.campaign_count} кампаний`}
      </span>
    </div>
  );
}

// ── Остатки лимитов Ozon ───────────────────────────────────────────────────────

const LIMIT_LABELS = {
  global: 'Запросы', bids_set: 'Ставки', products_add: 'Добавление товаров',
  campaign_create: 'Создание РК', campaign_copy: 'Копирование РК', campaign_budget: 'Бюджет РК', export: 'Выгрузки',
};

function ApiLimits({ accountId, request }) {
  const [buckets, setBuckets] = useState(null);

  useEffect(() => {
    if (!accountId) return undefined;
    let alive = true;
    const load = () => request(`/api/v1/ozon-ads/${accountId}/api-limits`)
      .then(d => { if (alive) setBuckets(d.buckets || []); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 30000);
    return () => { alive = false; clearInterval(timer); };
  }, [accountId, request]);

  if (!buckets) return null;
  // Показываем общий счётчик и те операции, по которым что-то уже потрачено или есть пауза
  const shown = buckets.filter(b => b.bucket === 'global' || b.day_used > 0 || b.hour_used > 0 || b.blocked_until);
  return (
    <div className="api-limits" style={{ fontSize: 12, marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
      <span style={{ color: 'var(--text-muted,#888)' }}>Лимиты Ozon:</span>
      {shown.map(b => {
        const hourShare = b.hour_limit ? b.hour_used / b.hour_limit : 0;
        const dayShare = b.day_limit ? b.day_used / b.day_limit : 0;
        const share = Math.max(hourShare, dayShare);
        const color = b.blocked_until || share >= 1 ? '#c0392b' : (share >= 0.8 ? '#c47f00' : 'inherit');
        const title = [
          b.hour_limit ? `В час: ${fmtInt(b.hour_used)} из ${fmtInt(b.hour_limit)}${b.effective_hour ? ` (сейчас Ozon пускает около ${fmtInt(b.effective_hour)})` : ''}, обнулится ${fmtDateTime(b.hour_resets_at)}` : null,
          b.day_limit ? `В сутки: ${fmtInt(b.day_used)} из ${fmtInt(b.day_limit)}, обнулится ${fmtDateTime(b.day_resets_at)}` : null,
          b.blocked_until ? `Пауза после ответа Ozon до ${fmtDateTime(b.blocked_until)}` : null,
        ].filter(Boolean).join(' | ');
        return (
          <span key={b.bucket} title={title} style={{ color }}>
            {LIMIT_LABELS[b.bucket] || b.label}:{' '}
            {b.hour_limit ? `${fmtInt(b.hour_used)}/${fmtInt(b.hour_limit)} в час` : ''}
            {b.hour_limit && b.day_limit ? ' · ' : ''}
            {b.day_limit ? `${fmtInt(b.day_used)}/${fmtInt(b.day_limit)} в сутки` : ''}
            {b.blocked_until ? ` · пауза до ${fmtDateTime(b.blocked_until)}` : ''}
          </span>
        );
      })}
    </div>
  );
}

// ── Root component ─────────────────────────────────────────────────────────────

export default function OzonAdsCampaigns({ accountId }) {
  const { request, showStatus } = useApi();
  useEffect(() => {
    notifySink = showStatus;
    return () => { if (notifySink === showStatus) notifySink = null; };
  }, [showStatus]);
  const { hasFeature } = usePlanFeatures();
  // Подвкладка рекламы живёт в адресе (?sub=articles): F5, ссылка и «Назад» ведут туда же.
  const { get: getUrlParam, update: updateUrlParams } = useUrlParams();
  const adsScope = useAdsScope();
  const urlTab = getUrlParam('sub');
  const tab = ADS_TAB_KEYS.includes(urlTab) ? urlTab : 'campaigns';
  const setTab = useCallback(
    (key) => updateUrlParams({ sub: key === 'campaigns' ? '' : key }, { push: true }),
    [updateUrlParams],
  );
  const p = defaultPeriod(30);
  // Ссылка из уведомления («резкий рост ДРР») задаёт период и список кампаний прямо в адресе.
  const urlFrom = getUrlParam('from');
  const urlTo = getUrlParam('to');
  const urlCamp = getUrlParam('camp');
  const urlPeriodValid = ISO_DATE.test(urlFrom) && ISO_DATE.test(urlTo) && urlFrom <= urlTo;
  const [dateFrom, setDateFrom] = useState(urlPeriodValid ? urlFrom : p.from);
  const [dateTo, setDateTo] = useState(urlPeriodValid ? urlTo : p.to);
  useEffect(() => {
    if (urlPeriodValid) {
      setDateFrom(urlFrom);
      setDateTo(urlTo);
    }
  }, [urlPeriodValid, urlFrom, urlTo]);
  const campaignIds = useMemo(() => (urlCamp ? urlCamp.split(',').map(s => s.trim()).filter(Boolean) : []), [urlCamp]);
  // refreshKey — кнопка "Обновить": передаётся вкладкам пропом, чтобы перезапросить
  // данные, сохранив фильтры и раскрытые строки.
  const [refreshKey, setRefreshKey] = useState(0);
  // remountKey — только для перехода "открыть эту кампанию": там нужен именно чистый
  // монтаж, потому что initialSearch/focusCampaignId читаются при монтировании.
  const [remountKey, setRemountKey] = useState(0);
  const [firstStatDate, setFirstStatDate] = useState(null);
  const [campaignInitSearch, setCampaignInitSearch] = useState('');
  // Товары, выбранные в «Анализаторе РК» для создания кампаний: переходят во вкладку «Генератор РК»
  const [generatorSkus, setGeneratorSkus] = useState(null);
  const [campaignFocusId, setCampaignFocusId] = useState(null);

  useEffect(() => {
    if (!accountId) return;
    request(`/api/v1/ozon-ads/${accountId}/sync-status`)
      .then(d => setFirstStatDate(d.first_sku_stat_date || null))
      .catch((err) => {
        console.error('OzonAdsCampaigns: не удалось получить /sync-status', err);
      });
  }, [accountId, request]);

  const isPartialPeriod = !!(firstStatDate && firstStatDate > dateFrom);

  function navigateToCampaigns(campaignName) {
    setCampaignInitSearch(campaignName);
    setCampaignFocusId(null);
    setTab('campaigns');
  }

  // «Создать РК» из анализа покрытия: выбранные товары уходят в «Генератор РК»
  function handleCreateForSkus(skus) {
    setGeneratorSkus(skus);
    setTab('generator');
  }

  // Переход из разбивки по кампаниям (По артикулам / По моделям): заранее
  // фильтруем список по названию и просим TabCampaigns раскрыть эту строку.
  function openCampaignInCampaignsTab(campaign) {
    if (!campaign) return;
    setCampaignInitSearch(campaign.name || '');
    setCampaignFocusId(campaign.ozon_campaign_id || null);
    setTab('campaigns');
    setRemountKey(k => k + 1); // ремоунт вкладки: initialSearch/focus читаются при монтировании
  }

  function handlePeriod(from, to) {
    setDateFrom(from);
    setDateTo(to);
  }

  return (
    <div className="ozon-ads-root">
      <div className="ads-topbar">
        <div className="ads-topbar-left">
          <h2 className="ads-title">Реклама</h2>
          <PeriodSelector dateFrom={dateFrom} dateTo={dateTo} onChange={handlePeriod} />
          {/* Обновление всего раздела: refreshKey уходит во вкладки пропом
              refreshNonce (он в зависимостях их load-эффектов), поэтому данные
              перезапрашиваются без перемонтирования — фильтры, раскрытые строки
              и галочка сравнения сохраняются. Дашборд перемонтируется по key. */}
          <button
            type="button"
            className="ads-refresh-btn"
            title="Обновить данные раздела Реклама"
            onClick={() => setRefreshKey(k => k + 1)}
          >
            ↺ Обновить
          </button>
        </div>
        <SyncStatus accountId={accountId} request={request} />
        <ApiLimits accountId={accountId} request={request} />
      </div>

      <AdsDashboard
        key={`dash-${dateFrom}-${dateTo}-${refreshKey}`}
        accountId={accountId}
        dateFrom={dateFrom}
        dateTo={dateTo}
        request={request}
      />

      <div className="ads-scope" role="group" aria-label="Какие кампании показывать">
        <span className="ads-scope__label">Кампании:</span>
        <button
          type="button"
          className={`ads-scope__btn${adsScope === 'active' ? ' active' : ''}`}
          onClick={() => setAdsScope('active')}
        >
          Активные
        </button>
        <button
          type="button"
          className={`ads-scope__btn${adsScope === 'all' ? ' active' : ''}`}
          onClick={() => setAdsScope('all')}
        >
          Все
        </button>
        {adsScope === 'active' && (
          <span className="ads-scope__hint">выключенные, завершённые и архивные скрыты</span>
        )}
      </div>

      <div className="ads-tabs">
        {[
          { key: 'campaigns', label: 'Кампании', toolKey: 'ozon_ads_campaigns' },
          { key: 'articles',  label: 'По артикулам', toolKey: 'ozon_ads_by_article' },
          { key: 'models',    label: 'По моделям', toolKey: 'ozon_ads_by_model' },
          { key: 'groups',    label: 'Свои группы', toolKey: 'ozon_ads_custom_groups' },
          { key: 'analyzer',   label: 'Анализатор РК', toolKey: 'ozon_ads_analyzer' },
          { key: 'duplicates', label: 'Дубли РК', toolKey: 'ozon_ads_analyzer' },
          { key: 'generator',  label: 'Генератор РК', toolKey: 'ozon_ads_analyzer' },
        ].filter(t => t.key === 'campaigns' || hasFeature(t.toolKey)).map(t => (
          <button
            key={t.key}
            className={`ads-tab ${tab === t.key ? 'active' : ''} ${t.key === 'analyzer' ? 'ads-tab--special' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            <ToolHelpButton toolKey={t.toolKey} />
          </button>
        ))}
      </div>

      <div className="ads-tab-body" key={`${tab}-${dateFrom}-${dateTo}-${remountKey}-${adsScope}`}>
        {tab === 'campaigns' && (
          <TabCampaigns
            accountId={accountId}
            dateFrom={dateFrom}
            dateTo={dateTo}
            request={request}
            initialSearch={campaignInitSearch}
            focusCampaignId={campaignFocusId}
            refreshNonce={refreshKey}
            campaignIds={campaignIds}
            onClearCampaignIds={() => updateUrlParams({ camp: '' })}
          />
        )}
        {tab === 'articles' && (
          <TabArticles
            accountId={accountId}
            dateFrom={dateFrom}
            dateTo={dateTo}
            request={request}
            isPartialPeriod={isPartialPeriod}
            onOpenCampaign={openCampaignInCampaignsTab}
            refreshNonce={refreshKey}
          />
        )}
        {tab === 'models' && (
          <TabModels
            accountId={accountId}
            dateFrom={dateFrom}
            dateTo={dateTo}
            request={request}
            isPartialPeriod={isPartialPeriod}
            onOpenCampaign={openCampaignInCampaignsTab}
            refreshNonce={refreshKey}
          />
        )}
        {tab === 'groups' && (
          <TabCustomGroups
            accountId={accountId}
            dateFrom={dateFrom}
            dateTo={dateTo}
            request={request}
            isPartialPeriod={isPartialPeriod}
            onOpenCampaign={openCampaignInCampaignsTab}
            refreshNonce={refreshKey}
          />
        )}
        {tab === 'analyzer' && (
          <div className="tab-analyzer" key={`analyzer-${refreshKey}`}>
            <AnalyzerCoverage
              accountId={accountId}
              request={request}
              onCreateForSkus={handleCreateForSkus}
              onNavigateToCampaigns={navigateToCampaigns}
            />
          </div>
        )}
        {tab === 'duplicates' && (
          <div className="tab-analyzer" key={`duplicates-${refreshKey}`}>
            <AnalyzerDuplicates accountId={accountId} request={request} />
          </div>
        )}
        {tab === 'generator' && (
          <div className="tab-analyzer" key={`generator-${refreshKey}`}>
            <AnalyzerGenerator
              accountId={accountId}
              request={request}
              initialSkus={generatorSkus}
              onClearSkus={() => setGeneratorSkus(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
